import useWizardForm from '../../../hooks/useWizzardForm';
import WizardFormProvider from '../../../providers/WizzardFormProvider';
import LaunchWarning from '../../common/LaunchWarning';
import { checkExecution, byField } from '../../../helpers/launchChecks';
import {
  Card,
  Form,
  Tab,
  Row,
  Col,
  Button,
  Modal,
  Alert
} from 'react-bootstrap';
import Badge from 'react-bootstrap/Badge';
import { useNavigate } from 'react-router-dom';
import StepProgressBoard from './StepProgressBoard';
import WizardFormFooter from './WizardFormFooter';
import WizardForm from './WizzardForm';
import MachineProgramPreview from './MachineProgramPreview';
import ProductColorDot from '../../common/ProductColorDot';
import ProductAllocationButton from '../ProductAllocationButton';
import { productColorName } from '../../../helpers/productColor';
import { cableLabel } from '../../../helpers/cableLabel';
import { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { useDebouncedCommit } from '../../../hooks/useDebounce';
import './finalReview.css';
import axios from 'axios';
import PhantomTokenPicker from './PhantomTokenPicker';
import { useDrums } from '../../../features/drums/hooks/useDrums.js';
import { API } from '../../../config/api';
import {
  flatSheathColors,
  isFlatSheathPhase,
  MULTI_SELECT_PHASES,
  isMultiUpstreamPhase
} from '../../../helpers/upstreamSelection';
import { checkTandemPair } from '../../../helpers/tandemCheck';
import { formatProductLabel, formatCoreSpec } from '../../../helpers/productLabel';
import {
  saveJobReservation,
  detachJobFromOrder,
  allocateJobToOrder
} from '../../../features/jobs/api/jobsApi';

const MACHINE_OPTIONS = [
  { label: 'L75 - Extruder', value: 'l75' },
  { label: 'L70 - Extruder', value: 'l70' },
  { label: 'L120 - Extruder', value: 'l120' },
  { label: 'DTB1250 - Strander', value: 'dtb1250' },
  { label: 'SkipStrander2250 - Strander', value: 'skip2250' },
  { label: 'ACH300 - Coiler', value: 'ach300' },
  { label: 'DHC600 - Coiler', value: 'dhc600' },
  { label: 'ATH600 - Taping / Armouring', value: 'ath600' }
];

const MACHINE_MAX_DRUM_DIAMETER = {
  l75: 1250,
  l70: 1000,
  dtb1250: 1250,
  l120: 2000,
  skip2250: 2250,
  ach300: 1600,
  dhc600: 1600,
  ath600: 1600
};

// Cable insulation colour → swatch hex (mirrors ProjectBobinState.colorHex). Used
// to render the incoming-execution colour label as the actual colour instead of a
// plain grey badge, so BLUE/BROWN/BLACK/RED/… are visually distinguishable.
// True-to-life insulation colours (vivid, not muted) so the badge looks like the
// physical wire colour. GREEN-YELLOW is a bicolour cable → rendered as a real
// green+yellow split, not a single olive tone.
const CABLE_COLOR_HEX = {
  BLUE: '#1565c0',
  BROWN: '#795548',
  BLACK: '#1a1a1a',
  GREY: '#9e9e9e',
  GRAY: '#9e9e9e',
  WHITE: '#ffffff',
  RED: '#e53935',
  YELLOW: '#ffd600',
  GREEN: '#2e7d32',
  ORANGE: '#fb8c00',
  'GREEN-YELLOW': '#2e7d32'
};

const cableColorHex = c => CABLE_COLOR_HEX[String(c || '').toUpperCase()] || '#8892a0';

// Pick black/white text for readable contrast on a given swatch hex.
const readableTextColor = hex => {
  const h = String(hex || '').replace('#', '');
  if (h.length !== 6) return '#fff';
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return lum > 0.6 ? '#111' : '#fff';
};

// Full inline style for a colour badge — text colour auto-picked for contrast.
// GREEN-YELLOW gets a green/yellow gradient with a light text halo so the name
// stays readable across both halves.
const cableColorBadgeStyle = color => {
  const key = String(color || '').toUpperCase();
  if (key === 'GREEN-YELLOW') {
    return {
      background:
        'linear-gradient(135deg, #2e7d32 0%, #2e7d32 48%, #ffd600 52%, #ffd600 100%)',
      color: '#111',
      textShadow: '0 0 3px rgba(255,255,255,0.85)',
      border: '1px solid rgba(0,0,0,0.25)'
    };
  }
  const hex = cableColorHex(color);
  return {
    backgroundColor: hex,
    color: readableTextColor(hex),
    border: '1px solid rgba(0,0,0,0.25)'
  };
};

// DRUM_LIBRARY is now fetched from DB via useDrums() hook — see drumLibrary prop

const CONDUCTOR_ISOLATION_OPTIMAL_LOTS = {
  'single-wire': {
    1.5: { 800: 15000, 1000: 30000 },
    2.5: { 800: 10000, 1000: 20000 },
    4: { 800: 10000, 1000: 20000 },
    6: { 800: 6000, 1000: 15000 },
    10: { 800: 5000, 1000: 10000 }
  },
  'multi-wire': {
    16: { 800: 2500, 1000: 6000 },
    25: { 800: 1800, 1000: 4000 }
  },
  stranded: {
    1: { 800: 20000 },
    1.5: { 800: 15000, 1000: 30000 },
    2.5: { 800: 10000, 1000: 20000 },
    4: { 800: 6000, 1000: 13000 },
    6: { 800: 5000, 1000: 12000 },
    10: { 800: 2500, 1000: 6500 },
    16: { 1000: 5000 }
  }
};

const toNum = value => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

/**
 * The progress tracker that sits in a section header, beside the title and the
 * "+ Add Execution" button.
 *
 * Those three were laid out with `d-flex justify-content-between` and no
 * wrapping, which is fine at desk width and falls apart on a phone: three items
 * are held on one line however narrow the card gets, so the tracker is crushed
 * and the button is pushed straight out through the right-hand edge of the
 * card. The wizard is used on the floor, on a phone, which is exactly where it
 * broke.
 *
 * A flex basis wide enough to be readable is what forces the wrap — without it
 * the tracker just squeezes instead of moving — and the order puts it on the
 * second line rather than between the title and the button.
 */
const HeaderProgressSlot = ({ children }) => {
  // Several of these headers only show a tracker under a condition, and the
  // wrapper used to be rendered regardless — an empty `flex-grow-1` div sitting
  // between the title and the button, eating the space the button needed. That
  // was half of why it ended up outside the card.
  if (!children) return null;

  return (
    <div
      className="flex-grow-1 order-3 order-sm-0 px-0 px-sm-3 mt-2 mt-sm-0"
      style={{ flexBasis: 180, minWidth: 0 }}
    >
      {children}
    </div>
  );
};

const round = (value, digits = 2) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  return Number(n.toFixed(digits));
};

const normalizeWireType = type =>
  String(type || '')
    .trim()
    .toLowerCase();

const getRecommendedUsableWidthFactor = drumDiameter => {
  const d = Number(drumDiameter || 0);

  if (d > 0 && d <= 1000) return 0.75;
  if (d >= 1250) return 0.8;

  return 0.8;
};

const estimateDrumCapacity = ({
  drumDiameter,
  barrelDiameter,
  flangeWidth,
  cableDiameter,
  fillFactor = 0.8,
  usableWidthFactor = 0.95
}) => {
  const D = Number(drumDiameter);
  const d = Number(barrelDiameter);
  const B = Number(flangeWidth) * Number(usableWidthFactor);
  const dc = Number(cableDiameter);
  const k = Number(fillFactor);

  if (![D, d, B, dc, k].every(v => Number.isFinite(v) && v > 0)) {
    return null;
  }

  if (D <= d) return null;
  if (dc >= B) return null;

  const theoreticalLength = ((D * D - d * d) * B) / (1000 * dc * dc);
  const estimatedLength = theoreticalLength * k;

  return {
    theoreticalLength: round(theoreticalLength, 2),
    estimatedLength: round(estimatedLength, 2)
  };
};
const PHASES_WITH_DRUM_ESTIMATE = [
  'rope',
  'interior_sheat',
  'armouring',
  'exterior_sheat',
  'rewinding',
  'pre_stranding'
];

const canEstimateDrumLengthForPhase = (phaseName, exec) => {
  if (!PHASES_WITH_DRUM_ESTIMATE.includes(phaseName)) return false;

  if (
    phaseName === 'exterior_sheat' &&
    exec?.deliveryMode === 'coils_on_machine'
  ) {
    return false;
  }

  return true;
};

const getExecutionCableDiameter = (phase, product) => {
  if (!phase) return 0;

  if (Number(phase.diameter) > 0) {
    return Number(phase.diameter);
  }

  if (phase.name === 'rewinding') {
    const exteriorPhase = product?.phases?.find(
      p => p.name === 'exterior_sheat'
    );

    if (Number(exteriorPhase?.diameter) > 0) {
      return Number(exteriorPhase.diameter);
    }
  }

  return 0;
};

const estimateExecutionLengthFromDrum = ({ phase, exec, product, drumLibrary = [] }) => {
  if (!canEstimateDrumLengthForPhase(phase?.name, exec)) return null;

  const drumMaterial = String(exec?.drum_material || '').trim();
  const drumDiameter = Number(exec?.drum_diameter);
  const cableDiameter = getExecutionCableDiameter(phase, product);

  if (!drumMaterial || !drumDiameter || !cableDiameter) return null;

  const drum = findDrumConfig(drumMaterial, drumDiameter, drumLibrary);
  if (!drum) return null;

  const phaseName = String(phase?.name || '').trim();

  const isSheat =
    phaseName === 'interior_sheat' || phaseName === 'exterior_sheat';
  const isRope = phaseName === 'rope';

  const usableWidthFactor = isSheat
    ? 1
    : isRope
      ? 0.9
      : getRecommendedUsableWidthFactor(drum.drumDiameter);

  const fillFactor = isSheat ? 0.9 : 0.8;

  const estimate = estimateDrumCapacity({
    drumDiameter: drum.drumDiameter,
    barrelDiameter: drum.barrelDiameter,
    flangeWidth: drum.flangeWidth,
    cableDiameter,
    fillFactor,
    usableWidthFactor
  });

  return estimate?.estimatedLength || null;
};

const findDrumConfig = (drumMaterial, drumDiameter, drumLibrary = []) => {
  const material = String(drumMaterial || '')
    .trim()
    .toLowerCase();
  const diameter = Number(drumDiameter);

  return (
    drumLibrary.find(
      d =>
        String(d.drumType).toLowerCase() === material &&
        Number(d.drumDiameter) === diameter
    ) || null
  );
};

const getMachineMaxDrum = machineId => {
  return MACHINE_MAX_DRUM_DIAMETER[String(machineId || '').trim()] || null;
};

// The drum this execution consumes, which rides the machine's pay-off. Resolved
// the same way the "Incoming executions" panel does: follow phase.dependsOn back
// and match on the upstream execution id the row already carries.
const incomingDrumOf = (exec, product, phase) => {
  const prevName = phase?.dependsOn;
  if (!prevName) return null;
  const prev = product?.phases?.find(p => p.name === prevName);
  if (!prev) return null;

  const upstreamId =
    exec?.fromUpstreamId ??
    (Array.isArray(exec?.fromUpstreamIds) ? exec.fromUpstreamIds[0] : null);
  if (upstreamId == null) return null;

  const pool = [];
  if (prev.conductors) {
    prev.conductors.forEach(cond => pool.push(...(cond.execution || [])));
  } else if (prev.execution) {
    prev.execution.forEach((e, idx) => pool.push({ ...e, id: e.id ?? idx + 1 }));
  }

  const src = pool.find(e => String(e.id) === String(upstreamId));
  return src
    ? { diameter: num(src.drum_diameter) || null, label: prevName.replaceAll('_', ' ') }
    : null;
};

/**
 * Advisory warnings for one planned execution — a single icon against the
 * control that is actually wrong, and nothing at all when the row is fine.
 *
 * Purely informational: it never blocks a launch or changes what is submitted.
 * The wizard is already dense, so the detail lives in the tooltip rather than
 * taking permanent space.
 *
 * Weight comes from computeWeightForExec — the same per-phase figure shown in
 * the kg badge beside it. Working it out separately gave a number six times too
 * large on an insulation run, because the finished-cable formula counts every
 * conductor while an insulation execution makes one.
 */
const ExecWarning = ({ exec, product, phase, conductor = null, field = null }) => {
  const length = num(exec?.plannedLength);
  const weight =
    length > 0 ? computeWeightForExec(phase?.name, length, product, exec) : null;
  const incoming = incomingDrumOf(exec, product, phase);

  const warnings = checkExecution(exec, {
    phase: phase?.name,
    section: conductor
      ? getConductorNominalSection(product, conductor) ?? product?.section
      : product?.section,
    conductors: product?.numberOfConductors,
    productName: product?.name,
    phases: product?.phases,
    // an insulation run makes ONE conductor — the check needs to know which
    conductor,
    weightKg: num(weight?.totalKg),
    incomingDrumDiameter: incoming?.diameter ?? null,
    incomingLabel: incoming?.label ?? null
  });

  return (
    <LaunchWarning
      className="ms-1"
      warnings={field ? byField(warnings)[field] || [] : warnings}
    />
  );
};

const getConductorNominalSection = (product, conductor) => {
  if (
    product?.hasMixedConductorSections &&
    conductor?.isolation?.color === 'GREEN-YELLOW'
  ) {
    const gy = Number(product?.greenYellowSection);
    if (Number.isFinite(gy) && gy > 0) return gy;
  }

  const main = Number(product?.section);
  if (Number.isFinite(main) && main > 0) return main;

  const fallback = Number(conductor?.wire?.section);
  if (Number.isFinite(fallback) && fallback > 0) return fallback;

  return null;
};

const getConductorOptimalLot = ({ product, conductor, drumDiameter }) => {
  const wireType = normalizeWireType(conductor?.wire?.type);
  const nominalSection = getConductorNominalSection(product, conductor);
  if (!wireType || !nominalSection || !drumDiameter) return null;

  const byType = CONDUCTOR_ISOLATION_OPTIMAL_LOTS[wireType];
  if (!byType) return null;

  const bySection = byType[nominalSection];
  if (!bySection) return null;

  return bySection[drumDiameter] || null;
};

const num = v => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

// ── Weight estimation per execution ──────────────────────────────────────────
const _PI = Math.PI;

const computeWeightForExec = (phaseName, length_m, product, exec) => {
  if (!length_m || length_m <= 0 || !product) return null;

  const phases = product.phases || [];
  let copperKg = 0;
  let pvcKg = 0;
  let steelKg = 0;

  const ciPhase = phases.find(p => p.name === 'conductor_isolation');
  const intPhase = phases.find(p => p.name === 'interior_sheat');
  const armPhase = phases.find(p => p.name === 'armouring');
  const extPhase = phases.find(p => p.name === 'exterior_sheat');

  const wireKgPerM = (d, density = 8.96) => {
    const area = (_PI * num(d) * num(d)) / 4;
    return area * 1e-6 * density * 1000;
  };

  // add all copper from ALL conductors
  const addAllCopper = len => {
    if (!ciPhase?.conductors) return;
    for (const cond of ciPhase.conductors) {
      const wire = cond.wire;
      if (!wire) continue;
      if (wire.type?.toUpperCase() === 'SINGLE-WIRE') {
        copperKg += wireKgPerM(wire.diameter, num(wire.density) || 8.96) * len;
      } else {
        const diams = Array.isArray(wire.wireDiameter)
          ? wire.wireDiameter
          : [wire.wireDiameter].filter(Boolean);
        const counts = Array.isArray(wire.numberOfWires)
          ? wire.numberOfWires
          : [wire.numberOfWires].filter(Boolean);
        diams.forEach((d, i) => {
          copperKg +=
            wireKgPerM(d, num(wire.density) || 8.96) *
            len *
            (num(counts[i]) || 1);
        });
      }
    }
  };

  // add copper from ONE conductor (by index or averaged)
  const addSingleConductorCopper = (len, condIdx) => {
    if (!ciPhase?.conductors) return;
    const cond =
      condIdx != null ? ciPhase.conductors[condIdx] : ciPhase.conductors[0];
    if (!cond?.wire) return;
    const wire = cond.wire;
    if (wire.type?.toUpperCase() === 'SINGLE-WIRE') {
      copperKg += wireKgPerM(wire.diameter, num(wire.density) || 8.96) * len;
    } else {
      const diams = Array.isArray(wire.wireDiameter)
        ? wire.wireDiameter
        : [wire.wireDiameter].filter(Boolean);
      const counts = Array.isArray(wire.numberOfWires)
        ? wire.numberOfWires
        : [wire.numberOfWires].filter(Boolean);
      diams.forEach((d, i) => {
        copperKg +=
          wireKgPerM(d, num(wire.density) || 8.96) *
          len *
          (num(counts[i]) || 1);
      });
    }
  };

  // add isolation PVC from ALL conductors
  const addAllIso = len => {
    if (!ciPhase?.conductors) return;
    for (const cond of ciPhase.conductors) {
      const iso = cond.isolation;
      if (iso?.fillingSection && iso?.density) {
        pvcKg += (num(iso.fillingSection) * num(iso.density) * len) / 1000;
      }
    }
  };

  // add isolation PVC from ONE conductor
  const addSingleIso = (len, condIdx) => {
    if (!ciPhase?.conductors) return;
    const cond =
      condIdx != null ? ciPhase.conductors[condIdx] : ciPhase.conductors[0];
    const iso = cond?.isolation;
    if (iso?.fillingSection && iso?.density) {
      pvcKg += (num(iso.fillingSection) * num(iso.density) * len) / 1000;
    }
  };

  const addSheat = (phase, len) => {
    if (phase?.fillingSection && phase?.density) {
      pvcKg += (num(phase.fillingSection) * num(phase.density) * len) / 1000;
    }
  };

  const addArmour = (phase, len) => {
    if (phase?.wide && phase?.thickness && phase?.density) {
      const cs = num(phase.wide) * num(phase.thickness);
      steelKg += cs * 1e-6 * (num(phase.density) || 7.85) * 1000 * len * 2;
    }
  };

  const phaseOrder = [
    'unwinding',
    'pre_stranding',
    'conductor_isolation',
    'rope',
    'interior_sheat',
    'armouring',
    'exterior_sheat'
  ];
  const targetIdx = phaseOrder.indexOf(phaseName);

  // helper: add copper from a pre_stranding rope entry
  const addRopeCopper = (ropeEntry, len) => {
    if (!ropeEntry) return;
    const density = num(ropeEntry.density) || 8.96;
    const diams = Array.isArray(ropeEntry.wireDiameter)
      ? ropeEntry.wireDiameter
      : [ropeEntry.wireDiameter].filter(Boolean);
    const counts = Array.isArray(ropeEntry.numberOfWires)
      ? ropeEntry.numberOfWires
      : [ropeEntry.numberOfWires].filter(Boolean);
    diams.forEach((d, i) => {
      copperKg += wireKgPerM(d, density) * len * (num(counts[i]) || 1);
    });
  };

  switch (phaseName) {
    case 'unwinding': {
      // single wire per drum — use exec.wireDiameter
      const wd = num(exec?.wireDiameter);
      if (wd > 0) {
        const pre = phases.find(p => p.name === 'pre_stranding');
        const density = num(pre?.density) || num(pre?.rope?.[0]?.density) || 8.9;
        copperKg += wireKgPerM(wd, density) * length_m;
      } else {
        addAllCopper(length_m);
      }
      break;
    }

    case 'pre_stranding': {
      const psPhase = phases.find(p => p.name === 'pre_stranding');
      if (psPhase) {
        // mixed conductor sections with rope array
        const ri = exec?.ropeIndex != null ? exec.ropeIndex : 0;
        if (psPhase.rope?.[ri]) {
          addRopeCopper(psPhase.rope[ri], length_m);
        } else if (psPhase.rope?.[0]) {
          addRopeCopper(psPhase.rope[0], length_m);
        } else if (psPhase.wireDiameter != null) {
          // direct pre_stranding phase with wireDiameter/numberOfWires/numberOfDrums
          // (COPPER_ROPE_SOLID, COPPER_ROPE_FLEXIBLE, or inline pre_stranding)
          const density = num(psPhase.density) || 8.96;
          const drums = num(psPhase.numberOfDrums) || 1;
          const diams = Array.isArray(psPhase.wireDiameter)
            ? psPhase.wireDiameter
            : psPhase.wireDiameter != null ? [psPhase.wireDiameter] : [];
          const counts = Array.isArray(psPhase.numberOfWires)
            ? psPhase.numberOfWires
            : psPhase.numberOfWires != null ? [psPhase.numberOfWires] : [];
          diams.forEach((d, i) => {
            const wiresPerDrum = num(counts[i]) || 1;
            const totalWires = wiresPerDrum * drums;
            copperKg += wireKgPerM(d, density) * length_m * totalWires;
          });
        } else {
          // fallback — try from conductor_isolation
          addSingleConductorCopper(length_m);
        }
      } else {
        addSingleConductorCopper(length_m);
      }
      break;
    }

    case 'conductor_isolation':
      // one conductor per execution — use first as approximation
      addSingleConductorCopper(length_m);
      addSingleIso(length_m);
      break;

    case 'rope':
      addAllCopper(length_m);
      addAllIso(length_m);
      break;

    case 'interior_sheat':
      addAllCopper(length_m);
      addAllIso(length_m);
      addSheat(intPhase, length_m);
      break;

    case 'armouring':
      addAllCopper(length_m);
      addAllIso(length_m);
      addSheat(intPhase, length_m);
      addArmour(armPhase, length_m);
      break;

    case 'exterior_sheat':
      addAllCopper(length_m);
      addAllIso(length_m);
      addSheat(intPhase, length_m);
      addArmour(armPhase, length_m);
      addSheat(extPhase, length_m);
      break;

    default:
      return null;
  }

  const total = copperKg + pvcKg + steelKg;
  if (total <= 0) return null;
  return { totalKg: Math.round(total * 100) / 100 };
};

const getExecTotalLength = (phaseName, exec) => {
  if (phaseName === 'coiling')
    return num(exec.coilCount) * num(exec.coilLength);
  if (phaseName === 'rewinding')
    return num(exec.rewindCount) * num(exec.rewindLength);
  // coils produced directly on the line (exterior_sheat delivery = coils_on_machine)
  // store no plannedLength, so their consumed length is coilCount × coilLength
  if (exec.deliveryMode === 'coils_on_machine')
    return num(exec.coilCount) * num(exec.coilLength);
  return num(exec.plannedLength);
};

// helper: check if a phase is the final delivery phase
// Works for any product — checks if phase is the last production phase
// (not coiling/rewinding which are post-production)
const isFinalDeliveryPhase = (phase, allPhases) => {
  if (phase?.name === 'exterior_sheat' || phase?.lastPhase === true) return true;
  if (!allPhases || !Array.isArray(allPhases)) return false;

  const productionPhases = allPhases.filter(
    p => p.name !== 'coiling' && p.name !== 'rewinding'
  );
  const lastProd = productionPhases[productionPhases.length - 1];
  return lastProd && lastProd.name === phase?.name;
};


// Visual production state of an incoming upstream drum/token for the wizard's
// "Incoming executions" cards. In continue/plan-next-phase mode the REAL token
// (status + produced/planned length) drives it; in create-new there's no token
// yet, so it's "Planned" (will be made in the future).
//   Produced       — the cable on this drum is fully made (DONE / produced≥planned)
//   In production   — partially produced (a run is currently going)
//   Upcoming        — token exists but nothing produced yet (queued)
//   Planned         — no real token yet (will be created on launch)
const getInputProductionStatus = (exec, product) => {
  const tok =
    exec?.tokenId && product?._existingTokens
      ? product._existingTokens[exec.tokenId]
      : null;
  if (!tok) return { label: 'Planned', bg: 'secondary' };

  const status = String(tok.status || '').toUpperCase();
  const planned = num(tok.plannedLength);
  const produced = num(tok.producedLength);

  if (
    status === 'DONE' ||
    status === 'COMPLETED' ||
    status === 'FINISHED' ||
    (planned > 0 && produced >= planned)
  ) {
    return { label: 'Produced', bg: 'success' };
  }
  if (produced > 0)
    return { label: 'In production', bg: 'warning', text: 'dark' };
  return { label: 'Upcoming', bg: 'info', text: 'dark' };
};

// Map an unwinding phantom to the rope/wire-diameter group it belongs to.
// renderUnwindingPhase groups execs by ropeIndex + wireDiameter and only renders
// those that fall into a known spec group — so a phantom added without these is
// invisible. We match the phantom's wire diameter to a spec (mixed pre_stranding
// rope array, or the normal pre_stranding/unwinding wireDiameter list), falling
// back to the first spec/diameter so it's always shown.
const matchUnwindingTarget = (product, phantom) => {
  const pDiam = Array.isArray(phantom?.wireDiameter)
    ? num(phantom.wireDiameter[0])
    : num(phantom?.wireDiameter);
  const near = (a, b) => Math.abs(num(a) - num(b)) < 0.01;
  const pre = product?.phases?.find(p => p.name === 'pre_stranding');

  // MIXED: pre_stranding.rope[] → one ropeIndex per rope
  if (Array.isArray(pre?.rope) && pre.rope.length) {
    for (let idx = 0; idx < pre.rope.length; idx++) {
      const diams = (pre.rope[idx].wireDiameter || []).map(num);
      const hit = diams.find(d => near(d, pDiam));
      if (hit != null) return { ropeIndex: idx, wireDiameter: hit };
    }
    const d0 = num((pre.rope[0].wireDiameter || [])[0]);
    return { ropeIndex: 0, wireDiameter: d0 || pDiam || 0 };
  }

  // NORMAL: single spec (ropeIndex null)
  const unw = product?.phases?.find(p => p.name === 'unwinding');
  const diams = (unw?.wireDiameter ?? pre?.wireDiameter ?? []).map(num);
  const hit = diams.find(d => near(d, pDiam));
  if (hit != null) return { ropeIndex: null, wireDiameter: hit };
  return { ropeIndex: null, wireDiameter: num(diams[0]) || pDiam || 0 };
};

// 🔹 normalizează exec: dacă e vechi (fromUpstreamIds) -> creează fromUpstreams cu split egal
const normalizeExecUpstreams = (exec, totalLen) => {
  // dacă deja e nou
  if (Array.isArray(exec.fromUpstreams) && exec.fromUpstreams.length)
    return exec;

  const ids = Array.isArray(exec.fromUpstreamIds) ? exec.fromUpstreamIds : [];
  if (!ids.length) return exec;

  const per = totalLen / ids.length;

  return {
    ...exec,
    fromUpstreams: ids.map(id => ({ id, take: per }))
  };
};

// 🔹 Consumuri: { [upstreamId]: totalConsumed }
const buildConsumedMapForPhase = (phase, options = {}) => {
  const consumed = {};
  const excludeId = options.excludeExecutionId;

  if (phase.name === 'conductor_isolation') {
    phase.conductors?.forEach(c => {
      c.execution?.forEach(exec => {
        if (exec.id === excludeId) return;
        exec.fromUpstreams?.forEach(u => {
          if (u?.id == null) return;
          consumed[u.id] = (consumed[u.id] || 0) + Number(u.take || 0);
        });
      });
    });
    return consumed;
  }

  // ✅ rope / pre_stranding: dacă există fromUpstreams -> FLOW, altfel fallback bundle
  if (phase.name === 'pre_stranding' || phase.name === 'rope') {
    phase.execution?.forEach(exec => {
      if (exec.id === excludeId) return;

      // FLOW
      if (Array.isArray(exec.fromUpstreams) && exec.fromUpstreams.length) {
        exec.fromUpstreams.forEach(u => {
          if (u?.id == null) return;
          consumed[u.id] = (consumed[u.id] || 0) + Number(u.take || 0);
        });
        return;
      }

      // FALLBACK vechi (bundle)
      const L = num(exec.plannedLength);
      if (!L) return;
      (exec.fromUpstreamIds || []).forEach(id => {
        consumed[id] = (consumed[id] || 0) + L;
      });
    });

    return consumed;
  }

  phase.execution?.forEach(exec => {
    if (exec.id === excludeId) return;

    // FLOW — a flat-cable exterior sheath draws from one drum per conductor
    // colour, each with its own take (same shape rope/pre_stranding produce).
    if (Array.isArray(exec.fromUpstreams) && exec.fromUpstreams.length) {
      exec.fromUpstreams.forEach(u => {
        if (u?.id == null) return;
        consumed[u.id] = (consumed[u.id] || 0) + Number(u.take || 0);
      });
      return;
    }

    if (exec.fromUpstreamId == null) return;

    const L = getExecTotalLength(phase.name, exec);
    consumed[exec.fromUpstreamId] = (consumed[exec.fromUpstreamId] || 0) + L;
  });

  return consumed;
};

// What this product's share of a finished drum is recorded as — and, when the
// drum is shared, what the whole bobbin then claims to hold.
//
// The only thing reconcile could do to a produced drum was move it to another
// product, which is meaningless for a MERGED one: both products already hold it,
// with a share each. What is actually wrong after a bad night is the SPLIT —
// on 2026-09-15 an L75 bobbin ended up recorded as 10 300 m of one product and
// 4.29 m of another. Correcting that is this control.
//
// The total is shown because it is the honest check on the edit: two shares that
// now add up to more than the bobbin ever held is a different claim from moving
// metres from one product to the other, and the person typing should see which
// one they are making.
const ReconcileDrumLength = ({
  exec,
  jobId,
  shares = [],
  reconcileLen = {},
  setReconcileLength,
  // Every other produced drum, so metres recorded on the wrong one can be sent
  // where they belong.
  otherDrums = []
}) => {
  // Hooks first, before any early return. A component that bails out before a
  // `useState` renders a different number of hooks depending on its props, and
  // React tears the tree down the moment that changes.
  const [sendTo, setSendTo] = useState('');
  const [sendAmount, setSendAmount] = useState('');

  if (!exec?.tokenId) return null;

  const key = `${jobId}::${exec.tokenId}`;
  const recorded = Number(exec._producedLength || 0);
  const draft = reconcileLen[key];
  const value = draft != null ? draft : String(recorded);
  const parsed = Number(String(value).replace(',', '.'));
  const edited = draft != null && Number.isFinite(parsed) && parsed !== recorded;
  const sendQty = Number(String(sendAmount).replace(',', '.')) || 0;
  // What this drum is credited with right now, staged edits included. Sending
  // from the recorded figure instead would undo whatever was typed first.
  const current = Number.isFinite(parsed) ? parsed : recorded;

  const others = shares.filter(s => String(s.jobId) !== String(jobId));
  const othersTotal = others.reduce((s, x) => s + Number(x.producedLength || 0), 0);
  const plannedTotal = shares.reduce((s, x) => s + Number(x.plannedLength || 0), 0);
  const newTotal = othersTotal + (Number.isFinite(parsed) ? parsed : recorded);

  return (
    <Row className="g-2 mt-1 pt-2 border-top">
      <Col md={5}>
        <Form.Group>
          <Form.Label className="fs-10 fw-semibold text-primary mb-1">
            Produced (m) — this product&apos;s share
          </Form.Label>
          <Form.Control
            size="sm"
            type="text"
            inputMode="decimal"
            value={value}
            isInvalid={!Number.isFinite(parsed) || parsed < 0}
            onChange={e => setReconcileLength(jobId, exec.tokenId, e.target.value)}
            title="What this product actually got off this drum"
          />
          {edited && (
            <div className="fs-10 text-warning mt-1">
              was {recorded.toLocaleString()} m
            </div>
          )}
        </Form.Group>
      </Col>
      {/* Two products on one bobbin whose shares ended up the wrong way round.
          The bobbin's total is right; only who got what is wrong — which is one
          exchange, not a move and not a recolour. The tokenId is untouched:
          that is what holds the merge together, and swapping shares is two
          numbers changing places, not a drum going anywhere. */}
      {others.length === 1 && (
        <Col md={12}>
          <Button
            size="sm"
            variant="outline-warning"
            className="fs-10"
            onClick={() => {
              const mine = recorded;
              const theirs = Number(others[0].producedLength || 0);
              setReconcileLength(jobId, exec.tokenId, String(theirs));
              setReconcileLength(others[0].jobId, exec.tokenId, String(mine));
            }}
            title={`Give this product ${Number(others[0].producedLength || 0).toLocaleString()} m and ${others[0].label} ${recorded.toLocaleString()} m`}
          >
            ⇄ swap the two shares of this bobbin
          </Button>
          <div className="fs-10 text-700 mt-1">
            {Math.abs(newTotal - plannedTotal) > 0.01 && plannedTotal > 0 ? (
              <span className="text-warning">
                the bobbin holds {Number(othersTotal + recorded).toLocaleString()} m
                against {plannedTotal.toLocaleString()} m planned — a swap moves
                metres between the two, it cannot change the total
              </span>
            ) : (
              'the bobbin total stays the same — only who got what changes'
            )}
          </div>
        </Col>
      )}

      {/* Metres recorded on the wrong drum, sent to the right one.

          Not a move and not a colour swap: the drums stay where they are and
          keep their names. One is credited less and the other more, by the
          amount typed — which is the only way to say "8 240 of these 10 300
          were never this product's". Applied as two length corrections, so it
          shows up in the staged list like everything else and nothing is
          written until submit. */}
      {otherDrums.length > 0 && (
        <Col md={12}>
          <div className="pt-2 mt-1 border-top">
            <Form.Label className="fs-10 fw-semibold text-primary mb-1">
              Send metres to another drum
            </Form.Label>
            <div className="d-flex gap-2 align-items-start flex-wrap">
              <Form.Select
                size="sm"
                style={{ maxWidth: 320 }}
                value={sendTo}
                onChange={e => setSendTo(e.target.value)}
              >
                <option value="">— where should they go —</option>
                {otherDrums.map(d => (
                  <option key={d.key || `${d.jobId}::${d.tokenId}`} value={`${d.jobId}::${d.tokenId}`}>
                    {d.label} · {d.color || '—'} ·{' '}
                    {Number(d.length || 0).toLocaleString()} m
                    {/* The token, because the two shares of a merged bobbin are
                        the same product name, the same colour and often the same
                        length — without it they are two identical lines. */}
                    {d.merged ? ` · ${d.tokenId}` : ''}
                  </option>
                ))}
              </Form.Select>
              <Form.Control
                size="sm"
                style={{ maxWidth: 130 }}
                type="text"
                inputMode="decimal"
                placeholder="metres"
                value={sendAmount}
                onChange={e => setSendAmount(e.target.value)}
              />
              <Button
                size="sm"
                variant="outline-primary"
                disabled={!sendTo || !(sendQty > 0) || sendQty > current}
                onClick={() => {
                  const [dJob, dTok] = sendTo.split('::');
                  const dest = otherDrums.find(
                    d => `${d.jobId}::${d.tokenId}` === sendTo
                  );
                  if (!dest) return;
                  setReconcileLength(jobId, exec.tokenId, String(current - sendQty));
                  setReconcileLength(
                    dJob,
                    dTok,
                    String(Number(dest.length || 0) + sendQty)
                  );
                  setSendAmount('');
                  setSendTo('');
                }}
              >
                Send
              </Button>
            </div>
            {sendQty > 0 && (
              <div
                className={`fs-10 mt-1 ${sendQty > current ? 'text-danger' : 'text-700'}`}
              >
                {sendQty > current
                  ? `this drum only has ${current.toLocaleString()} m to give`
                  : `this drum keeps ${(current - sendQty).toLocaleString()} m` +
                    (sendTo
                      ? `, the other becomes ${(
                          Number(
                            otherDrums.find(d => `${d.jobId}::${d.tokenId}` === sendTo)
                              ?.length || 0
                          ) + sendQty
                        ).toLocaleString()} m`
                      : '')}
              </div>
            )}
          </div>
        </Col>
      )}

      {others.length > 0 && (
        <Col md={7}>
          <div className="fs-10 text-700 mt-4">
            <div>
              Shared bobbin{' '}
              <span className="font-monospace">{exec.tokenId}</span> —{' '}
              {others
                .map(
                  o =>
                    `${o.label}: ${Number(o.producedLength || 0).toLocaleString()} m`
                )
                .join(', ')}
            </div>
            <div className={edited ? 'fw-semibold text-warning' : ''}>
              drum total {Number(newTotal.toFixed(2)).toLocaleString()} m
              {plannedTotal > 0
                ? ` of ${plannedTotal.toLocaleString()} m planned`
                : ''}
            </div>
          </div>
        </Col>
      )}
    </Row>
  );
};

// Every correction ever made to this drum's recorded length, on the drum.
// Shown wherever a produced drum is read, so "4.29 m" is never mistaken for a
// measurement when it is the result of somebody's edit — or the other way round.
const DrumCorrections = ({ exec }) => {
  const list = Array.isArray(exec?._corrections) ? exec._corrections : [];
  if (!list.length) return null;
  const fmt = ts => {
    const d = new Date(Number(ts) || 0);
    return Number.isFinite(d.getTime())
      ? d.toLocaleString('ro-RO', {
          day: '2-digit',
          month: 'short',
          hour: '2-digit',
          minute: '2-digit'
        })
      : '-';
  };
  return (
    <div className="fs-10 text-700 mt-2 pt-2 border-top">
      <span className="fw-semibold">Corrections</span>
      {list
        .slice()
        .reverse()
        .map((c, i) => (
          <div key={`${c.ts}-${i}`}>
            {fmt(c.ts)} · {Number(c.from || 0).toLocaleString()} m →{' '}
            <span className="fw-semibold">
              {Number(c.to || 0).toLocaleString()} m
            </span>{' '}
            · {c.by || 'System'}
            {c.reason ? ` · ${c.reason}` : ''}
            {c.source ? (
              <span className="text-body-tertiary"> ({c.source})</span>
            ) : null}
          </div>
        ))}
    </div>
  );
};

/**
 * Relabel every copy of one drum, in place on a cloned product list.
 *
 * A colour belongs to the BOBBIN. On a shared one each product keeps its own
 * copy of the drum, so recolouring only the card that was dragged leaves the
 * two products contradicting each other about the same physical object — and
 * the wizard would show one thing while the backend was told another.
 */
const recolorTokenIn = (products, tokenId, color) => {
  for (const p of products) {
    for (const ph of p?.phases || []) {
      const lists = [
        ...(Array.isArray(ph.execution) ? [ph.execution] : []),
        ...((ph.conductors || []).map(c => c.execution).filter(Array.isArray))
      ];
      for (const list of lists) {
        for (const e of list) {
          if (e && String(e.tokenId) === String(tokenId)) e.color = String(color);
        }
      }
    }
  }
  return products;
};

// Locked-card note: for a merged drum, name the products sharing it; else produced.
const mergedLockedNote = exec => {
  if (!exec?._merged) return 'Already produced — locked.';
  const names = (exec._mergePartners || []).map(
    p => `${p.productLabel}${p.isSelf ? ' (this)' : ''}`
  );
  return names.length
    ? `Merged — shared with: ${names.join(', ')}. Locked (unmerge to change it).`
    : 'Merged with another product — locked (unmerge to change it).';
};

const getPhaseEstimatedLength = ({
  phaseDiameter,
  drumMaterial,
  drumDiameter,
  drumLibrary = []
}) => {
  const cableDiameter = Number(phaseDiameter || 0);

  if (!drumMaterial || !drumDiameter || !cableDiameter) return null;

  const drum = findDrumConfig(drumMaterial, drumDiameter, drumLibrary);
  if (!drum) return null;

  const usableWidthFactor = getRecommendedUsableWidthFactor(drum.drumDiameter);

  const estimate = estimateDrumCapacity({
    drumDiameter: drum.drumDiameter,
    barrelDiameter: drum.barrelDiameter,
    flangeWidth: drum.flangeWidth,
    cableDiameter,
    fillFactor: 0.8,
    usableWidthFactor
  });

  if (!estimate?.estimatedLength) return null;

  return estimate.estimatedLength;
};

// Colour-parallel allocation: the run needs `needLength` of EVERY colour, and a
// colour may be spliced from several drums (drained FIFO). Used by rope, and by
// a flat cable's exterior sheath — which is extruded straight over the bare
// cores and therefore consumes exactly like a rope run.
const allocateRopeFlowByColor = (
  upstreams,
  consumedMap,
  needLength,
  requiredColors
) => {
  const result = [];

  for (const color of requiredColors) {
    let need = needLength;

    const candidates = upstreams
      .filter(u => u.color === color)
      .sort((a, b) => Number(a.id) - Number(b.id)); // FIFO

    for (const u of candidates) {
      if (need <= 0) break;

      const cap = num(u.plannedLength) ?? num(u.plannedLengthInput) ?? 0;

      const used = num(consumedMap[u.id]) || 0;
      const remaining = cap - used;

      if (remaining <= 0) continue;

      const take = Math.min(remaining, need);
      result.push({ id: u.id, take, color });
      need -= take;
    }

    if (need > 0) {
      return { ok: false, fromUpstreams: [] };
    }
  }

  return { ok: true, fromUpstreams: result };
};

const allocateConductorFlow = (upstreams, consumedMap, needLength) => {
  let need = needLength;
  const result = [];
  console.log('INFUNC   ', need);
  for (const u of upstreams) {
    if (need <= 0) break;

    const cap = num(u.plannedLength);
    const used = num(consumedMap[u.id]) || 0;
    const remaining = cap - used;

    if (remaining <= 0) continue;

    const take = Math.min(remaining, need);
    result.push({ id: u.id, take });
    need -= take;
    console.log('TAKE ', take);
    console.log('NEED , ', need);
  }

  return {
    ok: need <= 0,
    fromUpstreams: result
  };
};

// 🔹 “Free upstreams” (au metri rămași)
const getRemainingForUpstream = (upExec, consumedMap) => {
  const cap = num(upExec.plannedLength);
  const used = num(consumedMap[upExec.id]);
  return Math.max(cap - used, 0);
};

// 🔹 ALOCATOR: ia metri din tamburi în ordinea id/idx (greedy), până acoperă `needLen`
const allocateLengthGreedy = (upstreamsOrdered, consumedMap, needLen) => {
  let remainingNeed = num(needLen);
  const picked = [];

  for (const u of upstreamsOrdered) {
    if (remainingNeed <= 0) break;

    const avail = getRemainingForUpstream(u, consumedMap);
    if (avail <= 0) continue;

    const take = Math.min(avail, remainingNeed);
    picked.push({ id: u.id, take });
    remainingNeed -= take;
  }

  return {
    picked,
    isSatisfied: remainingNeed <= 0,
    missing: remainingNeed
  };
};
const byJobIndex = (allocs = []) => {
  const m = new Map();
  for (const a of allocs || []) {
    const idx = Number(a?.jobIndex);
    if (!Number.isFinite(idx)) continue;
    if (!m.has(idx)) m.set(idx, []);
    m.get(idx).push({
      orderId: a.orderId,
      lineId: a.lineId,
      allocatedMeters: Number(a.allocatedMeters || 0)
    });
  }
  return m;
};

const toBool = v => v === true || v === 'true';

// 🔹 MIGRARE 1-shot: adaugă fromUpstreams unde lipsește, fără să strici nimic
const migrateProductUpstreamsV1 = product => {
  if (!product?.phases) return product;

  const cloned = structuredClone(product);

  cloned.phases.forEach(phase => {
    // pre_stranding / rope
    if (
      (phase.name === 'pre_stranding' || phase.name === 'rope') &&
      Array.isArray(phase.execution)
    ) {
      phase.execution = phase.execution.map(exec => {
        const total = num(exec.plannedLength);
        return normalizeExecUpstreams(exec, total);
      });
    }

    // conductor_isolation
    if (
      phase.name === 'conductor_isolation' &&
      Array.isArray(phase.conductors)
    ) {
      phase.conductors = phase.conductors.map(c => ({
        ...c,
        execution: (c.execution || []).map(exec => {
          const total = num(exec.plannedLength);
          return normalizeExecUpstreams(exec, total);
        })
      }));
    }
  });

  return cloned;
};

// ── Order Delivery Panel (shows delivery plan from rope onwards) ──────────────
const OrderDeliveryPanel = ({
  product,
  phaseName,
  onChange,
  selectedUpstream,
  onAddCoilingPhase,
  allProducts = [],
  reconcileAutoCoiling,
  contextOnly = false,
  mode = 'create',
  onReserveChange,
  onSaveAllReservations,
  onDetachOrder,
  onAllocateMore,
  // Save the reservation on its own, without submitting a phase plan. Only
  // supplied in continue/plan-next-phase, where moving a reservation between two
  // jobs of one order is a thing somebody does on purpose.
  onSaveReservation
}) => {
  // Create-launch: the Order Reservation card is a PLAN — it edits product._reservation
  // (a commitment, itemId -> qty) and is recorded on the order only at submit. In
  // continue/edit the card reflects actual executions (launchedByItem) instead.
  const isReservationMode = mode === 'create';
  // continue/plan-next-phase: reservations are OPTIONAL — the planner can opt-in with
  // a toggle to reserve final drums for THIS job even mid-flow (if they change their
  // mind). resvOpen turns the informative card into the editable classic card.
  const [resvOpen, setResvOpen] = useState(false);
  // machine picked once and applied to every quick-select execution (quick launch)
  const [quickMachine, setQuickMachine] = useState('');
  const orders = product?._orders || [];
  if (!orders.length) return null;

  // Delivery phases that fulfil order delivery items:
  //  - exterior_sheat  -> DRUM items (cable on drum)
  //  - coiling/rewinding -> COIL items
  // Which delivery item kind is CLICKABLE on this phase:
  //  - exterior_sheat -> DRUM, coiling/rewinding -> COIL, others -> none (info)
  // Which delivery item kinds are CLICKABLE on this phase:
  //  - exterior_sheat -> DRUM (cable on drum, final product) AND COIL (cable
  //    sent to coiling) so a coil order can be routed straight from here
  //  - coiling/rewinding -> COIL (the actual coiling run)
  const clickableKinds =
    phaseName === 'exterior_sheat'
      ? ['DRUM', 'COIL']
      : phaseName === 'coiling' || phaseName === 'rewinding'
        ? ['COIL']
        : [];
  // contextOnly (create-launch): show the informational Order Context card only —
  // never the delivery-item assignment UI (that belongs to plan-next-phase / continue).
  const isDeliveryPhase = !contextOnly && clickableKinds.length > 0;
  // Only coiling/rewinding HIDE the non-matching items (show coils only).
  // Every other phase shows ALL items so coils stay visible as info.
  const coilOnly = phaseName === 'coiling' || phaseName === 'rewinding';

  // check if an upstream input is selected (required before quick-select)
  const selRaw = selectedUpstream?.[product?._id]?.[phaseName];
  const currentUpstream = Array.isArray(selRaw) ? selRaw[0] : selRaw;

  // count how many executions already exist for each orderItemId
  const phase = product?.phases?.find(p => p.name === phaseName);
  const existingExecs = Array.isArray(phase?.execution) ? phase.execution : [];

  // Flat cable (MYYUP): the sheath is fed by a drum of EVERY conductor colour at
  // once, so one drum being selected isn't enough and the input left is the
  // bottleneck colour — not the first drum's remainder.
  const flatColors = flatSheathColors(phase, product);
  const flatUpstreams = flatColors && Array.isArray(selRaw) ? selRaw : [];
  const flatRemainingByColor = () => {
    const consumedMap = buildConsumedMapForPhase(phase);
    const byColor = {};
    for (const u of flatUpstreams) {
      if (!u?.color) continue;
      const left = Math.max(
        0,
        (Number(u.plannedLength) || 0) - (Number(consumedMap[u.id]) || 0)
      );
      byColor[u.color] = (byColor[u.color] || 0) + left;
    }
    const perColor = flatColors.map(c => byColor[c] || 0);
    return perColor.length ? Math.min(...perColor) : 0;
  };

  const hasUpstreamSelected = flatColors
    ? flatColors.every(c => flatUpstreams.some(u => u.color === c))
    : !!currentUpstream;

  // check if input is fully consumed (remaining = 0)
  const upstreamCap = Number(currentUpstream?.plannedLength) || 0;
  const usedFromUpstream = currentUpstream
    ? existingExecs
        .filter(e => e.fromUpstreamId === currentUpstream.id)
        .reduce((s, e) => s + (parseFloat(e.plannedLength) || 0), 0)
    : 0;
  const inputRemaining = flatColors
    ? hasUpstreamSelected
      ? flatRemainingByColor()
      : 0
    : Math.max(0, upstreamCap - usedFromUpstream);
  const inputConsumed = hasUpstreamSelected && inputRemaining <= 0;
  // Two scopes, keyed by delivery-item id:
  //  - launchedByItem      = ORDER-WIDE (every job in this wizard). Order Requirements
  //    uses it so it reflects the ORDER's real progress across all jobs.
  //  - launchedByItemMine  = only THIS product's executions. The Order Reservation
  //    card uses it so one job's launches never leak into another job's context.
  // (A coil/rewind exec makes coilCount/rewindCount COILS; a drum exec makes ONE drum
  //  — count delivery UNITS, not executions.)
  const addExecUnits = (map, p) => {
    for (const ph of p?.phases || []) {
      for (const ex of Array.isArray(ph?.execution) ? ph.execution : []) {
        if (!ex.orderItemId) continue;
        const units = Number(ex.coilCount) || Number(ex.rewindCount) || 1;
        map[ex.orderItemId] = (map[ex.orderItemId] || 0) + units;
      }
    }
  };
  const launchedByItem = {};
  const productsForCount =
    Array.isArray(allProducts) && allProducts.length ? allProducts : [product];
  for (const p of productsForCount) addExecUnits(launchedByItem, p);
  const launchedByItemMine = {};
  addExecUnits(launchedByItemMine, product);

  // collect all delivery plan items across all order lines
  const allItems = [];
  for (const ord of orders) {
    const items = Array.isArray(ord.deliveryPlanItems)
      ? ord.deliveryPlanItems
      : [];
    for (const item of items) {
      allItems.push({ ...item, orderCode: ord.orderCode, client: ord.client });
    }
  }

  const handleQuickSelect = (item, maxQty, budgetRemaining = Infinity) => {
    if (!isDeliveryPhase || !hasUpstreamSelected || inputConsumed) return;
    const kind = String(item.kind || '').toUpperCase();
    if (!clickableKinds.includes(kind)) return;
    if (!phase) return;

    const orderQty = Number.isFinite(maxQty)
      ? maxQty
      : Number(item.qtyPlanned) || 0;
    const len = Number(item.lengthPerUnit) || 0;
    if (len <= 0) return;

    // Per-job "done": item.tokenIds is ORDER-WIDE (spans all jobs), so it must
    // NOT gate this per-job panel — use only what THIS job has launched.
    const done = launchedByItem[item.itemId] || 0;
    const remainingUnits = Math.max(0, orderQty - done);
    if (remainingUnits <= 0) return;

    // Units the remaining INPUT can supply, capped by the remaining job BUDGET
    // (drums & coils share one budget → free split up to the allocation).
    const fitByInput = Math.floor(inputRemaining / len);
    const fitByBudget =
      budgetRemaining === Infinity
        ? fitByInput
        : Math.floor(budgetRemaining / len);
    const fitUnits = Math.min(fitByInput, fitByBudget);
    if (fitUnits <= 0) return; // input/budget too small for even one unit

    const base = {
      machineId: quickMachine || '', // quick launch: apply the picked machine
      orderItemId: item.itemId || null,
      fromUpstreamId: currentUpstream?.id || null,
      fromUpstreamLength: currentUpstream?.plannedLength || null,
      fromUpstreamColor: currentUpstream?.color || null
    };

    let newExecs;
    const isCoilingPhase =
      phaseName === 'coiling' || phaseName === 'rewinding';

    if (kind === 'COIL' && isCoilingPhase) {
      // the actual coiling/rewinding run: one execution = a batch of coils.
      const units = Math.min(remainingUnits, fitUnits);
      const isRewind = phaseName === 'rewinding';
      newExecs = [
        {
          ...base,
          id: Date.now(),
          plannedLength: units * len,
          drum_material: '',
          drum_diameter: '',
          deliveryMode: 'final_product',
          ...(isRewind
            ? { rewindLength: len, rewindCount: units }
            : { coilLength: len, coilCount: units })
        }
      ];
    } else if (kind === 'COIL') {
      // exterior_sheat + coil delivery: make the cable and SEND TO COILING by
      // default (not coils-on-machine). `autoCoilFromOrder` tags this exec so
      // reconcileAutoCoiling() mirrors it into a linked coiling execution (and
      // creates the coiling phase if missing) — see the call after onChange.
      const units = Math.min(remainingUnits, fitUnits);
      newExecs = [
        {
          ...base,
          id: Date.now(),
          plannedLength: units * len, // cable length to be coiled
          deliveryMode: 'to_coiling',
          nextPhase: 'coiling',
          // carry the coil spec so the delivery unit count is right (this one
          // exec = `units` coils, not 1) AND the chained coiling run knows it
          // must make `units` × `len`-m coils from this cable.
          coilLength: len,
          coilCount: units,
          autoCoilFromOrder: true
        }
      ];
    } else {
      // DRUM: one physical drum per click = final product (cable on drum).
      newExecs = [
        {
          ...base,
          id: Date.now(),
          plannedLength: len,
          drum_material: String(item.drumMaterial || '').toLowerCase(),
          drum_diameter: Number(item.drumDiameter) || '',
          deliveryMode: 'final_product',
          nextPhase: null
        }
      ];
    }

    // Flat cable: this run is fed by a drum of every conductor colour, so bill
    // each colour for the cable it makes instead of the single upstream drum a
    // normal sheath consumes.
    if (flatColors) {
      const consumedMap = buildConsumedMapForPhase(phase);

      for (const ex of newExecs) {
        const { ok, fromUpstreams } = allocateRopeFlowByColor(
          flatUpstreams,
          consumedMap,
          Number(ex.plannedLength) || 0,
          flatColors
        );
        if (!ok) return;

        ex.fromUpstreams = fromUpstreams;
        ex.fromUpstreamIds = [...new Set(fromUpstreams.map(u => u.id))];
        ex.fromUpstreamColors = flatColors;
        ex.fromUpstreamId = null;
        ex.fromUpstreamLength = null;
        ex.fromUpstreamColor = null;

        for (const u of fromUpstreams) {
          consumedMap[u.id] = (consumedMap[u.id] || 0) + Number(u.take || 0);
        }
      }
    }

    onChange(product._id, phaseName, 'execution', [
      ...existingExecs,
      ...newExecs
    ]);

    // COIL routed at exterior_sheat → mirror it into a linked coiling run so the
    // drum shows Finished (input reserved) + the coiling exec is auto-created.
    if (kind === 'COIL' && !isCoilingPhase) {
      reconcileAutoCoiling?.(product._id);
    }
  };

  // ── Order Reservation card (companion to Order Context) ──────────────────────
  // Reserve the order's FINAL drums/coils on the allocated meters, coloured by the
  // product colour, kept IN SYNC with Order Context (reads the same executions via
  // launchedByItem). On a delivery phase the tiles are clickable and reserve via the
  // same handleQuickSelect — so "schedule directly from here" updates Order Context.
  const resColorHex = cableColorHex(product?.color);
  const RESV_ACCENT = '#6366f1'; // indigo — distinct from the green Order Context
  // Final drums/coils ACTUALLY launched for an item (persisted as `tokenIds` on the
  // order by a real final-phase plan). Counted only in continue mode. Uses tokenIds
  // (actual production), NOT launchedQty — launchedQty also holds the create-launch
  // RESERVATION (a plan), which stays informative and must NOT gate anything.
  //  - priorLaunchedAll  = every job's tokens on the item (order-wide reality).
  //  - priorLaunchedMine = only THIS job's tokens (intersect with _existingTokens),
  //    so a sibling job sharing the order doesn't leak into this job's context.
  const priorLaunchedAll = it =>
    mode === 'continue'
      ? Array.isArray(it.tokenIds)
        ? it.tokenIds.length
        : 0
      : 0;
  const priorLaunchedMine = it => {
    if (mode !== 'continue') return 0;
    const ids = Array.isArray(it.tokenIds) ? it.tokenIds : [];
    if (!ids.length) return 0;
    const mine = product?._existingTokens;
    return mine ? ids.filter(tid => mine[tid]).length : ids.length;
  };
  // Order Requirements: ORDER-WIDE progress (this wizard's execs + order tokens).
  const doneOfAll = it => (launchedByItem[it.itemId] || 0) + priorLaunchedAll(it);
  // Order Reservation: THIS job only (informative, no cross-job leak).
  const doneOfMine = it =>
    (launchedByItemMine[it.itemId] || 0) + priorLaunchedMine(it);
  // Editable reservation mode: create-launch always, OR continue-mode when the
  // planner opted-in with the toggle. In edit mode the tiles show the PLAN
  // (_reservation) with −/+ steppers; otherwise they reflect actual production.
  const resvEditable = isReservationMode || resvOpen;
  const reservedOf = it =>
    resvEditable
      ? Number(product?._reservation?.[it.itemId]) || 0
      : doneOfMine(it);
  // Cross-job reservation cap (create-launch): how many of this delivery item OTHER
  // products in this launch already reserved. Same order item can't be over-reserved
  // across jobs.
  const otherReservedOf = itemId =>
    (Array.isArray(allProducts) ? allProducts : []).reduce(
      (s, p) =>
        p?._id === product?._id
          ? s
          : s + (Number(p?._reservation?.[itemId]) || 0),
      0
    );
  // Continue mode: does THIS job carry a per-job reservation to fulfil?
  const anyJobResv =
    !isReservationMode &&
    orders.some(o =>
      (o.deliveryPlanItems || []).some(it => Number(it.jobReservedQty) > 0)
    );
  // When the planner opens the editor on a job that ALREADY has a reservation,
  // seed _reservation from the current jobReservedQty so the steppers start at the
  // committed values (the backend persists deltas — starting from 0 would submit a
  // negative delta and wipe the reservation).
  const seedReservationFromJob = () => {
    if (!onReserveChange) return;
    if (product?._reservation && Object.keys(product._reservation).length) return;
    orders.forEach(o =>
      (o.deliveryPlanItems || []).forEach(it => {
        const q = Number(it.jobReservedQty) || 0;
        if (q > 0) onReserveChange(product._id, it.itemId, q);
      })
    );
  };

  // What this product had committed BEFORE anyone started editing. Kept on the
  // product so a sibling can tell an edit from a starting value — without it a
  // job that simply opened its editor would look like it had just reserved
  // everything it already held, and the pool would appear to vanish.
  if (product && !product._committedReservation) {
    product._committedReservation = Object.fromEntries(
      orders.flatMap(o =>
        (o.deliveryPlanItems || [])
          .filter(it => Number(it.jobReservedQty) > 0)
          .map(it => [it.itemId, Number(it.jobReservedQty) || 0])
      )
    );
  }
  // Order-wide capacity still free to reserve for an item = total planned − units
  // already reserved/launched across ALL jobs (launchedQty already includes THIS
  // job's current reservation).
  const freeUnitsOf = it =>
    Math.max(0, (Number(it.qtyPlanned) || 0) - (Number(it.launchedQty) || 0));

  /**
   * What the OTHER jobs open in this wizard have changed but not yet saved.
   *
   * `launchedQty` on the order is what everyone else is holding as of the last
   * save. When two — or five — jobs of the same order are being edited side by
   * side, a sibling that has just given up thirty coils has not written that
   * anywhere yet, so without this the free pool lags behind what is on screen
   * and the steppers cap on a number the operator can see is wrong.
   *
   * Works for any number of products: it is a sum over the siblings, not a
   * comparison against one.
   */
  const siblingDeltaOf = it => {
    const others = Array.isArray(allProducts) ? allProducts : [];
    return others.reduce((sum, p) => {
      if (!p || p._id === product?._id) return sum;

      // What that sibling had committed for this item before anyone started.
      //
      // Read from its own order data (`jobReservedQty` is what the server says
      // that job holds) rather than from a field stamped during render: a card
      // that had not been rendered yet would have had no baseline, and its
      // sibling would have read every committed unit as a brand-new edit.
      const committed =
        p._committedReservation?.[it.itemId] !== undefined
          ? Number(p._committedReservation[it.itemId])
          : (Array.isArray(p._orders) ? p._orders : []).reduce((q, o) => {
              const found = (o?.deliveryPlanItems || []).find(
                x => String(x?.itemId) === String(it.itemId)
              );
              return q + (Number(found?.jobReservedQty) || 0);
            }, 0);

      // A CLEARED item has no key at all — handleReserveChange deletes it,
      // because in a launch plan "no entry" means "not reserved". Reading a
      // missing key as "this sibling has not edited anything" made every
      // release invisible to the other jobs: the ceilings only moved after
      // leaving the wizard and coming back, when the server was read afresh.
      //
      // Once a product has a reservation object at all, a missing key means
      // zero. Before that it means untouched, and the delta is nothing.
      const pending = p._reservation
        ? Number(p._reservation[it.itemId]) || 0
        : committed;

      return sum + (pending - committed);
    }, 0);
  };
  const reservationCard = (
    <Card
      className="border-0 shadow-sm h-100"
      style={{ borderLeft: `5px solid ${RESV_ACCENT}` }}
    >
      <Card.Header className="fw-semibold py-2" style={{ background: `${RESV_ACCENT}1f` }}>
        <div className="d-flex align-items-center justify-content-between gap-2 flex-wrap">
          <div className="d-flex align-items-center gap-2">
            <ProductColorDot colorName={product?.color} />
            <span style={{ color: RESV_ACCENT }}>Order Reservation</span>
          </div>
          {/* continue-mode: opt-in to reserve mid-flow, OR re-open the editor on a
              job that already reserved (to add more / fix a mistaken reservation) */}
          {!isReservationMode && (
            <Form.Check
              type="switch"
              id={`resv-toggle-${product?._id}-${phaseName}`}
              checked={resvOpen}
              onChange={() =>
                setResvOpen(v => {
                  const nv = !v;
                  if (nv) seedReservationFromJob();
                  return nv;
                })
              }
              label={
                <span className="fs-10" style={{ color: RESV_ACCENT }}>
                  {resvOpen
                    ? 'Editing reservation'
                    : anyJobResv
                      ? 'Edit reservation'
                      : 'Reserve'}
                </span>
              }
            />
          )}
        </div>
      </Card.Header>
      <Card.Body className="pt-2 pb-2" style={{ maxHeight: 360, overflowY: 'auto' }}>
        {!(resvEditable || anyJobResv) ? (
          <div className="text-muted fs-10 fst-italic py-2">
            No reservation for this job. Flip the toggle above to reserve final
            drums / coils for this order.
          </div>
        ) : (
          <>
        <div className="d-flex flex-wrap gap-3 align-items-start">
        {orders.map((ord, oi) => {
          const items = Array.isArray(ord.deliveryPlanItems) ? ord.deliveryPlanItems : [];
          // Meters cap for reservations = how much of the ORDER THIS lot launched.
          // In create-launch use the split-aware _launch.lengthToOrders (ord
          // .allocatedFromThisJob_m holds the FULL order launch, not the per-job split,
          // so it would let a split job reserve more than its lot produces).
          // PER-ORDER budget: this order's own allocation from the job, not the job's
          // whole order total. allocatedFromThisJob_m is per-order; cap it by the job's
          // total launch (_launch.lengthToOrders) so a split single-order job can't
          // over-reserve either. (continue mode already gives a per-order value.)
          const orderAlloc = Number(ord.allocatedFromThisJob_m) || 0;
          const jobTotal = Number(product?._launch?.lengthToOrders) || 0;
          const jobBudget = isReservationMode
            ? orderAlloc > 0
              ? Math.min(orderAlloc, jobTotal || orderAlloc)
              : jobTotal
            : orderAlloc;

          /**
           * The ceiling on what this job may reserve here — and there is always
           * one, which is the whole point.
           *
           * `jobBudget` is what the job was allocated to this order, and that
           * is the right bound while an allocation exists. But clearing a
           * reservation REMOVES the allocation (a promise of nothing is not a
           * promise), and the old code then fell back to no bound at all: a
           * 10,000 m product offered 150 coils of 100 m, because the only limit
           * left was what the order wanted.
           *
           * With no allocation the bound is what the product can still make and
           * has promised to nobody — which the backend sends as
           * `jobUnpromised_m`, and which falls back to the lot's own length.
           */
          const jobUnpromised = Number(ord.jobUnpromised_m);
          const jobCapacity = Number.isFinite(jobUnpromised)
            ? jobUnpromised
            : Math.max(
                0,
                Number(product?.plannedLength) || Number(product?.length) || 0
              );
          const budgetBase = jobBudget > 0 ? jobBudget : jobCapacity;

          // How many products in this wizard are launched against this order —
          // it decides whether the save button is saving one card or several.
          const jobsOnThisOrder = (Array.isArray(allProducts) ? allProducts : [])
            .filter(p =>
              (Array.isArray(p?._orders) ? p._orders : []).some(
                o => String(o?.orderId ?? o?._id) === String(ord?.orderId ?? ord?._id)
              )
            ).length;

          const orderItemIds = new Set(items.map(it => it.itemId));
          const launchedLen = existingExecs
            .filter(e => orderItemIds.has(e.orderItemId))
            .reduce((s, e) => s + (Number(e.plannedLength) || 0), 0);
          const priorLaunchedM = items.reduce(
            (s, it) => s + priorLaunchedMine(it) * (Number(it.lengthPerUnit) || 0),
            0
          );
          const budgetRemaining =
            jobBudget > 0 ? Math.max(0, jobBudget - launchedLen - priorLaunchedM) : Infinity;
          // Continue / plan-next-phase: if THIS job carries a per-job reservation,
          // show ONLY the items it committed to, with jobReservedQty as the target.
          // While editing (resvEditable) show ALL items with the full remaining qty.
          const jobResvActive =
            !resvEditable && items.some(it => Number(it.jobReservedQty) > 0);
          // An item cancelled off the order has qtyPlanned 0, which makes its
          // `avail` 0 — it could never be reserved. It was still drawn, as a
          // dead "0/1 reserved" chip next to the live ones. Hidden only when
          // there is nothing left AND nothing already reserved on it, so an
          // item this job did reserve never disappears from under the operator.
          const hasSomethingToShow = it =>
            Number(it.qtyPlanned) > 0 ||
            Number(it.jobReservedQty) > 0 ||
            Number(reservedOf(it)) > 0;

          const shownItems = (
            jobResvActive
              ? items.filter(it => Number(it.jobReservedQty) > 0)
              : items
          ).filter(hasSomethingToShow);
          const targetOf = it =>
            resvEditable
              ? Number(it.qtyPlanned) || 0
              : jobResvActive
                ? Number(it.jobReservedQty) || 0
                : Number(it.qtyPlanned) || 0;
          const reservedM = shownItems.reduce(
            (s, it) => s + reservedOf(it) * (Number(it.lengthPerUnit) || 0),
            0
          );
          const pct = jobBudget > 0 ? Math.min(100, Math.round((reservedM / jobBudget) * 100)) : 0;
          const ofKind = k => shownItems.filter(it => String(it.kind || '').toUpperCase() === k);
          const sumL = arr => arr.reduce((s, it) => s + reservedOf(it), 0);
          const sumT = arr => arr.reduce((s, it) => s + targetOf(it), 0);
          const drums = ofKind('DRUM');
          const coils = ofKind('COIL');
          return (
            <div
              key={oi}
              className="mb-1"
              style={{ flex: '1 1 240px', minWidth: 240 }}
            >
              {/* ORD-ID on top, reserved meters stacked below it */}
              <div className="mb-1">
                <Badge pill style={{ background: RESV_ACCENT, color: '#fff' }}>
                  {ord.orderCode || '—'}
                </Badge>
                <div className="fs-10 text-body-tertiary mt-1">
                  {reservedM.toLocaleString()} / {jobBudget.toLocaleString()} m reserved
                </div>
                {resvEditable && jobBudget > 0 && reservedM >= jobBudget && (
                  <div className="fs-10 fw-semibold" style={{ color: '#fd7e14' }}>
                    This job is full — reserve the rest on another job of this order
                  </div>
                )}
                {/* What another job of the same order is already holding. The
                    steppers cap on it, so without it the ceiling looks arbitrary
                    — "why can I only reserve 30 of 130?" */}
                {resvEditable && (() => {
                  const heldElsewhere = items.reduce(
                    (s, it) =>
                      s +
                      Math.max(
                        0,
                        (Number(it.launchedQty) || 0) - (Number(it.jobReservedQty) || 0)
                      ),
                    0
                  );
                  if (heldElsewhere <= 0) return null;
                  return (
                    <div className="fs-10 text-body-tertiary fst-italic">
                      {heldElsewhere} unit(s) reserved by other jobs of this order
                    </div>
                  );
                })()}
                {/* One button, not two.
                    They did the same thing whenever this order had a single
                    job, and when it had several the per-job save was the
                    trap the other one exists to avoid: saving one card at a
                    time leaves the intermediate state over-reserved, and the
                    backend clamps against it. So the safe one does the work
                    always, and only its label changes. */}
                {resvEditable && onSaveAllReservations && (
                  <button
                    type="button"
                    className="btn btn-sm p-0 px-2 mt-1 me-1 rounded"
                    style={{
                      fontSize: 10,
                      background: RESV_ACCENT,
                      color: '#fff',
                      border: 'none',
                      fontWeight: 700
                    }}
                    title={
                      jobsOnThisOrder > 1
                        ? `Save the reservation for all ${jobsOnThisOrder} products of this order — releases before it takes, so no step is briefly over-reserved`
                        : 'Save this reservation'
                    }
                    onClick={() => onSaveAllReservations(ord, items)}
                  >
                    {jobsOnThisOrder > 1
                      ? `Save all ${jobsOnThisOrder} products`
                      : 'Save reservation'}
                  </button>
                )}
                {resvEditable && reservedM > 0 && (
                  <button
                    type="button"
                    className="btn btn-sm p-0 px-2 mt-1 rounded"
                    style={{
                      fontSize: 10,
                      background: 'rgba(220,53,69,0.12)',
                      color: '#dc3545',
                      border: 'none'
                    }}
                    title="Release everything this job reserved on this order"
                    onClick={() =>
                      items.forEach(it =>
                        onReserveChange && onReserveChange(product._id, it.itemId, 0)
                      )
                    }
                  >
                    Clear all for this order
                  </button>
                )}
                {!isReservationMode && (() => {
                  // Order-wide capacity still open to reserve (across all jobs).
                  // Same arithmetic as the steppers, or the two disagree: this
                  // line said "101 still reservable" while the controls next to
                  // it capped at 70, because it ignored what the sibling jobs in
                  // this wizard had just given up or taken.
                  const freeOf = it => Math.max(0, freeUnitsOf(it) - siblingDeltaOf(it));
                  const freeUnits = items.reduce((s, it) => s + freeOf(it), 0);
                  const freeMeters = items.reduce(
                    (s, it) => s + freeOf(it) * (Number(it.lengthPerUnit) || 0),
                    0
                  );
                  if (freeUnits <= 0) return null;
                  return (
                    <div className="fs-10 fw-semibold mt-1" style={{ color: '#dc3545' }}>
                      ⚠ Still reservable on this order: {freeUnits} unit
                      {freeUnits !== 1 ? 's' : ''} ({freeMeters.toLocaleString()} m)
                      {jobBudget > 0 && reservedM >= jobBudget && (
                        <span className="fw-normal fst-italic">
                          {' '}— not by this job, it is full
                        </span>
                      )}
                      {!resvEditable && (
                        <span className="fw-normal fst-italic">
                          {' '}— toggle “Edit reservation” to allocate
                        </span>
                      )}
                    </div>
                  );
                })()}
              </div>
              <div
                className="rounded-pill mb-2"
                style={{ height: 6, background: 'rgba(128,128,128,0.2)', overflow: 'hidden' }}
              >
                <div
                  style={{
                    width: `${pct}%`,
                    height: '100%',
                    background: pct >= 100 ? '#22c55e' : RESV_ACCENT
                  }}
                />
              </div>
              <div className="d-flex flex-wrap gap-3 mb-2 fs-10">
                {drums.length > 0 && (
                  <span className="fw-semibold">
                    🛢 Drums:{' '}
                    <span style={{ color: RESV_ACCENT }}>{sumL(drums)}</span> / {sumT(drums)}{' '}
                    {resvEditable ? 'reserved' : jobResvActive ? 'produced' : 'in execution'}
                  </span>
                )}
                {coils.length > 0 && (
                  <span className="fw-semibold">
                    🎗 Coils:{' '}
                    <span style={{ color: RESV_ACCENT }}>{sumL(coils)}</span> / {sumT(coils)}{' '}
                    {resvEditable ? 'reserved' : jobResvActive ? 'produced' : 'launched'}
                  </span>
                )}
              </div>
              {shownItems.length === 0 ? (
                <div className="text-muted fs-10">No delivery plan items to reserve.</div>
              ) : (
                <div className="d-flex flex-wrap gap-2">
                  {shownItems.map((it, ii) => {
                    const kind = String(it.kind || '').toUpperCase();
                    const len = Number(it.lengthPerUnit) || 0;
                    // create: planned = REMAINING; continue: planned = this job's target.
                    const planned = targetOf(it);
                    const reserved = reservedOf(it);
                    const full = planned > 0 && reserved >= planned;
                    const specs =
                      kind === 'DRUM'
                        ? [it.drumMaterial, it.drumDiameter ? `Ø${it.drumDiameter}` : '']
                            .filter(Boolean)
                            .join(' ') || 'Drum'
                        : 'Coil';

                    // create-launch OR opted-in continue: edit the plan with steppers.
                    if (resvEditable) {
                      // How many of this item THIS job may reserve:
                      //  - create-launch: qtyPlanned is REMAINING → remaining − what
                      //    sibling products in this launch already reserved.
                      //  - continue: this job's current reservation + the order's free
                      //    pool (total − order-wide reserved/launched). The backend
                      //    clamps to the order total, so this can't over-reserve.
                      const avail = isReservationMode
                        ? Math.max(0, planned - otherReservedOf(it.itemId))
                        : Math.max(
                            0,
                            (Number(it.jobReservedQty) || 0) +
                              freeUnitsOf(it) -
                              siblingDeltaOf(it)
                          );
                      const atMax = avail > 0 && reserved >= avail;
                      const budgetOk = reservedM + len <= budgetBase;
                      const canInc = reserved < avail && budgetOk && len > 0;
                      const canDec = reserved > 0;
                      // "Max" = reserve as many as possible in one click (no 150
                      // clicks), capped by the order capacity (avail) AND by the
                      // metres this product can actually make.
                      //
                      // There is ALWAYS a physical ceiling. This used to fall
                      // back to Infinity when the job had no allocation, and
                      // clearing a reservation removes the allocation — so
                      // emptying the card and saving it left a 10,000 m product
                      // offering 150 coils of 100 m. The job's unpromised
                      // length is the bound when nothing is allocated yet.
                      const budgetRoom = Math.max(
                        0,
                        budgetBase - (reservedM - reserved * len)
                      );
                      const maxByBudget = len > 0 ? Math.floor(budgetRoom / len) : 0;
                      const maxUnits = Math.max(0, Math.min(avail, maxByBudget));
                      const setQ = q =>
                        onReserveChange &&
                        onReserveChange(
                          product._id,
                          it.itemId,
                          Math.max(0, Math.min(avail, q))
                        );
                      const stepBtn = {
                        background: `${RESV_ACCENT}22`,
                        color: RESV_ACCENT,
                        border: 'none',
                        fontSize: 14,
                        fontWeight: 700,
                        lineHeight: 1
                      };
                      return (
                        <div
                          key={ii}
                          className={`px-2 py-1 rounded border ${atMax ? 'border-success' : ''}`}
                          style={{
                            minWidth: 130,
                            background: atMax ? 'rgba(34,197,94,0.12)' : `${RESV_ACCENT}0f`,
                            borderLeft: `4px solid ${atMax ? '#22c55e' : resColorHex}`
                          }}
                        >
                          <div className="fw-semibold" style={{ fontSize: 11 }}>
                            {specs}
                          </div>
                          <div style={{ fontSize: 10 }}>{len.toLocaleString()}m</div>
                          <div className="d-flex align-items-center justify-content-between mt-1">
                            <button
                              type="button"
                              className="btn btn-sm p-0 px-2 rounded"
                              style={stepBtn}
                              disabled={!canDec}
                              onClick={() => setQ(reserved - 1)}
                            >
                              −
                            </button>
                            <span
                              style={{
                                fontSize: 12,
                                fontWeight: 700,
                                color: atMax ? '#198754' : RESV_ACCENT
                              }}
                            >
                              {reserved}/{avail}
                              {atMax ? ' ✓' : ''}
                            </span>
                            <button
                              type="button"
                              className="btn btn-sm p-0 px-2 rounded"
                              style={stepBtn}
                              disabled={!canInc}
                              // The ceiling on screen is the ORDER's; the metre
                              // budget is the job's and is invisible. When that
                              // is what blocks, "70/100 and + greyed out" reads
                              // as a bug rather than a limit.
                              title={
                                canInc
                                  ? ''
                                  : !budgetOk
                                    ? `This job can make ${jobBudget.toLocaleString()} m for this order and has reserved ${reservedM.toLocaleString()} m — one more is ${(reservedM + len).toLocaleString()} m. Reserve the rest on another job.`
                                    : reserved >= avail
                                      ? 'The order has nothing left free for this item'
                                      : ''
                              }
                              onClick={() => setQ(reserved + 1)}
                            >
                              +
                            </button>
                          </div>
                          {maxUnits > reserved && (
                            <button
                              type="button"
                              className="btn btn-sm p-0 w-100 mt-1 rounded"
                              style={{ ...stepBtn, fontSize: 10 }}
                              onClick={() => setQ(maxUnits)}
                              // Which of the two limits is actually binding. The
                              // order's free units are on screen; the job's
                              // allocated metres are not, so when THEY are the
                              // smaller number the button looks stale rather
                              // than limited.
                              title={
                                maxByBudget <= avail
                                  ? `Limited by this job: ${jobBudget.toLocaleString()} m allocated to this order = ${maxByBudget} unit(s). The order itself has ${avail} free — to go higher, this job needs more of the order allocated to it.`
                                  : `Limited by the order: ${avail} unit(s) still free.`
                              }
                            >
                              Max ({maxUnits})
                            </button>
                          )}
                          {/* Straight to zero. Stepping 150 coils down one at a
                              time is not a decision anybody makes — and when the
                              automatic trim gets a case wrong, this is the way
                              out of it. */}
                          {reserved > 0 && (
                            <button
                              type="button"
                              className="btn btn-sm p-0 w-100 mt-1 rounded"
                              style={{
                                ...stepBtn,
                                fontSize: 10,
                                background: 'rgba(220,53,69,0.12)',
                                color: '#dc3545'
                              }}
                              onClick={() => setQ(0)}
                              title="Release this job's reservation on this item"
                            >
                              Clear
                            </button>
                          )}
                        </div>
                      );
                    }

                    const canClick =
                      isDeliveryPhase &&
                      clickableKinds.includes(kind) &&
                      !full &&
                      hasUpstreamSelected &&
                      !inputConsumed &&
                      len > 0 &&
                      len <= inputRemaining;
                    return (
                      <div
                        key={ii}
                        onClick={() => canClick && handleQuickSelect(it, planned, budgetRemaining)}
                        title={canClick ? 'Click to reserve one on the selected input' : ''}
                        className={`px-2 py-1 rounded border ${full ? 'border-success' : ''}`}
                        style={{
                          cursor: canClick ? 'pointer' : 'default',
                          minWidth: 108,
                          background: full ? 'rgba(34,197,94,0.12)' : `${RESV_ACCENT}0f`,
                          borderLeft: `4px solid ${full ? '#22c55e' : resColorHex}`
                        }}
                      >
                        <div className="fw-semibold" style={{ fontSize: 11 }}>
                          {specs}
                        </div>
                        <div style={{ fontSize: 10 }}>{len.toLocaleString()}m</div>
                        <div
                          style={{
                            fontSize: 10,
                            color: full ? '#198754' : '#666',
                            fontWeight: 700
                          }}
                        >
                          {reserved}/{planned} {full ? '✓' : 'reserved'}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
        </div>
        <div className="text-muted fs-10 fst-italic mt-1">
          {resvEditable
            ? 'Reserve the final drums / coils with − / + — recorded on the order at submit.'
            : anyJobResv
              ? 'These are the drums this job reserved at launch — produce them at the exterior sheath / coiling phase.'
              : isDeliveryPhase
                ? 'Click a drum to reserve it on the selected input (updates Order Context).'
                : 'Reserve the final drums at the exterior sheath / coiling phase.'}
        </div>
          </>
        )}
      </Card.Body>
    </Card>
  );

  // Reservations are OPTIONAL. Show the Order Reservation card in create-launch (the
  // editor), when THIS job carries a reservation, OR in plan-next-phase so the planner
  // can opt-in with the header toggle (otherwise it's just a compact "No reservation").
  const showReservation =
    isReservationMode || anyJobResv || mode === 'continue';

  return (
    <Row className="g-2 mb-3">
      <Col xs={12} xl={showReservation ? 7 : 12}>
        <Card
          className="border-0 shadow-sm h-100"
          style={{ borderLeft: '5px solid #198754' }}
        >
      <Card.Header className="bg-success-subtle fw-semibold py-2">
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
          <span>{isDeliveryPhase ? 'Order Requirements' : 'Order Context'}</span>
          <div className="d-flex align-items-center gap-2 flex-wrap">
            {isDeliveryPhase && (
              <Form.Select
                size="sm"
                value={quickMachine}
                onChange={e => setQuickMachine(e.target.value)}
                style={{ width: 220 }}
                title="Quick launch: machine applied to every execution you click below"
              >
                <option value="">Machine for clicks…</option>
                {MACHINE_OPTIONS.map(m => (
                  <option key={m.value} value={m.value}>
                    {m.label}
                  </option>
                ))}
              </Form.Select>
            )}
            {isDeliveryPhase && !hasUpstreamSelected && (
              <Badge bg="warning" text="dark" className="fs-10">
                {flatColors
                  ? `Select a drum of every colour first (${flatColors.join(
                      ' + '
                    )})`
                  : 'Select an input first'}
              </Badge>
            )}
            {isDeliveryPhase && hasUpstreamSelected && inputConsumed && (
              <Badge bg="success" className="fs-10">
                Input fully consumed — select another input
              </Badge>
            )}
            {isDeliveryPhase && hasUpstreamSelected && !inputConsumed && (
              <Badge bg="info" className="fs-10">
                Click an item to add an execution (
                {inputRemaining.toLocaleString()}m remaining)
              </Badge>
            )}
          </div>
        </div>
      </Card.Header>
      <Card.Body className="pt-2 pb-2" style={{ maxHeight: 360, overflowY: 'auto' }}>
        {orders.map((ord, oi) => {
          // The job's total allocation to this order. The drum/coil split is
          // FREE up to this budget — it is NOT pre-assigned greedily to drums.
          // Each item shows its full order requirement; clicks are bounded by
          // what's left of this budget (and of the input token), so the planner
          // can do e.g. 7 drums + the remaining length routed to coils.
          const orderTotal = Number(ord.plannedLength_m) || 0;
          const jobBudget = Number(ord.allocatedFromThisJob_m) || 0;
          const useBudget = jobBudget > 0;
          const orderItemIds = new Set(
            (ord.deliveryPlanItems || []).map(it => it.itemId)
          );
          const launchedLenThisOrder = existingExecs
            .filter(e => orderItemIds.has(e.orderItemId))
            .reduce((s, e) => s + (Number(e.plannedLength) || 0), 0);
          // meters already launched on the order by PRIOR plan-next-phase tokens
          // (sibling tokens of this job) — so the budget doesn't reset.
          const priorLaunchedM = (ord.deliveryPlanItems || []).reduce(
            (s, it) => s + priorLaunchedMine(it) * (Number(it.lengthPerUnit) || 0),
            0
          );
          const budgetRemaining = useBudget
            ? Math.max(0, jobBudget - launchedLenThisOrder - priorLaunchedM)
            : Infinity;

          // Metres this job could still promise to this order: what it has not
          // promised to anybody, bounded by what the order still needs. Both
          // sides matter — over either edge is cable promised twice.
          //
          // The backend sends both figures because only it can see them: the
          // job's other orders, and the line as the other jobs left it. Reading
          // them off the wizard's own copy gave "unpromised = everything" and
          // "the order needs all of it", so a job with nothing free offered to
          // allocate its whole length to a line that was already full.
          const jobUnpromised = Math.max(
            0,
            Number(ord?.jobUnpromised_m) >= 0
              ? Number(ord.jobUnpromised_m)
              : (Number(product?.plannedLength) || Number(product?.length) || 0) -
                  (product?.allocations || []).reduce(
                    (s, a) => s + (Number(a?.allocatedMeters) || 0),
                    0
                  )
          );
          const orderStillNeeds = Math.max(
            0,
            Number(ord?.remainingTotal_m) >= 0
              ? Number(ord.remainingTotal_m)
              : (Number(ord?.orderTotal) || orderTotal || 0) -
                  (Number(ord?.allocatedTotal_m) || 0)
          );
          const allocatableM = Math.floor(Math.min(jobUnpromised, orderStillNeeds));

          return (
          <div key={oi} className="mb-2">
            <div className="d-flex align-items-center gap-2 mb-1 flex-wrap">
              <Badge bg="primary">{ord.orderCode || '—'}</Badge>
              {ord.client && (
                <Badge bg="info" variant="phoenix" className="fs-10">
                  {ord.client}
                </Badge>
              )}
              <span className="text-muted fs-10">
                • {(useBudget ? jobBudget : orderTotal).toLocaleString()} m for
                this job
                {useBudget && jobBudget < orderTotal
                  ? ` (order ${orderTotal.toLocaleString()} m)`
                  : ''}
              </span>
              {isDeliveryPhase && useBudget && (
                <Badge
                  bg={budgetRemaining > 0 ? 'info' : 'secondary'}
                  className="fs-10"
                >
                  {budgetRemaining.toLocaleString()} m left to assign
                </Badge>
              )}

              {/* Detach this product from THIS order. A context can hold
                  several, so it is per-order and not a single button for the
                  card. What it undoes: the metres go back to stock, the
                  reservations on this order's packages go, and the order line
                  reads as unallocated again — free to be promised to another
                  product, or re-reserved from sales. */}
              {/* Only when it would actually do something: the order still
                  needs cable AND this job has metres it has not promised to
                  anyone. Same rule as "still reservable" — offered when the
                  situation allows it, absent otherwise. */}
              {onAllocateMore && allocatableM > 0 && (
                <button
                  type="button"
                  className="btn btn-sm p-0 px-2 rounded ms-auto"
                  style={{
                    fontSize: 10,
                    background: 'rgba(25,135,84,0.14)',
                    color: '#198754',
                    border: 'none',
                    fontWeight: 700
                  }}
                  title={`Promise ${allocatableM.toLocaleString()} m more of this product to this order. It comes out of the part that was going to stock, and raises how much this job may reserve here.`}
                  onClick={() => onAllocateMore(product, ord, allocatableM)}
                >
                  + Allocate {allocatableM.toLocaleString()} m to this order
                </button>
              )}

              {onDetachOrder && (
                <button
                  type="button"
                  className={`btn btn-sm p-0 px-2 rounded${
                    onAllocateMore && allocatableM > 0 ? '' : ' ms-auto'
                  }`}
                  style={{
                    fontSize: 10,
                    background: 'rgba(220,53,69,0.12)',
                    color: '#dc3545',
                    border: 'none',
                    fontWeight: 700
                  }}
                  title="Release this order — its metres return to stock and its reservations are cleared"
                  onClick={() => onDetachOrder(product, ord)}
                >
                  Release order
                </button>
              )}
            </div>

            <div className="d-flex flex-wrap gap-2">
              {(ord.deliveryPlanItems || [])
                .filter(
                  item =>
                    !coilOnly ||
                    String(item.kind || '').toUpperCase() === 'COIL'
                )
                // "wood Ø1000: 0 × 1,500m" is not context, it is a package the
                // customer cancelled. Nothing is owed on it, so it is not part
                // of what this launch has to cover.
                .filter(item => Number(item.qtyPlanned) > 0)
                .map((item, ii) => {
                const kind = String(item.kind || '').toUpperCase();
                const len = Number(item.lengthPerUnit) || 0;
                const specs =
                  kind === 'DRUM'
                    ? [
                        item.drumMaterial,
                        item.drumDiameter ? `Ø${item.drumDiameter}` : ''
                      ]
                        .filter(Boolean)
                        .join(' ')
                    : 'Coil';

                // Intermediate phases (rope, conductor_isolation, …) only
                // forward length onward — the drum/coil split is decided at the
                // FINAL phase. Show the order's delivery plan as READ-ONLY
                // context (full planned qty, no launched/Done/click) so nothing
                // is prematurely "cut" or flagged done here.
                if (!isDeliveryPhase) {
                  const plannedQty = Number(item.qtyPlanned) || 0;
                  // Between phases the split isn't decided yet — keep it a COLOURED
                  // context card (fixed teal, not the product colour, which is grey/white
                  // for white cables) instead of a flat grey box.
                  return (
                    <div
                      key={ii}
                      className="px-2 py-1 rounded border d-flex align-items-center gap-2"
                      style={{
                        background: 'rgba(13,110,253,0.07)',
                        borderColor: 'rgba(13,110,253,0.25)',
                        borderLeft: '4px solid #0d6efd'
                      }}
                    >
                      <span className="fw-semibold" style={{ fontSize: 12 }}>
                        {specs || kind}: {plannedQty} × {len.toLocaleString()}m
                      </span>
                      <span className="text-muted" style={{ fontSize: 10 }}>
                        order target
                      </span>
                    </div>
                  );
                }

                // Full order requirement for this item — the split between
                // drums & coils is decided freely here, not pre-capped.
                const qty = Number(item.qtyPlanned) || 0;
                // done = this session's executions + what a PRIOR plan-next-phase
                // already stamped on the order (e.g. a sibling rope token). So the
                // count persists when you come back to plan the next token, and the
                // remaining shrinks correctly instead of resetting to 0.
                const totalDone = doneOfAll(item);
                const isDone = qty > 0 && totalDone >= qty;
                // drums & coils share ONE job budget; once it's spent, the
                // not-yet-launched items can't be made from THIS job.
                const budgetUnits =
                  budgetRemaining === Infinity
                    ? Number.MAX_SAFE_INTEGER
                    : len > 0
                      ? Math.floor(budgetRemaining / len)
                      : 0;
                const budgetBlocked = budgetUnits <= 0;
                const canClick =
                  clickableKinds.includes(kind) &&
                  !isDone &&
                  !budgetBlocked &&
                  hasUpstreamSelected &&
                  !inputConsumed &&
                  len <= inputRemaining;

                // units one click adds: coils batch the input, drums add 1 each
                // — bounded by both the input token AND the remaining budget.
                const remainingUnits = Math.max(0, qty - totalDone);
                const fitUnits = Math.min(
                  len > 0 ? Math.floor(inputRemaining / len) : 0,
                  budgetUnits
                );
                const willAdd =
                  kind === 'COIL'
                    ? Math.min(remainingUnits, fitUnits)
                    : fitUnits >= 1 && remainingUnits >= 1
                      ? 1
                      : 0;
                const tooSmall =
                  clickableKinds.includes(kind) &&
                  hasUpstreamSelected &&
                  !inputConsumed &&
                  !isDone &&
                  !budgetBlocked &&
                  fitUnits <= 0;

                // GREEN = clickable (can launch). RED = maxed out (order fully
                // launched OR this job's budget spent → nothing more possible here).
                // Neutral = simply not ready yet (no input / input consumed / too small).
                const maxed = isDone || budgetBlocked;
                return (
                  <div
                    key={ii}
                    className={`px-3 py-2 rounded border ${
                      canClick
                        ? 'border-success bg-success-subtle'
                        : maxed
                          ? 'border-danger bg-danger-subtle'
                          : 'bg-light'
                    }`}
                    style={{
                      cursor: canClick ? 'pointer' : 'default',
                      opacity: maxed && totalDone === 0 ? 0.6 : 1
                    }}
                    onClick={() =>
                      canClick && handleQuickSelect(item, qty, budgetRemaining)
                    }
                  >
                    <div className="fw-semibold" style={{ fontSize: 13 }}>
                      {specs || kind}: {qty} × {len.toLocaleString()}m
                    </div>
                    <div className="text-muted" style={{ fontSize: 11 }}>
                      {isDone
                        ? 'Done'
                        : budgetBlocked && totalDone === 0
                          ? 'job budget used'
                          : `${totalDone}/${qty} launched`}
                      {canClick && willAdd > 0 ? ` · +${willAdd} on click` : ''}
                      {tooSmall ? ' · input too small' : ''}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          );
        })}

        {!isDeliveryPhase && allItems.length > 0 && (
          <div className="text-muted fs-10 fst-italic">
            Split decided at the final phase — this step only forwards length.
          </div>
        )}
        {!allItems.length && (
          <div className="text-muted fs-10">No delivery plan items.</div>
        )}
        {allItems.length > 0 &&
          coilOnly &&
          !allItems.some(
            it => String(it.kind || '').toUpperCase() === 'COIL'
          ) && (
            <div className="text-muted fs-10">
              No coil deliveries planned for this order.
            </div>
          )}
      </Card.Body>
        </Card>
      </Col>
      {showReservation && (
        <Col xs={12} xl={5}>
          {reservationCard}
        </Col>
      )}
    </Row>
  );
};

/**
 * The job behind a wizard product.
 *
 * In continue / plan-next-phase the wizard's product IS the job, and its id
 * arrives as `_id`. Spelled out once because it was written twice and the second
 * copy dropped the `_id` fallback: "Release order" then found no job, silently
 * took the create-launch path, and edited local state that nothing ever saved.
 */
const jobIdOf = product =>
  product?.jobId || product?._jobId || product?._id || null;

const RESERVATION_SAVE_NOTE =
  'Saved. The other jobs of this order now see the change.';

const REDIRECT_AFTER_LAUNCH_MS = 2500;

/**
 * What a successful launch says.
 *
 * A tick, what was made, and then it takes itself back to a fresh launch form —
 * which is where a planner is going anyway, because products are launched in
 * runs of several. The job codes are there to read, and the countdown is
 * cancellable, so the auto-return never takes the page away from somebody who
 * wants to open the product instead.
 *
 * IN THE MIDDLE OF THE SCREEN, as a modal. It used to stand where Submit stood,
 * at the bottom of a review that is several screens long — so on anything but a
 * tall monitor the confirmation appeared below the fold and the planner was left
 * looking at the same plan with no visible sign it had been accepted. A modal
 * also answers the question the inline panel answered by accident: there is no
 * Submit button on screen any more, so the same plan cannot be launched twice.
 *
 * `backdrop="static"` for the same reason — dismissing it by clicking next to it
 * would put the Submit button back.
 */
const LaunchedPanel = ({ jobs = [], onLaunchAnother }) => {
  const navigate = useNavigate();
  const known = jobs.filter(j => j?.jobId);
  const many = known.length > 1;

  const [secondsLeft, setSecondsLeft] = useState(
    Math.round(REDIRECT_AFTER_LAUNCH_MS / 1000)
  );
  const [holding, setHolding] = useState(false);

  useEffect(() => {
    if (holding || !onLaunchAnother) return undefined;
    // One timer for the return, one for the count the reader sees. Cleared on
    // unmount, so a click that navigates first cannot reset the wizard behind
    // the page it moved to.
    const done = setTimeout(onLaunchAnother, REDIRECT_AFTER_LAUNCH_MS);
    const tick = setInterval(() => setSecondsLeft(s => (s > 0 ? s - 1 : 0)), 1000);
    return () => {
      clearTimeout(done);
      clearInterval(tick);
    };
  }, [holding, onLaunchAnother]);

  return (
    <Modal show centered backdrop="static" keyboard={false} contentClassName="border-success">
      <Modal.Body className="text-center py-4">
        {/* Dependency-free: this file imports no icon set, and a tick is not a
            reason to pull one in. */}
        <div
          className="d-inline-flex align-items-center justify-content-center mb-3"
          style={{
            width: 64,
            height: 64,
            borderRadius: '50%',
            background: 'var(--phoenix-success)',
            color: '#fff',
            fontSize: 34,
            lineHeight: 1,
            fontWeight: 700
          }}
          aria-hidden="true"
        >
          ✓
        </div>

        <h4 className="text-success mb-1">
          {jobs.length} {jobs.length === 1 ? 'product' : 'products'} launched
        </h4>
        <div className="fs-9 text-700 mb-3">
          {many
            ? 'Each one is its own job, queued on its machines — a multi-colour launch makes one job per colour.'
            : 'Queued on its machines.'}
          {!known.length && ' The launch was accepted but did not report its job code.'}
        </div>

        {known.length > 0 && (
          <div className="d-flex flex-wrap justify-content-center gap-2 mb-3">
            {known.map(j => (
              <Button
                key={j.jobId}
                size="sm"
                variant="phoenix-success"
                onClick={() =>
                  navigate(`/apps/project-management/project-details/${j.jobId}`)
                }
                title="Open this product"
              >
                {j.jobCode || 'JOB'}
              </Button>
            ))}
          </div>
        )}

        <div className="d-flex flex-wrap justify-content-center gap-2">
          <Button
            variant="phoenix-secondary"
            size="sm"
            onClick={() => navigate('/apps/project-management/project-monitor')}
          >
            Project Monitor
          </Button>
          {onLaunchAnother && (
            <Button variant="primary" size="sm" onClick={onLaunchAnother}>
              Launch another now
            </Button>
          )}
        </div>

        {onLaunchAnother && (
          <div className="fs-10 text-700 mt-3">
            {holding ? (
              'Staying here.'
            ) : (
              <>
                Back to a new launch in {secondsLeft}s ·{' '}
                <Button
                  variant="link"
                  className="p-0 fs-10"
                  onClick={() => setHolding(true)}
                >
                  stay here
                </Button>
              </>
            )}
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
};

const CreateWizzardForm = ({
  standardData = [],
  setStandardsData,
  clearStandardsData,
  handleSubmitFc,
  mode = 'create',
  // Reconcile mode: runs the wizard exactly like edit (mode="edit") but turns on the
  // per-produced-drum "Belongs to product" swap control. Kept as its own flag so NONE
  // of the many `mode === 'edit'` branches change behaviour — only the swap UI appears.
  reconcile = false,
  // Edit mode: pre-seed the group-by toggle ({ phase: 'color'|'wireDiameter' }) so
  // editing a group-by launch preserves the grouping unless the user turns it off.
  initialGroupByPhases = {}
}) => {
  const { data: drumsData } = useDrums();
  const drumLibrary = drumsData?.data || [];

  /**
   * Persist a reservation edit immediately, outside the phase plan.
   *
   * `mode === 'create'` has nothing to save to yet — the job does not exist —
   * so this is offered only once there is a job to write against.
   */
  const handleSaveReservation = async (product, items, reservation) => {
    const jobId = jobIdOf(product);
    if (!jobId || mode === 'create') return;

    // Clearing an item REMOVES its key (see handleReserveChange — a plan with no
    // entry means "not reserved"). The save only changes what it is sent, so a
    // cleared item would simply be absent and nothing would happen: the request
    // came back 200 having done nothing at all.
    //
    // Every item on this order gets an explicit number, zero included. That is
    // what makes "release these forty" a thing the backend can act on.
    const payload = {};
    for (const it of Array.isArray(items) ? items : []) {
      if (!it?.itemId) continue;
      payload[it.itemId] = Number(reservation?.[it.itemId]) || 0;
    }

    try {
      const res = await saveJobReservation(jobId, payload);
      applySavedReservation(jobId, res?.reservation);
      applyResync(jobId, res?.resynced);
      const clamped = (res?.rejected || []).length
        ? ` Some values were reduced to what the order still has free.`
        : '';
      const moved = (res?.resynced || [])
        .filter(r => Number(r.to) !== Number(r.from))
        .map(
          r =>
            ` What this job owes the order is now ${Number(
              r.to || 0
            ).toLocaleString()} m.`
        )
        .join('');
      window.alert(RESERVATION_SAVE_NOTE + clamped + moved);
    } catch (err) {
      window.alert(
        `Could not save the reservation: ${
          err?.response?.data?.error || err.message || 'request failed'
        }`
      );
    }
  };

  // Order Reservation plan editor (create-launch). Updates product._reservation
  // (itemId -> qty). Carried from the pre-fill, editable here, recorded at submit.
  const handleReserveChange = (productId, itemId, qty) => {
    const q = Math.max(0, Math.floor(Number(qty) || 0));
    setStandardsData(prev =>
      prev.map(p => {
        if (p._id !== productId) return p;
        const res = { ...(p._reservation || {}) };
        if (q <= 0) delete res[itemId];
        else res[itemId] = q;
        return { ...p, _reservation: res };
      })
    );
  };

  /**
   * Save every job of this order that is open in the wizard, in one pass.
   *
   * Moving units between jobs is two edits that only make sense together —
   * saving one at a time means the intermediate state is briefly over- or
   * under-reserved, and the backend clamps against it. Releases go FIRST for
   * exactly that reason: the capacity has to be free before anybody claims it,
   * or the second save is refused for asking more than the order has left.
   *
   * Any number of jobs — the wizard may hold two or five.
   */
  const handleSaveAllReservations = async (items, orderCode) => {
    const list = Array.isArray(standardData) ? standardData : [];
    const ids = (Array.isArray(items) ? items : []).map(i => i?.itemId).filter(Boolean);
    if (!ids.length) return;

    const payloadFor = p => {
      const out = {};
      for (const id of ids) out[id] = Number(p?._reservation?.[id]) || 0;
      return out;
    };
    const totalOf = p => Object.values(payloadFor(p)).reduce((s, n) => s + n, 0);
    const committedOf = p =>
      ids.reduce((s, id) => s + Number(p?._committedReservation?.[id] || 0), 0);

    // touching this order at all, and giving up before taking
    const touching = list.filter(p => jobIdOf(p));
    const releasing = touching.filter(p => totalOf(p) < committedOf(p));
    const taking = touching.filter(p => !releasing.includes(p));

    const failures = [];
    for (const p of [...releasing, ...taking]) {
      const jobId = jobIdOf(p);
      try {
        const r = await saveJobReservation(jobId, payloadFor(p));
        applySavedReservation(jobId, r?.reservation);
        applyResync(jobId, r?.resynced);
      } catch (err) {
        failures.push(
          `${p.name || jobId}: ${err?.response?.data?.error || err.message}`
        );
      }
    }

    window.alert(
      failures.length
        ? `Some jobs were not saved — ${failures.join('; ')}`
        : `Saved for ${touching.length} job(s) on ${orderCode || 'this order'}.`
    );
  };

  /**
   * Release one order from one product.
   *
   * Before a launch there is no job to write against, so the same button just
   * drops the order from the payload being built — same outcome, different
   * place, and the operator should not have to know which wizard they are in.
   */
  /**
   * Promise more of this product to this order — the mirror of Release.
   *
   * Explicit on purpose. Raising a job's allocation is an accounting decision
   * (that cable stops being stock and becomes somebody's), so it is a button
   * with a number on it and not something a "+" does quietly.
   */
  /**
   * Fold a write back into the wizard's own copy of the order context.
   *
   * Two scopes, and they are not the same. What a job owes the order belongs to
   * that job alone; what the LINE has allocated and still needs is shared by
   * every job open on it, so a save on one has to move the numbers the others
   * are reading. Without this the second job goes on offering metres the first
   * one just took, and the operator has to leave the wizard and come back to see
   * the truth.
   *
   * @param jobId    the job whose own promise changed
   * @param orderId  the order both scopes are about
   * @param delta    metres added to (or, negative, taken off) the line
   * @param mine     what this job now owes the line
   */
  /**
   * A reservation save now moves the job's allocation with it — the backend
   * says by how much, per line, and that has to reach the cards on screen.
   */
  /**
   * Fold a saved reservation back into the wizard's own copy of the order.
   *
   * `applyResync` moves the metre totals, and that is all it moves. The
   * read-only side of this panel is drawn from `jobReservedQty` — the per-item
   * count the server sent when the page loaded — so after a successful save,
   * toggling "Edit reservation" off showed the numbers as they were BEFORE the
   * edit, against a reservation that had in fact been written.
   *
   * The response is authoritative: it is what the server stored, clamping and
   * all, so a value it reduced shows reduced rather than as typed.
   */
  /**
   * Drop a freshly built Order Context onto the product it belongs to.
   *
   * A reservation made from the product chip writes on the server, and the
   * wizard's own copy of `_orders` — the whole Order Context and Order
   * Reservation panel — knew nothing about it until the page was reloaded. The
   * server hands back the rebuilt shape, so the panel appears where it should:
   * immediately, on the card the promise was made from.
   */
  const applyOrderContext = (jobId, orders) => {
    if (!Array.isArray(orders)) return;
    setStandardsData(prev =>
      prev.map(p =>
        jobIdOf(p) === jobId
          ? {
              ...p,
              _orders: orders,
              // The steppers seed from what the job holds; without this they
              // would open on the numbers from before the promise.
              _reservation: orders.reduce((acc, o) => {
                for (const it of o?.deliveryPlanItems || []) {
                  const q = Number(it?.jobReservedQty) || 0;
                  if (q > 0) acc[it.itemId] = q;
                }
                return acc;
              }, {}),
              _committedReservation: orders.reduce((acc, o) => {
                for (const it of o?.deliveryPlanItems || []) {
                  acc[it.itemId] = Number(it?.jobReservedQty) || 0;
                }
                return acc;
              }, {})
            }
          : p
      )
    );
  };

  const applySavedReservation = (jobId, reservation) => {
    const saved =
      reservation && typeof reservation === 'object' ? reservation : {};

    setStandardsData(prev => {
      // How many units of each item this job's promise moved by. `launchedQty`
      // is order-wide — what ANY job holds — so every card's copy has to move
      // with it, or the siblings go on offering units that are now taken.
      const deltas = new Map();
      const owner = prev.find(p => jobIdOf(p) === jobId);
      for (const o of owner?._orders || []) {
        for (const it of o.deliveryPlanItems || []) {
          const before = Number(it.jobReservedQty) || 0;
          const after = Number(saved[it.itemId]) || 0;
          if (before !== after) deltas.set(String(it.itemId), after - before);
        }
      }
      if (!deltas.size) return prev;

      return prev.map(p => {
        const orders = Array.isArray(p._orders) ? p._orders : null;
        if (!orders) return p;
        const mine = jobIdOf(p) === jobId;

        return {
          ...p,
          ...(mine
            ? { _committedReservation: { ...saved }, _reservation: { ...saved } }
            : {}),
          _orders: orders.map(o => ({
            ...o,
            deliveryPlanItems: (o.deliveryPlanItems || []).map(it => {
              const d = deltas.get(String(it.itemId)) || 0;
              return {
                ...it,
                // Same clamp the server applies, so the local copy and the
                // stored one cannot drift apart by a rounding of the rules.
                launchedQty: Math.max(
                  0,
                  Math.min(
                    Number(it.qtyPlanned) || 0,
                    (Number(it.launchedQty) || 0) + d
                  )
                ),
                ...(mine
                  ? { jobReservedQty: Number(saved[it.itemId]) || 0 }
                  : {})
              };
            })
          }))
        };
      });
    });
  };

  const applyResync = (jobId, resynced) => {
    const moved = (Array.isArray(resynced) ? resynced : []).filter(
      r => (Number(r?.to) || 0) !== (Number(r?.from) || 0)
    );
    if (!moved.length) return;

    setStandardsData(prev =>
      prev.map(p => {
        const orders = Array.isArray(p._orders) ? p._orders : null;
        if (!orders) return p;
        const isMine = jobIdOf(p) === jobId;

        return {
          ...p,
          _orders: orders.map(o => {
            const hit = moved.find(r => String(r.lineId) === String(o?.lineId));
            if (!hit) return o;

            const to = Number(hit.to) || 0;
            const delta = to - (Number(hit.from) || 0);
            const next = {
              ...o,
              // line-level: every job open on this order reads it
              allocatedTotal_m: Math.max(0, (Number(o.allocatedTotal_m) || 0) + delta),
              remainingTotal_m: Math.max(0, (Number(o.remainingTotal_m) || 0) - delta)
            };
            if (!isMine) return next;
            return {
              ...next,
              allocatedFromThisJob_m: to,
              jobUnpromised_m: Math.max(0, (Number(o.jobUnpromised_m) || 0) - delta)
            };
          })
        };
      })
    );
  };

  const patchOrderContext = (jobId, orderId, delta, mine) => {
    const d = Number(delta) || 0;
    const sameOrder = o => String(o?.orderId ?? o?._id) === String(orderId);

    setStandardsData(prev =>
      prev.map(p => {
        const orders = Array.isArray(p._orders) ? p._orders : null;
        if (!orders) return p;
        const isMine = jobIdOf(p) === jobId;

        return {
          ...p,
          _orders: orders.map(o => {
            if (!sameOrder(o)) return o;
            const next = {
              ...o,
              // line-level: everyone sees it
              allocatedTotal_m: Math.max(0, (Number(o.allocatedTotal_m) || 0) + d),
              remainingTotal_m: Math.max(0, (Number(o.remainingTotal_m) || 0) - d)
            };
            if (!isMine) return next;
            return {
              ...next,
              allocatedFromThisJob_m: Number.isFinite(Number(mine))
                ? Number(mine)
                : (Number(o.allocatedFromThisJob_m) || 0) + d,
              jobUnpromised_m: Math.max(0, (Number(o.jobUnpromised_m) || 0) - d)
            };
          })
        };
      })
    );
  };

  const handleAllocateMore = async (product, ord, meters) => {
    const orderId = ord?.orderId || ord?._id;
    const jobId = jobIdOf(product);
    if (!orderId || !jobId || !meters) return;

    const label = ord?.orderCode || orderId;
    if (
      !window.confirm(
        `Promise ${Number(meters).toLocaleString()} m more of ${product?.name || 'this product'} to ${label}? ` +
          `Those metres stop being stock, and this job will then be able to reserve up to that much on the order.`
      )
    ) {
      return;
    }

    try {
      const res = await allocateJobToOrder(jobId, orderId, meters, ord?.lineId);
      patchOrderContext(
        jobId,
        orderId,
        Number(res?.meters) || 0,
        Number(res?.allocatedNow)
      );
      window.alert(
        `${Number(res?.meters || 0).toLocaleString()} m allocated. ` +
          `This job may now reserve up to ${Number(res?.allocatedNow || 0).toLocaleString()} m on ${label}.` +
          (res?.jobFreeLeft
            ? ` ${Number(res.jobFreeLeft).toLocaleString()} m still unpromised.`
            : '')
      );
    } catch (err) {
      window.alert(
        `Could not allocate: ${
          err?.response?.data?.error || err.message || 'request failed'
        }`
      );
    }
  };

  const handleDetachOrder = async (product, ord) => {
    const orderId = ord?.orderId || ord?._id;
    if (!orderId) return;

    const label = ord?.orderCode || orderId;
    if (
      !window.confirm(
        `Release ${label} from ${product?.name || 'this product'}?

` +
          `Its metres go back to stock and any reservation on it is cleared. ` +
          `The order line becomes free to promise again.`
      )
    ) {
      return;
    }

    const jobId = jobIdOf(product);

    if (!jobId || mode === 'create') {
      // Nothing on the server yet — take it out of what is about to be launched.
      setStandardsData(prev =>
        prev.map(p => {
          if (p._id !== product._id) return p;
          const allocations = (p.allocations || []).filter(
            a => String(a?.orderId) !== String(orderId)
          );
          const dropped = new Set(
            (ord?.deliveryPlanItems || []).map(i => String(i.itemId))
          );
          const _reservation = Object.fromEntries(
            Object.entries(p._reservation || {}).filter(
              ([id]) => !dropped.has(String(id))
            )
          );
          return { ...p, allocations, _reservation };
        })
      );
      return;
    }

    try {
      const res = await detachJobFromOrder(jobId, orderId);

      // Take it out of the wizard too. The server no longer has the link, and a
      // card still sitting there invites the operator to press the button again
      // — which would then answer "this job holds nothing on that order".
      setStandardsData(prev =>
        prev.map(p => {
          if (jobIdOf(p) !== jobId) return p;
          const dropped = new Set(
            (ord?.deliveryPlanItems || []).map(i => String(i.itemId))
          );
          return {
            ...p,
            allocations: (p.allocations || []).filter(
              a => String(a?.orderId) !== String(orderId)
            ),
            _orders: Array.isArray(p._orders)
              ? p._orders.filter(o => String(o?.orderId ?? o?._id) !== String(orderId))
              : p._orders,
            _reservation: Object.fromEntries(
              Object.entries(p._reservation || {}).filter(
                ([id]) => !dropped.has(String(id))
              )
            )
          };
        })
      );

      window.alert(
        `${label} released — ${Number(res?.freedMeters || 0).toLocaleString()} m back to stock` +
          (res?.releasedUnits ? `, ${res.releasedUnits} reserved unit(s) freed` : '') +
          '.'
      );
    } catch (err) {
      window.alert(
        `Could not release the order: ${
          err?.response?.data?.error || err.message || 'request failed'
        }`
      );
    }
  };

  const [maxSteps, setMaxSteps] = useState(
    Math.max(...standardData.map(p => p.phases.length))
  );

  const form = useWizardForm({ totalStep: maxSteps }, {});

  // Reconcile mode (guarded by the `reconcile` prop; wizard still runs as mode="edit"):
  // produced drums carry a "belongs to product" control AND can be dragged between
  // products — moving a drum PHYSICALLY relocates its card to the target product's
  // phase (WYSIWYG). Unused in create/edit. reconcileSel: tokenId -> { relaunch }.
  const [reconcileSel, setReconcileSel] = useState({});
  // The drum under the cursor, so the products that can actually receive it can
  // say so while it is in the air. `dataTransfer` cannot be read during
  // dragover, only on drop — by which time the highlight is too late to help.
  const [draggingToken, setDraggingToken] = useState(null);
  // Corrected produced lengths, keyed `jobId::tokenId` — NOT by token alone.
  // A merged drum is one bobbin with a SHARE per product, each recorded on that
  // product's own copy of the token, so "this drum's length" is not one number.
  // Keying by token (as `reconcileSel` does for relaunch) would make the two
  // shares of a merge overwrite each other.
  const [reconcileLen, setReconcileLen] = useState({});
  const setReconcileLength = useCallback((jobId, tokenId, value) => {
    setReconcileLen(m => {
      const key = `${jobId}::${tokenId}`;
      const next = { ...m };
      if (value === '' || value == null) delete next[key];
      else next[key] = value;
      return next;
    });
  }, []);
  const [dragOverJob, setDragOverJob] = useState(null);
  // "N2XH 5 x 6", not "N2XH".
  //
  // A family name alone is not a product: the same N2XH covers a dozen cables
  // that are nothing alike to make, and picking between two of them by name is
  // guessing. `cableLabel` puts the cores and the section back on — and the
  // "+ 16" green-yellow with them, because a 3 x 25 + 16 and a 3 x 25 are
  // different cables. Every field it needs is already on the wizard product; it
  // just calls the family `name` rather than `cableName`.
  const productLabelOf = useCallback(
    p =>
      cableLabel(
        { ...p, cableName: p?.name },
        p?.productLabel || p?._productLabel || p?.name || String(p?._id || '')
      ),
    []
  );

  const reconcileTargets = useMemo(
    () =>
      (standardData || [])
        .map(p => ({
          jobId: String(p._id || p._parentJobId || ''),
          label: productLabelOf(p)
        }))
        .filter(t => t.jobId),
    [standardData, productLabelOf]
  );

  // Physically move a produced drum (by tokenId) into another product's matching
  // phase. Removes the exec from its current product/phase/conductor and re-inserts
  // it into the target's same-named phase. For conductor_isolation an optional
  // `targetColor` re-slots + RE-COLOURS the drum (BLUE→BROWN inversion, length kept).
  // For non-conductor phases (unwinding etc.) an optional `targetWireDiameter` re-slots
  // the drum into another wire-Ø group (Ø1.7→Ø1.35 — the diameter analogue of colour).
  // The drum keeps `_reconcileOrigin`/`_reconcileOriginColor`/`_reconcileOriginWireDiameter`,
  // so the submit diff = origin → current (job + colour / wire-Ø).
  const moveReconcileDrum = useCallback(
    (
      tokenId,
      targetJobId,
      targetColor = null,
      targetWireDiameter = null,
      // Which product the drum was taken FROM. A merged token exists in two of
      // them, so searching by token alone finds whichever product happens to
      // come first — and detaches the wrong card.
      sourceJobId = null
    ) => {
      if (!tokenId || !targetJobId) return;

      // ── where a shared bobbin is dropped decides what dropping means ─────
      //
      // ONTO A PRODUCT THAT ALREADY SHARES IT — the merge partner. It cannot be
      // "moved" there; it is already there, with a share of its own. So this
      // means "all of this bobbin was really theirs": this share goes to zero
      // and theirs takes the lot. Nothing relocates, two numbers change, and the
      // tokenId — which is what holds the merge together — is untouched.
      //
      // ONTO ANY OTHER PRODUCT — an ordinary move, exactly as for an unmerged
      // drum: the share leaves this product and lands on that one. The partner
      // keeps its own share, so the bobbin is then shared between a different
      // pair. This is what `reallocate-produced` does, and it refuses only the
      // first case, where it would overwrite the partner.
      const holders = [];
      for (const p of standardData || []) {
        const pid = String(p._id || p._parentJobId || '');
        for (const ph of p.phases || []) {
          const execs = [
            ...(Array.isArray(ph.execution) ? ph.execution : []),
            ...((ph.conductors || []).flatMap(c =>
              Array.isArray(c.execution) ? c.execution : []
            ))
          ];
          for (const e of execs) {
            if (String(e?.tokenId) === String(tokenId)) {
              holders.push({ jobId: pid, produced: Number(e._producedLength || 0) });
            }
          }
        }
      }

      const partner = holders.length > 1
        ? holders.find(h => h.jobId === String(targetJobId))
        : null;

      if (partner) {
        const total = holders.reduce((s, h) => s + h.produced, 0);
        for (const h of holders) {
          setReconcileLength(
            h.jobId,
            tokenId,
            String(h.jobId === partner.jobId ? total : 0)
          );
        }
        // Dropped on a specific CONDUCTOR of the partner, not just on the
        // product: that says two things at once — take the whole drum, and it
        // is this colour. Both are meant.
        if (targetColor != null) {
          setStandardsData(prev => {
            const products = Array.isArray(prev) ? [...prev] : [];
            return recolorTokenIn(products, tokenId, targetColor);
          });
        }
        return;
      }
      // else: fall through and move the card, merged or not

      setStandardsData(prev => {
        const clone = v => {
          try {
            return structuredClone(v);
          } catch {
            return JSON.parse(JSON.stringify(v));
          }
        };
        const products = clone(Array.isArray(prev) ? prev : []);

        // locate + detach the drum from wherever it currently lives
        let moving = null;
        let phaseName = null;
        let conductorColor = null;
        for (const p of products) {
          // A merged token is in two products. Take it from the one it was
          // dragged out of, not from whichever comes first in the list.
          if (sourceJobId && String(p._id || p._parentJobId || '') !== String(sourceJobId)) {
            continue;
          }
          for (const ph of p.phases || []) {
            if (Array.isArray(ph.conductors)) {
              for (const c of ph.conductors) {
                const i = (c.execution || []).findIndex(
                  e => e && String(e.tokenId) === String(tokenId)
                );
                if (i >= 0) {
                  moving = c.execution[i];
                  phaseName = ph.name;
                  conductorColor = c.isolation?.color ?? null;
                  c.execution.splice(i, 1);
                  break;
                }
              }
            }
            if (moving) break;
            const j = (ph.execution || []).findIndex(
              e => e && String(e.tokenId) === String(tokenId)
            );
            if (j >= 0) {
              moving = ph.execution[j];
              phaseName = ph.name;
              ph.execution.splice(j, 1);
              break;
            }
          }
          if (moving) break;
        }
        // not found → no-op. A merged drum never reaches here: it is re-sliced
        // above, because both products already hold its token.
        if (!moving) return prev;

        const tgt = products.find(
          p => String(p._id || p._parentJobId || '') === String(targetJobId)
        );
        const tph = tgt?.phases?.find(ph => ph.name === phaseName);
        if (!tgt || !tph) return prev; // target lacks this phase → abort the move

        // conductor phase: place into the target colour's conductor. A `targetColor`
        // different from the drum's own colour = inversion → re-colour the drum.
        const placeColor =
          conductorColor != null && targetColor != null
            ? String(targetColor)
            : conductorColor;

        if (placeColor != null) {
          tph.conductors = Array.isArray(tph.conductors) ? tph.conductors : [];
          let c = tph.conductors.find(
            cc => String(cc.isolation?.color) === String(placeColor)
          );
          if (!c) {
            c = { isolation: { color: placeColor }, execution: [] };
            tph.conductors.push(c);
          }
          if (conductorColor != null && targetColor != null) {
            moving.color = String(targetColor); // inversion — length kept
            // …and on every other copy of the same bobbin. A merged drum still
            // has a card under its partner; leaving that one the old colour
            // would have the two products disagreeing about one physical drum.
            recolorTokenIn(products, tokenId, targetColor);
          }
          c.execution = [...(c.execution || []), moving];
        } else {
          // non-conductor phase: optionally re-slot into another wire-Ø group
          // (the diameter is a plain exec field; the render re-groups by it).
          if (targetWireDiameter != null) {
            const d = Number(targetWireDiameter);
            if (Number.isFinite(d)) moving.wireDiameter = d;
          }
          tph.execution = [...(tph.execution || []), moving];
        }
        return products;
      });
    },
    [setStandardsData, standardData, setReconcileLength]
  );

  // Auto-scroll while dragging a reconcile card: native HTML5 DnD does not scroll,
  // so a drum far down the page is unreachable. When a drag is near the top/bottom
  // viewport edge, scroll the nearest scrollable ancestor under the cursor (or the
  // window). Active only in reconcile mode.
  useEffect(() => {
    if (!reconcile) return undefined;
    const EDGE = 90; // px band that triggers scrolling
    const MAX = 22; // px per event at the very edge
    const onDragOver = e => {
      const y = e.clientY;
      const h = window.innerHeight || document.documentElement.clientHeight;
      let dy = 0;
      if (y < EDGE) dy = -MAX * (1 - y / EDGE);
      else if (y > h - EDGE) dy = MAX * (1 - (h - y) / EDGE);
      if (!dy) return;
      let el = document.elementFromPoint(e.clientX, y);
      while (el && el !== document.body && el !== document.documentElement) {
        const st = window.getComputedStyle(el);
        if (
          /(auto|scroll)/.test(st.overflowY) &&
          el.scrollHeight > el.clientHeight
        ) {
          el.scrollTop += dy;
          return;
        }
        el = el.parentElement;
      }
      window.scrollBy(0, dy);
    };
    window.addEventListener('dragover', onDragOver);
    return () => window.removeEventListener('dragover', onDragOver);
  }, [reconcile]);

  // One physical bobbin, every product on it.
  //
  // A merge execution puts the same tokenId in each product with that product's
  // SHARE of the drum. No single product knows what the bobbin holds in total —
  // and that total is the number an operator needed and never had: on 2026-09-15
  // an L75 bobbin planned for 10 300 m of one product and 2 060 m of another was
  // taken off at 10 305, because 10 300 was the only figure on any screen.
  // What a drum is credited with RIGHT NOW: the staged value if one has been
  // typed, otherwise what was recorded.
  //
  // Every list of drums has to read through this. They used to read
  // `_producedLength` directly, which is the figure on disk — so after sending
  // metres from one drum to another, the destination still advertised its old
  // length. A second send computed from that stale base and overwrote the
  // first, silently.
  const effectiveLength = useCallback(
    (jobId, tokenId, recorded) => {
      const draft = reconcileLen[`${jobId}::${tokenId}`];
      if (draft == null || draft === '') return Number(recorded || 0);
      const n = Number(String(draft).replace(',', '.'));
      return Number.isFinite(n) ? n : Number(recorded || 0);
    },
    [reconcileLen]
  );

  const reconcileDrumShares = useMemo(() => {
    const map = new Map();
    if (!reconcile) return map;
    for (const p of standardData || []) {
      const jobId = String(p._id || p._parentJobId || '');
      const label = p.productLabel || p.name || jobId;
      for (const ph of p.phases || []) {
        const execs = [
          ...(Array.isArray(ph.execution) ? ph.execution : []),
          ...((ph.conductors || []).flatMap(c =>
            Array.isArray(c.execution) ? c.execution : []
          ))
        ];
        for (const e of execs) {
          if (!e?.tokenId) continue;
          const key = String(e.tokenId);
          if (!map.has(key)) map.set(key, []);
          map.get(key).push({
            jobId,
            label,
            producedLength: effectiveLength(jobId, key, e._producedLength),
            plannedLength: Number(e.plannedLength || 0)
          });
        }
      }
    }
    return map;
  }, [reconcile, standardData, effectiveLength]);

  // Everything staged but not yet written, in one list.
  //
  // The wizard's state IS the edit — a drum's card sitting under a different
  // product means it will be moved, its colour means it will be relabelled —
  // and that is invisible unless you remember what the card looked like before.
  // Several actions in a row, on cards that look alike, and there is no way to
  // answer "what have I actually changed". Each one undoes on its own.
  const reconcilePending = useMemo(() => {
    if (!reconcile) return [];
    const out = [];
    const nameOf = jobId =>
      reconcileTargets.find(t => t.jobId === String(jobId))?.label || String(jobId);

    for (const p of standardData || []) {
      const jobId = String(p._id || p._parentJobId || '');
      for (const ph of p.phases || []) {
        const execs = [
          ...(Array.isArray(ph.execution) ? ph.execution : []),
          ...((ph.conductors || []).flatMap(c =>
            Array.isArray(c.execution) ? c.execution : []
          ))
        ];
        for (const e of execs) {
          if (!e?.tokenId || !e._locked) continue;
          const origin = e._reconcileOrigin ? String(e._reconcileOrigin) : jobId;

          if (origin !== jobId) {
            out.push({
              key: `move-${e.tokenId}-${jobId}`,
              kind: 'move',
              tokenId: String(e.tokenId),
              text: `${e.color || 'drum'} ${String(e.tokenId)} → ${nameOf(jobId)} (was ${nameOf(origin)})`,
              undo: () =>
                moveReconcileDrum(
                  e.tokenId,
                  origin,
                  e._reconcileOriginColor ?? null,
                  e._reconcileOriginWireDiameter ?? null,
                  jobId
                )
            });
          }

          const wasColor = e._reconcileOriginColor;
          if (wasColor != null && e.color != null && String(e.color) !== String(wasColor)) {
            out.push({
              key: `color-${e.tokenId}`,
              kind: 'colour',
              tokenId: String(e.tokenId),
              text: `${String(e.tokenId)} recoloured ${wasColor} → ${e.color}`,
              // Inlined rather than calling `recolorEveryCopy`, which is
              // declared further down the component: naming it in this memo's
              // dependency list read it before its `const` was initialised and
              // the whole wizard threw on render.
              undo: () =>
                setStandardsData(prev =>
                  recolorTokenIn(
                    JSON.parse(JSON.stringify(Array.isArray(prev) ? prev : [])),
                    e.tokenId,
                    wasColor
                  )
                )
            });
          }
        }
      }
    }

    for (const [key, value] of Object.entries(reconcileLen)) {
      const [jobId, tokenId] = String(key).split('::');
      out.push({
        key: `len-${key}`,
        kind: 'length',
        tokenId,
        text: `${tokenId} on ${nameOf(jobId)} → ${value} m`,
        undo: () => setReconcileLength(jobId, tokenId, '')
      });
    }

    // One entry per fact, even when two cards of a shared bobbin say the same.
    const seen = new Set();
    return out.filter(o => (seen.has(o.key) ? false : seen.add(o.key)));
  }, [
    reconcile,
    standardData,
    reconcileLen,
    reconcileTargets,
    moveReconcileDrum,
    setStandardsData,
    setReconcileLength
  ]);

  // Every produced SHARE, one entry per product-and-drum.
  //
  // Not the same list as `reconcileDrums`. That one is deduplicated by token,
  // because a colour belongs to the BOBBIN and listing a shared one twice would
  // offer to swap it with itself. A destination for metres is the opposite: it
  // is a share, `jobId::tokenId`, and on a merged bobbin BOTH products hold one.
  // Reusing the deduplicated list meant only the first product ever appeared —
  // with a 5x6 and a 3x6 open, every destination said 5x6.
  //
  // `phase` travels with each entry so a drum can only be sent to another drum
  // of the same phase: metres of conductor do not become metres of rope.
  const reconcileShares = useMemo(() => {
    if (!reconcile) return [];
    const out = [];
    for (const p of standardData || []) {
      const jobId = String(p._id || p._parentJobId || '');
      const label = productLabelOf(p);
      for (const ph of p.phases || []) {
        const execs = [
          ...(Array.isArray(ph.execution) ? ph.execution : []),
          ...((ph.conductors || []).flatMap(c =>
            (c.execution || []).map(e => ({
              e,
              fallbackColor: c.isolation?.color ?? null
            }))
          ))
        ].map(x => (x && x.e ? x : { e: x, fallbackColor: null }));

        for (const { e, fallbackColor } of execs) {
          if (!e?._locked || !e.tokenId) continue;
          out.push({
            key: `${jobId}::${e.tokenId}`,
            tokenId: String(e.tokenId),
            jobId,
            phase: ph.name,
            color: e.color ?? fallbackColor,
            length: effectiveLength(
              jobId,
              e.tokenId,
              e._producedLength || e.plannedLength || 0
            ),
            merged: !!e._merged,
            label
          });
        }
      }
    }
    return out;
  }, [reconcile, standardData, productLabelOf, effectiveLength]);

  // All produced isolation drums, for the explicit "swap colours" picker.
  const reconcileDrums = useMemo(() => {
    if (!reconcile) return [];
    const out = [];
    for (const p of standardData || []) {
      const jobId = String(p._id || p._parentJobId || '');
      const label = productLabelOf(p);
      for (const ph of p.phases || []) {
        if (ph.name !== 'conductor_isolation') continue; // colour swap = isolation
        for (const c of ph.conductors || []) {
          for (const e of c.execution || []) {
            // Merged drums included. A colour is a property of the BOBBIN, not
            // of one product's share of it, so exchanging two colours is
            // meaningful whether or not the drums are shared — it just has to
            // reach every product holding them, which POST /jobs/recolor-drums
            // does. Leaving them out meant the one thing the operator could not
            // fix was the one they had actually got wrong.
            if (e?._locked && e.tokenId) {
              if (out.some(x => x.tokenId === String(e.tokenId))) continue;
              out.push({
                tokenId: String(e.tokenId),
                jobId,
                color: e.color ?? c.isolation?.color ?? null,
                length: effectiveLength(
                  jobId,
                  e.tokenId,
                  e._producedLength || e.plannedLength || 0
                ),
                merged: !!e._merged,
                label
              });
            }
          }
        }
      }
    }
    return out;
  }, [reconcile, standardData, productLabelOf, effectiveLength]);

  // Relabel one drum wherever it appears — in every product that shares the
  // bobbin, and on the conductor slot it sits in. Purely the wizard's own view;
  // the write goes through POST /jobs/recolor-drums on submit.
  const recolorEveryCopy = useCallback(
    (tokenId, color) => {
      if (!tokenId || color == null) return;
      setStandardsData(prev => {
        const clone = v => {
          try {
            return structuredClone(v);
          } catch {
            return JSON.parse(JSON.stringify(v));
          }
        };
        return recolorTokenIn(clone(Array.isArray(prev) ? prev : []), tokenId, color);
      });
    },
    [setStandardsData]
  );

  // Explicit colour inversion between TWO produced drums: each keeps its length
  // and product, they exchange colours (BLUE↔GREEN-YELLOW, across products too).
  const swapReconcileDrums = useCallback(
    (tokA, tokB) => {
      if (!tokA || !tokB || String(tokA) === String(tokB)) return;
      const find = tok => {
        for (const p of standardData || [])
          for (const ph of p.phases || [])
            for (const c of ph.conductors || []) {
              const e = (c.execution || []).find(
                x => String(x.tokenId) === String(tok)
              );
              if (e)
                return { color: e.color ?? c.isolation?.color ?? null,
                  jobId: String(p._id || p._parentJobId || '') };
            }
        return null;
      };
      const A = find(tokA);
      const B = find(tokB);
      if (!A || !B) return;

      // Both target colours are read BEFORE either is applied. Done one after
      // the other, the first relabel would become the second one's input and
      // both drums would come out the same colour.
      //
      // Every copy of a shared bobbin is relabelled, not just the one whose card
      // was used: one bobbin is one colour, and half a relabel leaves the two
      // products contradicting each other about the same physical drum.
      recolorEveryCopy(tokA, B.color);
      recolorEveryCopy(tokB, A.color);
    },
    [standardData, recolorEveryCopy]
  );

  const [showCustomPhaseModal, setShowCustomPhaseModal] = useState(false);
  const [newPhaseName, setNewPhaseName] = useState('');
  const [selectedProductId, setSelectedProductId] = useState('all');
  const [submitError, setSubmitError] = useState('');
  // What the launch created, once it has. Until 2026-10-19 a successful submit
  // did NOTHING visible: no message, no navigation, the wizard just sat there
  // looking exactly as it had a moment before. The only way to find out whether
  // the products had been launched was to go and look at the monitor — and
  // nothing stopped a second press of Submit launching the whole thing again.
  //
  // Holding the result also replaces the footer, which is what closes that door.
  const [launched, setLaunched] = useState(null);
  const [machineError, setMachineError] = useState('');
  const [mergedPhases, setMergedPhases] = useState({});
  // Group-by ORDERING per phase (separate from merge): 'color' (conductor_isolation)
  // or 'wireDiameter' (unwinding) → same-color / same-Ø drums queue together.
  // Seeded from initialGroupByPhases so editing a group-by launch keeps it on.
  const [groupByPhases, setGroupByPhases] = useState(
    () => initialGroupByPhases || {}
  );
  const [drumQuickPresets, setDrumQuickPresets] = useState({});
  // Edit mode choice for the downstream planned phases: EDIT FURTHER (re-plan them
  // with the new tokens as input; queue updates to the edited reality) vs DELETE
  // (remove them from the queue entirely). Both delete the stale plan on the
  // backend; the flag only decides whether we then guide a re-plan. Default = edit.
  const [editFurther, setEditFurther] = useState(true);

  // Edit mode: turn the Merge Execution toggle ON for any phase whose drums are
  // merged (shared across products), so a group edit reflects the launched state.
  // A user toggle-off (to un-merge) is preserved over this default.
  useEffect(() => {
    if (mode !== 'edit') return;
    const mergedByPhase = {};
    for (const product of standardData || []) {
      for (const p of product?.phases || []) {
        const execs = [];
        if (Array.isArray(p.conductors))
          for (const c of p.conductors) for (const ex of c.execution || []) execs.push(ex);
        for (const ex of p.execution || []) execs.push(ex);
        if (execs.some(ex => ex?._merged)) mergedByPhase[p.name] = true;
      }
    }
    if (Object.keys(mergedByPhase).length)
      setMergedPhases(prev => ({ ...mergedByPhase, ...prev }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, standardData]);

  useEffect(() => {
    console.log(drumQuickPresets);
  }, [drumQuickPresets]);
  const [selectedUpstream, setSelectedUpstream] = useState({});

  // HELPERS

  const getDrumPreset = (productId, phaseName, itemKey = 'default') => {
    return (
      drumQuickPresets?.[productId]?.[phaseName]?.[itemKey] || {
        material: '',
        diameter: ''
      }
    );
  };

  const setDrumPresetMaterial = (productId, phaseName, itemKey, material) => {
    setDrumQuickPresets(prev => ({
      ...prev,
      [productId]: {
        ...(prev[productId] || {}),
        [phaseName]: {
          ...(prev[productId]?.[phaseName] || {}),
          [itemKey]: {
            ...(prev[productId]?.[phaseName]?.[itemKey] || {}),
            material,
            diameter:
              prev[productId]?.[phaseName]?.[itemKey]?.material === material
                ? prev[productId]?.[phaseName]?.[itemKey]?.diameter || ''
                : ''
          }
        }
      }
    }));
  };

  const setDrumPresetDiameter = (productId, phaseName, itemKey, diameter) => {
    setDrumQuickPresets(prev => ({
      ...prev,
      [productId]: {
        ...(prev[productId] || {}),
        [phaseName]: {
          ...(prev[productId]?.[phaseName] || {}),
          [itemKey]: {
            ...(prev[productId]?.[phaseName]?.[itemKey] || {}),
            diameter
          }
        }
      }
    }));
  };

  const updateSelectedUpstream = (productId, phaseName, execOrNull) => {
    setSelectedUpstream(prev => {
      const safePrev = prev && typeof prev === 'object' ? prev : {};

      return {
        ...safePrev,
        [productId]: {
          ...(safePrev[productId] || {}),
          [phaseName]: execOrNull
        }
      };
    });
  };

  const handleProductPhaseChange = (productId, phaseName, key, value) => {
    setStandardsData(prev =>
      prev.map(product => {
        if (product._id !== productId) return product;

        const updatedPhases = product.phases.map(phase => {
          if (phase.name !== phaseName) return phase;

          // dacă actualizăm execution → îl înlocuim complet
          if (key === 'execution') {
            return { ...phase, execution: value };
          }

          // altfel update simplu
          return { ...phase, [key]: value };
        });

        return { ...product, phases: updatedPhases };
      })
    );
  };

  const handleAddCoilingPhase = productId => {
    setStandardsData(prev =>
      prev.map(product => {
        // modificăm DOAR produsul selectat
        if (product._id !== productId) return product;

        if (product.phases.some(p => p.name === 'coiling')) {
          alert(`Produsul ${product.name} are deja faza 'coiling'.`);
          return product;
        }

        // find last production phase (not coiling/rewinding)
        const productionPhases = product.phases.filter(
          p => p.name !== 'coiling' && p.name !== 'rewinding'
        );
        const lastPhase = productionPhases[productionPhases.length - 1];
        const lastPhaseIdx = product.phases.findIndex(p => p.name === lastPhase?.name);

        if (lastPhaseIdx === -1) {
          alert(`Produsul ${product.name} nu are faze de productie.`);
          return product;
        }

        // Added empty, like every other phase — the planner adds executions with
        // "+ Add Coil". Seeding one here meant a phase you added to explore
        // silently carried a blank drum into the launch. (The order-driven
        // routing below is different: those executions mirror a real coil plan
        // from the exterior phase and stay auto-created.)
        const newPhase = {
          name: 'coiling',
          dependsOn: lastPhase.name,
          execution: []
        };

        const updatedPhases = [...product.phases];
        updatedPhases.splice(lastPhaseIdx + 1, 0, newPhase);

        return { ...product, phases: updatedPhases };
      })
    );
  };

  // Mirror the order-requirement COIL routing done at exterior_sheat into the
  // coiling phase. For each tagged exterior `to_coiling` exec (autoCoilFromOrder,
  // with a coil plan) we keep ONE linked coiling execution that CONSUMES that
  // exact exterior drum (fromUpstreamId = exterior exec id) and produces its
  // coils. Effect: the drum shows "Finished" at the coiling step (its input is
  // reserved) and the coiling run is pre-created (length + coils) — the user only
  // assigns the coiling machine. Call this AFTER adding (OrderDeliveryPanel) and
  // AFTER removing (handleRemoveExecution) an exterior exec, so deleting the
  // routing also removes its linked coiling run. Manual to_coiling execs have no
  // flag → untouched. User edits (e.g. machine) on the auto exec are preserved.
  const reconcileAutoCoiling = productId => {
    setStandardsData(prev => {
      if (!Array.isArray(prev)) return prev;
      return prev.map(product => {
        if (product._id !== productId) return product;
        const phases = product.phases || [];
        const exterior = phases.find(p => p.name === 'exterior_sheat');
        if (!exterior) return product;

        const sources = (exterior.execution || []).filter(
          e =>
            e.autoCoilFromOrder &&
            e.nextPhase === 'coiling' &&
            Number(e.coilCount) > 0 &&
            Number(e.coilLength) > 0
        );

        const coiling = phases.find(p => p.name === 'coiling');
        const existingAuto = (coiling?.execution || []).filter(
          e => e.autoFromExterior
        );
        const bySource = new Map(
          existingAuto.map(e => [String(e.sourceExecId), e])
        );

        // desired auto coiling execs (preserve id/machine of an existing one)
        const desiredAuto = sources.map(src => {
          const prevA = bySource.get(String(src.id));
          const plannedLength = Number(src.coilCount) * Number(src.coilLength);
          return {
            ...(prevA || {}),
            id: prevA?.id || `autocoil-${src.id}`,
            machineId: prevA?.machineId || '',
            fromUpstreamId: src.id,
            fromUpstreamLength: src.plannedLength || plannedLength,
            coilLength: src.coilLength,
            coilCount: src.coilCount,
            plannedLength,
            deliveryMode: 'final_product',
            nextPhase: null,
            autoFromExterior: true,
            sourceExecId: src.id
            // NB: intentionally NO orderItemId — the exterior exec already
            // carries it for the order-requirement launched count; tagging the
            // coiling exec too would double-count the coils.
          };
        });

        // coiling phase doesn't exist yet → create it (after the last
        // production phase) only if there's something to mirror.
        if (!coiling) {
          if (!desiredAuto.length) return product;
          const prodPhases = phases.filter(
            p => p.name !== 'coiling' && p.name !== 'rewinding'
          );
          const lastProd = prodPhases[prodPhases.length - 1];
          const idx = phases.findIndex(p => p.name === lastProd?.name);
          if (idx === -1) return product;
          const newPhase = {
            name: 'coiling',
            dependsOn: lastProd.name,
            execution: desiredAuto
          };
          const updated = [...phases];
          updated.splice(idx + 1, 0, newPhase);
          return { ...product, phases: updated };
        }

        // reconcile within the existing coiling phase, preserving manual execs
        const manual = (coiling.execution || []).filter(
          e => !e.autoFromExterior
        );
        const updatedPhases = phases.map(p =>
          p.name === 'coiling'
            ? { ...p, execution: [...manual, ...desiredAuto] }
            : p
        );
        return { ...product, phases: updatedPhases };
      });
    });
  };

  const handleAddRewindingPhase = productId => {
    setStandardsData(prev =>
      prev.map(product => {
        // modificăm DOAR produsul cu productId
        if (product._id !== productId) return product;

        if (product.phases.some(p => p.name === 'rewinding')) {
          alert(`Produsul ${product.name} are deja faza 'rewinding'.`);
          return product;
        }

        const productionPhases = product.phases.filter(
          p => p.name !== 'coiling' && p.name !== 'rewinding'
        );
        const lastPhase = productionPhases[productionPhases.length - 1];
        const lastPhaseIdx = product.phases.findIndex(p => p.name === lastPhase?.name);

        if (lastPhaseIdx === -1) {
          alert(`Produsul ${product.name} nu are faze de productie.`);
          return product;
        }

        // Empty, like every other phase — the planner adds executions manually.
        const newPhase = {
          name: 'rewinding',
          dependsOn: lastPhase.name,
          execution: []
        };

        const updatedPhases = [...product.phases];
        updatedPhases.splice(lastPhaseIdx + 1, 0, newPhase);

        return { ...product, phases: updatedPhases };
      })
    );
  };

  const handleAddCustomPhase = () => {
    if (!newPhaseName) {
      alert('Select a phase type before continuing.');
      return;
    }

    const shouldModify = product =>
      selectedProductId === 'all' || product._id === selectedProductId;

    // --- UNWINDING (existing: for pre_stranding) ---
    if (newPhaseName.toLowerCase() === 'unwinding') {
      setStandardsData(prev =>
        prev.map(product => {
          if (!shouldModify(product)) return product;

          const preStrandingIdx = product.phases.findIndex(
            p => p.name === 'pre_stranding'
          );

          if (preStrandingIdx === -1) {
            alert(
              `Product ${product.name} has no 'pre_stranding' phase. Can't add 'unwinding'.`
            );
            return product;
          }

          if (product.phases.some(p => p.name === 'unwinding')) {
            alert(`Produsul ${product.name} are deja faza 'unwinding'.`);
            return product;
          }

          const preStranding = product.phases[preStrandingIdx];

          // ===============================
          // 🔥 BUILD ATOMIC UNWINDING EXECs
          // ===============================
          const executions = [];

          if (Array.isArray(preStranding.rope)) {
            // 🔥 NEW – mixed conductor sections
            preStranding.rope.forEach((rope, ropeIndex) => {
              rope.numberOfWires.forEach((count, i) => {
                const qty = Number(count) || 0;
                const diameter = Number(rope.wireDiameter[i]);

                for (let j = 0; j < qty; j++) {
                  executions.push({
                    id: Date.now() + ropeIndex * 1000 + i * 100 + j,
                    ropeIndex,
                    wireDiameter: diameter,
                    machineId: '',
                    plannedLength: '',
                    drum_material: '',
                    drum_diameter: ''
                  });
                }
              });
            });
          } else {
            // 🔁 OLD behaviour (unchanged)
            preStranding.numberOfWires.forEach((count, i) => {
              const qty = Number(count) || 0;
              const diameter = Number(preStranding.wireDiameter[i]);

              for (let j = 0; j < qty; j++) {
                executions.push({
                  id: Date.now() + i * 100 + j,
                  ropeIndex: null,
                  wireDiameter: diameter,
                  machineId: '',
                  plannedLength: '',
                  drum_material: '',
                  drum_diameter: ''
                });
              }
            });
          }

          const newPhase = {
            name: 'unwinding',
            numberOfWires: preStranding.numberOfWires,
            wireDiameter: preStranding.wireDiameter,
            density: preStranding.density,
            material: preStranding.material,
            dependsOn: null,
            execution: executions
          };

          // ===============================
          // 🔁 INSERT BEFORE PRE-STRANDING
          // ===============================
          const updatedPhases = [...product.phases];

          updatedPhases[preStrandingIdx] = {
            ...preStranding,
            dependsOn: 'unwinding'
          };

          updatedPhases.splice(preStrandingIdx, 0, newPhase);

          return {
            ...product,
            phases: updatedPhases
          };
        })
      );

      setShowCustomPhaseModal(false);
      setNewPhaseName('');
      setSelectedProductId('all');
      return;
    }

    // --- COILING (final product) ---
    if (newPhaseName.toLowerCase() === 'coiling') {
      setStandardsData(prev =>
        prev.map(product => {
          if (!shouldModify(product)) return product;

          if (product.phases.some(p => p.name === 'coiling')) {
            alert(`Produsul ${product.name} are deja faza 'coiling'.`);
            return product;
          }

          const productionPhases = product.phases.filter(
            p => p.name !== 'coiling' && p.name !== 'rewinding'
          );
          const lastPhase = productionPhases[productionPhases.length - 1];
          const lastPhaseIdx = product.phases.findIndex(p => p.name === lastPhase?.name);

          if (lastPhaseIdx === -1) {
            alert(`Produsul ${product.name} nu are faze de productie.`);
            return product;
          }

          // coiling/rewinding are always the LAST step — they depend on the last
          // production phase (e.g. exterior_sheat), in create-new and in
          // plan-next-phase alike.
          const newPhase = {
            name: 'coiling',
            dependsOn: lastPhase.name,
            execution: []
          };

          const updatedPhases = [...product.phases];
          updatedPhases.splice(lastPhaseIdx + 1, 0, newPhase);

          return { ...product, phases: updatedPhases };
        })
      );

      setShowCustomPhaseModal(false);
      setNewPhaseName('');
      setSelectedProductId('all');
      return;
    }

    if (newPhaseName.toLowerCase() === 'rewinding') {
      setStandardsData(prev =>
        prev.map(product => {
          if (!shouldModify(product)) return product;

          if (product.phases.some(p => p.name === 'rewinding')) {
            alert(`Produsul ${product.name} are deja faza 'rewinding'.`);
            return product;
          }

          const productionPhases = product.phases.filter(
            p => p.name !== 'coiling' && p.name !== 'rewinding'
          );
          const lastPhase = productionPhases[productionPhases.length - 1];
          const lastPhaseIdx = product.phases.findIndex(p => p.name === lastPhase?.name);

          if (lastPhaseIdx === -1) {
            alert(`Produsul ${product.name} nu are faze de productie.`);
            return product;
          }

          // Empty, matching the coiling branch above and every other phase.
          const newPhase = {
            name: 'rewinding',
            dependsOn: lastPhase.name,
            execution: []
          };

          const updatedPhases = [...product.phases];
          updatedPhases.splice(lastPhaseIdx + 1, 0, newPhase);

          return { ...product, phases: updatedPhases };
        })
      );

      setShowCustomPhaseModal(false);
      setNewPhaseName('');
      setSelectedProductId('all');
      return;
    }

    alert(`Custom phase "${newPhaseName}" is not recognized.`);
  };

  const handleDeletePhase = (productId, phaseName) => {
    setStandardsData(prev =>
      prev.map(product => {
        if (product._id !== productId) return product;

        // 1) Ștergem faza
        let updatedPhases = product.phases.filter(p => p.name !== phaseName);

        // 2) Curățăm dependințele: orice fază care depindea de "phaseName"
        updatedPhases = updatedPhases.map(p => {
          if (p.dependsOn === phaseName) {
            return { ...p, dependsOn: null };
          }
          return p;
        });

        return { ...product, phases: updatedPhases };
      })
    );
  };

  // 2) Obținem lista FINALĂ de faze corect sortată
  const orderedPhases = useMemo(() => {
    if (!standardData || standardData.length === 0) return [];

    // 1) Construim phaseMap doar din fazele ACTUALE
    const phaseMap = {};

    standardData.forEach(product => {
      product.phases.forEach(phase => {
        phaseMap[phase.name] = phase.dependsOn || null;
      });
    });

    // 2) Sortăm dependent
    const sortPhasesByDependency = phaseMap => {
      const sorted = [];
      const visited = new Set();

      const visit = phase => {
        if (visited.has(phase)) return;
        visited.add(phase);

        const dependsOn = phaseMap[phase];
        if (dependsOn) visit(dependsOn);

        if (!sorted.includes(phase)) sorted.push(phase);
      };

      Object.keys(phaseMap).forEach(visit);
      return sorted;
    };

    return sortPhasesByDependency(phaseMap);
  }, [standardData]);

  useEffect(() => {
    if (standardData.length === 0) return;
    console.log('STANDARD DATA , ', standardData);
    const newMax = Math.max(...standardData.map(p => p.phases.length));

    if (newMax !== maxSteps) {
      setMaxSteps(newMax);
    }
  }, [standardData]);

  useEffect(() => {
    if (!standardData || standardData.length === 0) return;

    // colectam toate fazele unice
    const phaseSet = new Set();

    standardData.forEach(product => {
      product.phases.forEach(phase => {
        phaseSet.add(phase.name);
      });
    });

    // Rebuild the { phaseName: bool } map but PRESERVE any toggle the user already
    // set. This effect re-runs on every standardData change (e.g. editing an
    // execution); without preserving, it would silently reset the user's "Merge
    // Execution" toggles to false before submit, so merge looked like it was ignored.
    setMergedPhases(prev => {
      const next = {};
      phaseSet.forEach(name => {
        next[name] = prev?.[name] === true;
      });
      return next;
    });
  }, [standardData]);

  // Trimite maxSteps în wizard form (SYNC)
  useEffect(() => {
    if (form.totalStep !== maxSteps) {
      form.setTotalStep(maxSteps);
    }
  }, [maxSteps]);

  // ── Machine required before advancing / submitting ──
  // Every execution the user creates must have a machine before leaving a step
  // (and before final submit). Two exemptions: phantom-source execs run on no
  // machine (pre-made stock → shown as PHANTOM) and reserved final phases are
  // intentionally machine-less.
  const collectPhaseExecutions = phase => {
    const out = [];
    if (!phase) return out;
    if (Array.isArray(phase.execution)) out.push(...phase.execution);
    if (Array.isArray(phase.conductors)) {
      phase.conductors.forEach(c => {
        if (Array.isArray(c?.execution)) out.push(...c.execution);
      });
    }
    return out;
  };

  const findExecsMissingMachine = phaseNames => {
    const missing = [];
    phaseNames.forEach(phaseName => {
      standardData.forEach(product => {
        const phase = product.phases?.find(p => p.name === phaseName);
        collectPhaseExecutions(phase).forEach(exec => {
          if (exec._locked) return; // produced drum (edit mode) → no machine needed
          // In edit mode edited PENDING drums still carry a tokenId but must be
          // machine-validated; in continue mode a tokenId means already planned.
          if (exec.tokenId && !product._editMode) return;
          if (exec.isPhantomSource) return; // PHANTOM → no machine needed
          if (exec.reserve) return; // reserved final phase → no machine
          if (!String(exec.machineId || '').trim()) {
            missing.push(
              `${product.name || product.productName || product._id} · ${phaseName.replaceAll('_', ' ')}`
            );
          }
        });
      });
    });
    return [...new Set(missing)];
  };

  // Passed to WizardFormFooter as beforeNext: returns false to block advancing.
  const validateMachinesBeforeNext = stepIdx => {
    const phaseNames =
      stepIdx >= orderedPhases.length
        ? orderedPhases // review step / final submit → validate every phase
        : [orderedPhases[stepIdx]]; // otherwise just the phase being left
    const missing = findExecsMissingMachine(phaseNames);
    if (missing.length) {
      setMachineError(
        `Select a machine for every execution before continuing. Missing machine on: ${missing.join(', ')}.`
      );
      return false;
    }
    setMachineError('');
    return true;
  };

  // In continue/edit mode, auto-navigate to the target phase step
  const continueTargetPhaseIndex =
    mode === 'continue' || mode === 'edit'
      ? (standardData[0]?._targetPhaseIndex ?? 0)
      : null;

  // Auto-jump to the target phase must happen ONCE, on entry — not every time
  // standardData changes. orderedPhases is a useMemo([standardData]) so it gets a
  // NEW reference on every edit (adding an execution, picking a machine); without
  // this ref the effect re-fired and yanked the user back to the target phase each
  // time they were at step 0 (e.g. filling a missing first-phase conductor).
  const didAutoJumpRef = useRef(false);
  useEffect(() => {
    if ((mode !== 'continue' && mode !== 'edit') || continueTargetPhaseIndex == null)
      return;
    if (!orderedPhases.length) return;
    if (didAutoJumpRef.current) return; // already auto-jumped once — respect manual nav

    // Map _targetPhaseName to orderedPhases index (they may differ from phases array index)
    const targetPhaseName = standardData[0]?._targetPhaseName || '';
    const stepIdx = orderedPhases.indexOf(targetPhaseName);
    const targetStep = stepIdx >= 0 ? stepIdx : continueTargetPhaseIndex;

    // Only jump once on mount, when still at step 0
    if (form.selectedStep === 0 && targetStep > 0) {
      didAutoJumpRef.current = true;
      form.setSelectedStep(targetStep);
    }
  }, [mode, continueTargetPhaseIndex, orderedPhases]);

  // In continue mode, pre-select upstream executions matching the user's selected tokens
  useEffect(() => {
    if (mode !== 'continue') return;
    if (!standardData?.length) return;

    for (const product of standardData) {
      const targetPhaseName = product._targetPhaseName || '';
      if (!targetPhaseName) continue;

      const parentTokenIds = new Set(product._parentTokenIds || []);
      if (!parentTokenIds.size) continue;

      const phase = product.phases?.find(p => p.name === targetPhaseName);
      const previousPhase =
        phase?.dependsOn &&
        product.phases?.find(p => p.name === phase.dependsOn);
      if (!phase || !previousPhase) continue;

      // build previousExecutions (same logic as PhaseDependencyPreview)
      const previousExecutions = [];
      if (previousPhase.conductors) {
        previousPhase.conductors.forEach(cond => {
          (cond.execution || []).forEach(exec => {
            previousExecutions.push({ color: cond.isolation?.color, ...exec });
          });
        });
      } else if (previousPhase.execution) {
        const allExec = previousPhase.execution.map((exec, idx) => ({
          ...exec,
          id: exec.id ?? idx + 1
        }));
        if (targetPhaseName === 'coiling') {
          const toCoiling = allExec.filter(e => e.nextPhase === 'coiling');
          previousExecutions.push(...(toCoiling.length ? toCoiling : allExec));
        } else if (targetPhaseName === 'rewinding') {
          const toRew = allExec.filter(e => e.nextPhase === 'rewinding');
          previousExecutions.push(...(toRew.length ? toRew : allExec));
        } else {
          previousExecutions.push(...allExec);
        }
      }
      if (!previousExecutions.length) continue;

      // filter out finished (consumed) executions
      const consumedLengths = buildConsumedMapForPhase(phase);
      const available = previousExecutions.filter(exec => {
        const cap =
          num(exec.plannedLength) ?? num(exec.plannedLengthInput) ?? 0;
        const used = num(consumedLengths[exec.id]) ?? 0;
        return Math.max(cap - used, 0) > 0;
      });
      if (!available.length) continue;

      // only select executions whose tokenId matches the user's selected tokens
      const hasTokenIds = available.some(e => e.tokenId);
      const matched = hasTokenIds
        ? available.filter(e => parentTokenIds.has(e.tokenId))
        : available; // fallback: select all if no tokenIds on execution entries

      if (!matched.length) continue;

      // A flat cable's exterior sheath is fed one drum per colour, like a rope.
      // Asking the same rule the picker asks is the point: when these two
      // disagreed, a MYYUP was pre-selected with a single execution where the
      // flat-sheath view requires an array, and it rendered as nothing selected.
      const isMulti = isMultiUpstreamPhase(targetPhaseName, phase, product);
      updateSelectedUpstream(
        product._id,
        targetPhaseName,
        isMulti ? matched : matched[0]
      );
    }
  }, [mode, standardData?.length]);

  return (
    <WizardFormProvider {...form}>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h4 className="mb-0">
          {mode === 'continue'
            ? 'Continue launch — Phase configuration'
            : 'Products phases configuration'}
        </h4>

        <div className="d-flex gap-2">
          <Button
            variant="outline-success"
            onClick={() => setShowCustomPhaseModal(true)}
          >
            + Add custom phase
          </Button>

          <Button variant="outline-danger" onClick={clearStandardsData}>
            {mode === 'continue' ? 'Back' : 'Reset Data'}
          </Button>
        </div>
      </div>

      <StepProgressBoard className="mb-4" />

      <Row className="kanban-create-board align-items-start">
        <Col xs={12} md={12} className="h-100 overflow-auto">
          <div className="theme-wizard d-flex flex-column h-100">
            <Tab.Content className="h-100 flex-column d-flex">
              {/* 🔁 extragem toate fazele distincte */}

              {orderedPhases.map((phaseName, idx) => {
                // In continue/edit mode, determine the target phase (read-only rules differ)
                const targetPhaseName =
                  mode === 'continue' || mode === 'edit'
                    ? standardData[0]?._targetPhaseName || ''
                    : '';
                const targetStepIdx = orderedPhases.indexOf(targetPhaseName);

                // A FIRST phase (dependsOn null) sitting before the target may still
                // have UNPLANNED conductors — e.g. only BLUE+BROWN isolation was done,
                // GREEN-YELLOW not. Allow adding those (already-planned conductors,
                // which carry a tokenId, stay locked) instead of freezing the phase.
                const isFirstPhase = standardData.some(pr => {
                  const ph = pr.phases?.find(p => p.name === phaseName);
                  return ph && (ph.dependsOn ?? null) === null;
                });
                const hasUnplannedConductor = standardData.some(pr => {
                  const ph = pr.phases?.find(p => p.name === phaseName);
                  return (
                    Array.isArray(ph?.conductors) &&
                    ph.conductors.some(
                      c =>
                        !(
                          Array.isArray(c.execution) &&
                          c.execution.some(e => e.tokenId)
                        )
                    )
                  );
                });
                const allowFirstPhaseFill =
                  mode === 'continue' &&
                  targetStepIdx > 0 &&
                  idx < targetStepIdx &&
                  isFirstPhase &&
                  hasUnplannedConductor;

                // In edit mode, phases AFTER the edited one get the per-pass choice.
                // Reconcile never re-plans, so its edit chrome (badge/toggle/alert) is
                // suppressed and every phase stays interactive.
                const isEditDownstream =
                  mode === 'edit' && !reconcile && idx > targetStepIdx;

                const isReadOnlyPhase = reconcile
                  ? false // reconcile: all phases interactive (swap produced drums)
                  : mode === 'edit'
                    ? idx < targetStepIdx
                      ? true // upstream (produced) — always read-only
                      : idx === targetStepIdx
                        ? false // the edited phase
                        : !editFurther // downstream: EDITABLE when "Edit further" is on
                    : mode === 'continue' &&
                      targetStepIdx > 0 &&
                      idx < targetStepIdx &&
                      !allowFirstPhaseFill;

                return (
                  <Tab.Pane key={phaseName} eventKey={idx}>
                    <WizardForm step={idx}>
                      <Card className="mb-4 shadow-sm">
                        <Card.Header className="fw-bold text-capitalize d-flex justify-content-between align-items-center">
                          <span>
                            {phaseName.replaceAll('_', ' ')}
                            {isReadOnlyPhase && (
                              <Badge
                                bg={
                                  isEditDownstream
                                    ? editFurther
                                      ? 'info'
                                      : 'danger'
                                    : 'success'
                                }
                                className="ms-2 fs-10"
                              >
                                {isEditDownstream
                                  ? editFurther
                                    ? 'Edit further'
                                    : 'Will delete'
                                  : 'Completed'}
                              </Badge>
                            )}
                          </span>
                          <div className="d-flex align-items-center gap-3">
                            {/* Edit-further / Delete choice for a launched downstream
                                phase — SAME style as Merge Execution, only shown for
                                phases that already have active execution. */}
                            {isEditDownstream &&
                              standardData?.[0]?._jobProgress?.[phaseName]
                                ?.taskIds?.length > 0 && (
                                <div className="d-flex align-items-center gap-2 text-primary">
                                  <span style={{ fontSize: '14px' }}>
                                    {editFurther ? 'Edit further' : 'Delete'}
                                  </span>
                                  <label className="ep-switch">
                                    <input
                                      type="checkbox"
                                      checked={editFurther}
                                      onChange={e =>
                                        setEditFurther(e.target.checked)
                                      }
                                    />
                                    <span className="ep-slider" />
                                  </label>
                                </div>
                              )}
                            {!isReadOnlyPhase && !reconcile && (
                              <div className="d-flex align-items-center gap-2 text-primary">
                                <span style={{ fontSize: '14px' }}>
                                  Merge Execution
                                </span>

                                <label className="ep-switch">
                                  <input
                                    type="checkbox"
                                    checked={mergedPhases[phaseName]}
                                    onChange={e =>
                                      setMergedPhases(prev => ({
                                        ...prev,
                                        [phaseName]: e.target.checked
                                      }))
                                    }
                                  />
                                  <span className="ep-slider" />
                                </label>
                              </div>
                            )}
                            {/* Group-by ORDERING (2+ products, same phase): queue all
                                same-color / same-wire-Ø drums together. Separate from
                                merge — drums stay independent. */}
                            {!isReadOnlyPhase &&
                              !reconcile &&
                              standardData.length >= 2 &&
                              (phaseName === 'conductor_isolation' ||
                                phaseName === 'unwinding') && (
                                <div className="d-flex align-items-center gap-2 text-primary">
                                  <span style={{ fontSize: '14px' }}>
                                    {phaseName === 'unwinding'
                                      ? 'Group by wire Ø'
                                      : 'Group by color'}
                                  </span>
                                  <label className="ep-switch">
                                    <input
                                      type="checkbox"
                                      checked={!!groupByPhases[phaseName]}
                                      onChange={e =>
                                        setGroupByPhases(prev => ({
                                          ...prev,
                                          [phaseName]: e.target.checked
                                            ? phaseName === 'unwinding'
                                              ? 'wireDiameter'
                                              : 'color'
                                            : undefined
                                        }))
                                      }
                                    />
                                    <span className="ep-slider" />
                                  </label>
                                </div>
                              )}
                          </div>
                        </Card.Header>

                        <Card.Body>
                          {isReadOnlyPhase && (
                            <div
                              className={`alert ${
                                isEditDownstream
                                  ? editFurther
                                    ? 'alert-info'
                                    : 'alert-danger'
                                  : 'alert-secondary'
                              } py-2 mb-3 fs-10`}
                            >
                              {isEditDownstream
                                ? editFurther
                                  ? 'Edit further is ON — after you save, this phase is re-opened so you can re-plan it from the new drums; its queue updates to the edited reality.'
                                  : 'Delete is ON — this phase will be removed from the queue entirely when you save (not re-planned).'
                                : 'This phase has already been planned. Showing read-only data.'}
                            </div>
                          )}
                          {/* What is staged, before any of it is written. Every
                              reconcile action changes the wizard's own state and
                              nothing else until Submit — which means it is
                              invisible unless you remember how the card looked
                              a minute ago. */}
                          {reconcile && reconcilePending.length > 0 && (
                            <div className="mb-3 p-2 rounded-2 bg-body-tertiary border">
                              <div className="d-flex align-items-center gap-2 mb-1">
                                <span className="fs-9 fw-semibold text-900">
                                  {reconcilePending.length} change
                                  {reconcilePending.length === 1 ? '' : 's'} staged
                                </span>
                                <span className="fs-10 text-700">
                                  — nothing is written until you submit
                                </span>
                              </div>
                              {reconcilePending.map(c => (
                                <div
                                  key={c.key}
                                  className="d-flex align-items-center gap-2 fs-10 text-800"
                                >
                                  <Badge
                                    bg={
                                      c.kind === 'move'
                                        ? 'info'
                                        : c.kind === 'colour'
                                          ? 'warning'
                                          : 'secondary'
                                    }
                                    className="fs-10"
                                  >
                                    {c.kind}
                                  </Badge>
                                  <span>{c.text}</span>
                                  <Button
                                    size="sm"
                                    variant="link"
                                    className="p-0 fs-10"
                                    onClick={() => c.undo()}
                                    title="Undo this one"
                                  >
                                    undo
                                  </Button>
                                </div>
                              ))}
                            </div>
                          )}

                          {/* 🔹 Bootstrap grid pentru produse */}
                          <div
                            style={
                              isReadOnlyPhase
                                ? { pointerEvents: 'none', opacity: 0.6 }
                                : undefined
                            }
                          >
                            <Row className="g-3">
                              {standardData.map(product => {
                                const phase = product.phases.find(
                                  p => p.name === phaseName
                                );
                                if (!phase) return null;
                                const productCount = standardData.length;

                                // Every product can take a dropped drum; what
                                // differs is what dropping MEANS. On a product
                                // that already shares the bobbin it is a
                                // re-slice — that one takes the whole drum. On
                                // any other it is an ordinary move. Saying which
                                // while the drum is still in the air is worth
                                // more than finding out after the drop.
                                const dragHolders = draggingToken
                                  ? (reconcileDrumShares.get(String(draggingToken)) || [])
                                  : [];
                                const dropAbsorbs =
                                  dragHolders.length > 1 &&
                                  dragHolders.some(
                                    h => String(h.jobId) === String(product._id)
                                  );

                                const colSize =
                                  productCount === 1
                                    ? 12 // full width
                                    : productCount === 2
                                      ? 6 // jumătate-jumătate
                                      : 4; // 3 pe rând

                                return (
                                  <Col
                                    key={product._id}
                                    xs={12}
                                    sm={12}
                                    md={colSize}
                                    lg={colSize}
                                    xl={colSize}
                                    xxl={colSize}
                                  >
                                    <Card
                                      className={`h-100 shadow-sm ${
                                        reconcile &&
                                        dragOverJob === String(product._id)
                                          ? dropAbsorbs
                                            ? 'border-warning border-2'
                                            : 'border-info border-2'
                                          : 'border-primary-subtle'
                                      }`}
                                      title={
                                        reconcile && draggingToken
                                          ? dropAbsorbs
                                            ? 'Already shares this bobbin — drop to give it the whole drum'
                                            : 'Drop to move this drum here'
                                          : undefined
                                      }
                                      onDragOver={
                                        reconcile
                                          ? e => {
                                              e.preventDefault();
                                              e.dataTransfer.dropEffect = 'move';
                                            }
                                          : undefined
                                      }
                                      onDragEnter={
                                        reconcile
                                          ? () =>
                                              setDragOverJob(String(product._id))
                                          : undefined
                                      }
                                      onDragLeave={
                                        reconcile
                                          ? e => {
                                              if (
                                                !e.currentTarget.contains(
                                                  e.relatedTarget
                                                )
                                              )
                                                setDragOverJob(null);
                                            }
                                          : undefined
                                      }
                                      onDrop={
                                        reconcile
                                          ? e => {
                                              e.preventDefault();
                                              const [tok, src] = String(
                                                e.dataTransfer.getData(
                                                  'text/plain'
                                                ) || ''
                                              ).split('|');
                                              setDragOverJob(null);
                                              setDraggingToken(null);
                                              if (tok)
                                                moveReconcileDrum(
                                                  tok,
                                                  String(product._id),
                                                  null,
                                                  null,
                                                  src || null
                                                );
                                            }
                                          : undefined
                                      }
                                    >
                                      <Card.Header className="bg-light fw-semibold d-flex justify-content-between align-items-center">
                                        <span className="d-inline-flex align-items-center gap-2 flex-wrap">
                                          {product.name} —{' '}
                                          {formatCoreSpec(product)}mm²
                                          <ProductColorDot
                                            colorName={productColorName(product)}
                                            size={14}
                                          />
                                          {/* Who wants this cable, asked where
                                              the cable is. Silent unless there
                                              is a decision to make. */}
                                          <ProductAllocationButton
                                            jobId={jobIdOf(product)}
                                            onAllocated={applyOrderContext}
                                          />
                                        </span>

                                        <span className="text-end">
                                          <Badge
                                            bg="primary"
                                            className="fs-6 px-3 py-2"
                                          >
                                            {product.length} m
                                          </Badge>
                                        </span>
                                        {!isReadOnlyPhase &&
                                          [
                                            'coiling',
                                            'rewinding',
                                            'unwinding'
                                          ].includes(phaseName) && (
                                            <Button
                                              variant="outline-danger"
                                              size="sm"
                                              onClick={() =>
                                                handleDeletePhase(
                                                  product._id,
                                                  phaseName
                                                )
                                              }
                                            >
                                              Delete Phase
                                            </Button>
                                          )}
                                      </Card.Header>

                                      <Card.Body>
                                        <PhaseDependencyPreview
                                          selectedUpstream={selectedUpstream}
                                          updateSelectedUpstream={
                                            updateSelectedUpstream
                                          }
                                          mergedPhases={mergedPhases}
                                          product={product}
                                          phaseName={phaseName}
                                        />

                                        {!isReadOnlyPhase &&
                                          [
                                            'unwinding',
                                            'pre_stranding',
                                            'conductor_isolation'
                                          ].includes(phaseName) &&
                                          (() => {
                                            // collect phantom allocations from ALL products (global across wizard)
                                            const phantomAllocMap = {};
                                            const usedIds = [];
                                            for (const prod of standardData) {
                                              for (const ph of prod.phases || []) {
                                                // from conductors
                                                if (ph.conductors) {
                                                  for (const c of ph.conductors) {
                                                    for (const e of c.execution || []) {
                                                      if (e.phantomId && e.isPhantomSource) {
                                                        phantomAllocMap[e.phantomId] = (phantomAllocMap[e.phantomId] || 0) + (Number(e.plannedLength) || 0);
                                                        usedIds.push(e.phantomId);
                                                      }
                                                    }
                                                  }
                                                }
                                                // from execution array
                                                for (const e of ph.execution || []) {
                                                  if (e.phantomId && e.isPhantomSource) {
                                                    phantomAllocMap[e.phantomId] = (phantomAllocMap[e.phantomId] || 0) + (Number(e.plannedLength) || 0);
                                                    usedIds.push(e.phantomId);
                                                  }
                                                }
                                              }
                                            }

                                            // collect wire specs from all conductors for matching
                                            const wireSpecs = [];
                                            if (phase?.conductors) {
                                              for (const c of phase.conductors) {
                                                if (c?.wire) {
                                                  wireSpecs.push({
                                                    wireType: c.wire.type || '',
                                                    wireDiameter: c.wire.wireDiameter || c.wire.diameter,
                                                    numberOfWires: c.wire.numberOfWires
                                                  });
                                                }
                                              }
                                            }

                                            // total planned length for pre_stranding stock picker
                                            const preStrandingTotal = phaseName === 'pre_stranding'
                                              ? (phase?.execution || [])
                                                  .filter(e => !e.isPhantomSource)
                                                  .reduce((s, e) => s + (Number(e.plannedLength) || 0), 0)
                                                || Number(product.length) || 0
                                              : 0;

                                            return (
                                              <PhantomTokenPicker
                                                phaseName={phaseName}
                                                section={product.section}
                                                wireSpecs={wireSpecs}
                                                phaseData={phaseName === 'pre_stranding' ? phase : null}
                                                totalNeeded={preStrandingTotal}
                                                usedPhantomIds={usedIds}
                                                phantomAllocMap={phantomAllocMap}
                                                onUsePhantom={phantom => {
                                                  // prevent double-add
                                                  if (
                                                    usedIds.includes(
                                                      phantom.phantomId
                                                    )
                                                  )
                                                    return;

                                                  const ph =
                                                    product.phases?.find(
                                                      p => p.name === phaseName
                                                    );
                                                  if (!ph) return;

                                                  const newExec = {
                                                    id: Date.now(),
                                                    machineId: '',
                                                    plannedLength:
                                                      phantom.availableLength,
                                                    // cap for the editable "used
                                                    // length" input on the card
                                                    phantomMax:
                                                      phantom.availableLength,
                                                    drum_material:
                                                      phantom.drum_material ||
                                                      '',
                                                    drum_diameter:
                                                      phantom.drum_diameter ||
                                                      '',
                                                    phantomId:
                                                      phantom.phantomId,
                                                    isPhantomSource: true,
                                                    phantomColor:
                                                      phantom.color || null,
                                                    phantomNote:
                                                      phantom.note || null
                                                  };

                                                  if (ph.conductors) {
                                                    const condIdx =
                                                      ph.conductors.findIndex(
                                                        c =>
                                                          String(
                                                            c?.isolation
                                                              ?.color || ''
                                                          ).toUpperCase() ===
                                                          String(
                                                            phantom.color || ''
                                                          ).toUpperCase()
                                                      );
                                                    if (condIdx >= 0) {
                                                      const updated = [
                                                        ...ph.conductors
                                                      ];
                                                      const cond = {
                                                        ...updated[condIdx]
                                                      };
                                                      cond.execution = [
                                                        ...(cond.execution ||
                                                          []),
                                                        {
                                                          ...newExec,
                                                          color:
                                                            phantom.color ||
                                                            null
                                                        }
                                                      ];
                                                      updated[condIdx] = cond;
                                                      handleProductPhaseChange(
                                                        product._id,
                                                        phaseName,
                                                        'conductors',
                                                        updated
                                                      );
                                                    }
                                                  } else {
                                                    // unwinding groups execs by
                                                    // ropeIndex + wireDiameter — tag
                                                    // the phantom so it lands on the
                                                    // correct wire (otherwise invisible)
                                                    const extra =
                                                      phaseName === 'unwinding'
                                                        ? matchUnwindingTarget(
                                                            product,
                                                            phantom
                                                          )
                                                        : {};
                                                    handleProductPhaseChange(
                                                      product._id,
                                                      phaseName,
                                                      'execution',
                                                      [
                                                        ...(ph.execution || []),
                                                        { ...newExec, ...extra }
                                                      ]
                                                    );
                                                  }
                                                }}
                                                onUsePhantoms={phantomSelections => {
                                                  // multi-phantom for pre_stranding stock
                                                  const ph = product.phases?.find(p => p.name === phaseName);
                                                  if (!ph) return;

                                                  // remove existing phantom executions, keep non-phantom ones
                                                  const kept = (ph.execution || []).filter(e => !e.isPhantomSource);

                                                  const phantomExecs = phantomSelections.map(sel => ({
                                                    id: Date.now() + Math.random(),
                                                    machineId: '',
                                                    plannedLength: sel.useLength,
                                                    drum_material: sel.drum_material || '',
                                                    drum_diameter: sel.drum_diameter || '',
                                                    phantomId: sel.phantomId,
                                                    isPhantomSource: true,
                                                    phantomColor: sel.color || null,
                                                    phantomNote: sel.note || null
                                                  }));

                                                  handleProductPhaseChange(
                                                    product._id,
                                                    phaseName,
                                                    'execution',
                                                    [...kept, ...phantomExecs]
                                                  );
                                                }}
                                              />
                                            );
                                          })()}

                                        {!isReadOnlyPhase &&
                                          (mode === 'continue' || mode === 'create') &&
                                          [
                                            // create-launch also shows the context
                                            // from the first phase; continue starts at rope.
                                            ...(mode === 'create'
                                              ? ['conductor_isolation', 'pre_stranding']
                                              : []),
                                            'rope',
                                            'interior_sheat',
                                            'armouring',
                                            'exterior_sheat',
                                            'coiling',
                                            'rewinding'
                                          ].includes(phaseName) && (
                                            <OrderDeliveryPanel
                                              product={product}
                                              phaseName={phaseName}
                                              contextOnly={
                                                mode !== 'continue' &&
                                                ![
                                                  'exterior_sheat',
                                                  'coiling',
                                                  'rewinding'
                                                ].includes(phaseName)
                                              }
                                              onChange={
                                                handleProductPhaseChange
                                              }
                                              selectedUpstream={
                                                selectedUpstream
                                              }
                                              onAddCoilingPhase={
                                                handleAddCoilingPhase
                                              }
                                              allProducts={standardData}
                                              reconcileAutoCoiling={
                                                reconcileAutoCoiling
                                              }
                                              mode={mode}
                                              onReserveChange={
                                                handleReserveChange
                                              }
                                              onSaveReservation={
                                                handleSaveReservation
                                              }
                                              onDetachOrder={handleDetachOrder}
                                              onAllocateMore={handleAllocateMore}
                                              onSaveAllReservations={(ord, items) =>
                                                handleSaveAllReservations(
                                                  items,
                                                  ord?.orderCode
                                                )
                                              }
                                            />
                                          )}

                                        <PhaseFormStep
                                          lockPlannedConductors={
                                            allowFirstPhaseFill
                                          }
                                          handleAddCoilingPhase={
                                            handleAddCoilingPhase
                                          }
                                          handleAddRewindingPhase={
                                            handleAddRewindingPhase
                                          }
                                          product={product}
                                          phase={phase}
                                          mergeEnabled={
                                            mergedPhases?.[phase.name] === true
                                          }
                                          productId={product._id}
                                          productCount={standardData.length}
                                          selectedUpstream={selectedUpstream}
                                          updateSelectedUpstream={
                                            updateSelectedUpstream
                                          }
                                          onChange={handleProductPhaseChange}
                                          getDrumPreset={getDrumPreset}
                                          setDrumPresetMaterial={
                                            setDrumPresetMaterial
                                          }
                                          setDrumPresetDiameter={
                                            setDrumPresetDiameter
                                          }
                                          drumLibrary={drumLibrary}
                                          reconcileAutoCoiling={
                                            reconcileAutoCoiling
                                          }
                                          reconcile={reconcile}
                                          reconcileTargets={reconcileTargets}
                                          reconcileSel={reconcileSel}
                                          setReconcileSel={setReconcileSel}
                                          moveReconcileDrum={moveReconcileDrum}
                                          reconcileDrums={reconcileDrums}
                                          reconcileShares={reconcileShares}
                                          reconcileDrumShares={
                                            reconcileDrumShares
                                          }
                                          reconcileLen={reconcileLen}
                                          onDragTokenChange={
                                            setDraggingToken
                                          }
                                          setReconcileLength={
                                            setReconcileLength
                                          }
                                          swapReconcileDrums={swapReconcileDrums}
                                        />
                                      </Card.Body>
                                    </Card>
                                  </Col>
                                );
                              })}
                            </Row>
                          </div>
                        </Card.Body>
                      </Card>
                    </WizardForm>
                  </Tab.Pane>
                );
              })}
              <Tab.Pane key="review" eventKey={orderedPhases.length}>
                <FinalReviewPage
                  standardData={standardData}
                  orderedPhases={orderedPhases}
                  mergedPhases={mergedPhases}
                  // A separate state from `mergedPhases`, and the review needs
                  // BOTH: group-by decides the ORDER the drums reach the machine
                  // (colour by colour), merge decides which of them share one.
                  // Passing only the merge flag is why a group-by-colour launch
                  // previewed as one product after the other.
                  groupByPhases={groupByPhases}
                  setStandardsData={setStandardsData}
                />
              </Tab.Pane>
            </Tab.Content>

            {submitError && (
              <div className="alert alert-danger py-2 mt-2 mb-0">
                {submitError}
              </div>
            )}

            {machineError && (
              <div className="alert alert-warning py-2 mt-2 mb-0">
                ⚠ {machineError}
              </div>
            )}

            {launched ? (
              <LaunchedPanel
                jobs={launched}
                onLaunchAnother={clearStandardsData}
              />
            ) : (
              <WizardFormFooter
                setSelectedUpstream={setSelectedUpstream}
                beforeNext={validateMachinesBeforeNext}
                nextBtnLabel={form.getCanNextPage ? 'Next' : 'Submit'}
                handleSubmit={async () => {
                  setSubmitError('');
                  try {
                    if (handleSubmitFc) {
                      await handleSubmitFc({
                        standardData,
                        mergedPhases,
                        groupByPhases,
                        editFurther,
                        reconcile: reconcileSel,
                        reconcileLengths: reconcileLen
                      });
                      // The edit / reconcile flows navigate on their own — their
                      // own page owns where to go next. Nothing to announce.
                    } else {
                      const { data } = await axios.post(
                        `${API.REDIS}/api/redis/new-job`,
                        {
                          standardData,
                          mergedPhases,
                          groupByPhases
                        }
                      );
                      // `{ success, jobs: [{ jobId, jobCode }] }`. A launch can
                      // make SEVERAL jobs — one per product, and one per colour
                      // of a multi-colour launch — so this is a list, never one.
                      const made = Array.isArray(data?.jobs) ? data.jobs : [];
                      setLaunched(
                        made.length
                          ? made
                          : // Launched, but the service did not say what it made.
                            // Still a success: say so rather than leave the
                            // planner pressing Submit again.
                            [{ jobId: null, jobCode: null }]
                      );
                    }
                  } catch (e) {
                    const msg =
                      e?.response?.data?.error ||
                      e?.response?.data?.message ||
                      e?.message ||
                      'Submit failed';
                    console.error('plan submit error:', e);
                    setSubmitError(msg);
                  }
                }}
              />
            )}
          </div>
        </Col>
      </Row>

      <Modal
        show={showCustomPhaseModal}
        onHide={() => setShowCustomPhaseModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Add custom phase</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form>
            {/* Selectăm faza */}
            <Form.Group className="mb-3">
              <Form.Label>Select phase type</Form.Label>
              <Form.Select
                value={newPhaseName}
                onChange={e => setNewPhaseName(e.target.value)}
              >
                <option value="">Select Phase...</option>
                <option value="unwinding">UNWINDING</option>
                <option value="coiling">COILING</option>
                <option value="rewinding">REWINDING</option>
              </Form.Select>
            </Form.Group>

            {/* Selectăm produsul */}
            <Form.Group className="mb-3">
              <Form.Label>Apply to</Form.Label>
              <Form.Select
                value={selectedProductId}
                onChange={e => setSelectedProductId(e.target.value)}
              >
                <option value="all">All products</option>
                {standardData.map(product => (
                  <option key={product._id} value={product._id}>
                    {product.name} — {formatCoreSpec(product)}mm²
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            {/* Afișăm descrieri scurte */}
            {newPhaseName === 'unwinding' && (
              <Alert variant="info">
                <strong>UNWINDING:</strong> Used for copper ropes (multi-wire or
                solid).
              </Alert>
            )}
            {newPhaseName === 'rewinding' && (
              <Alert variant="info">
                <strong>REWINDING:</strong> Drum → Drum transfer for final
                products.
              </Alert>
            )}
            {newPhaseName === 'coiling' && (
              <Alert variant="info">
                <strong>COILING:</strong> Used for final products — creates
                coils after extrusion.
              </Alert>
            )}
          </Form>
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="danger"
            onClick={() => setShowCustomPhaseModal(false)}
          >
            Cancel
          </Button>
          <Button variant="primary" onClick={handleAddCustomPhase}>
            Add phase
          </Button>
        </Modal.Footer>
      </Modal>
    </WizardFormProvider>
  );
};

export default CreateWizzardForm;

const FinalReviewPage = ({
  standardData,
  orderedPhases,
  mergedPhases,
  groupByPhases = {},
  setStandardsData
}) => {
  const productTitle = p =>
    `${formatProductLabel(p)}mm² — ${p.length}m`;

  // ------------------------ TANDEM (per product) ------------------------
  // interior_sheat + exterior_sheat run as ONE execution on a 2-extruder line.
  const productHasTandemPair = p =>
    p.phases?.some(x => x.name === 'interior_sheat') &&
    p.phases?.some(x => x.name === 'exterior_sheat');

  // Same machine was the only thing checked here, and it is not enough. The
  // fusion pairs the two mantles by the upstream link between their executions,
  // so a shape where some interior drum is fed to nothing passes the machine
  // test and then comes apart at launch: fusion deletes the interior tasks it
  // matched, leaves the rest standing, and the interior phase never closes.
  // See helpers/tandemCheck.js.
  const tandemCheck = p => checkTandemPair(p);

  const toggleTandem = productId => {
    if (typeof setStandardsData !== 'function') return;
    setStandardsData(prev =>
      prev.map(p => (p._id === productId ? { ...p, tandem: !p.tandem } : p))
    );
  };

  const tandemProducts = standardData.filter(productHasTandemPair);

  // ------------------------ BADGES ------------------------
  const statusBadges = () => {
    return (
      <div className="badge-group">
        <span className="badge badge-ok">OK</span>
      </div>
    );
  };

  // ------------------------------------
  // NORMAL EXECUTION TABLE
  // ------------------------------------
  const renderExecutionTable = (phase, executions) => {
    if (!executions?.length) return <div className="empty">No executions</div>;

    return (
      <table className="phase-table">
        <thead>
          <tr>
            <th>#</th>
            <th>Machine</th>
            <th>Length</th>
            <th>Drum</th>
            <th>Ø</th>
            {phase === 'coiling' && <th>Coils</th>}
            {phase === 'rewinding' && <th>Rewinds</th>}
            <th>Status</th>
          </tr>
        </thead>

        <tbody>
          {executions.map((exec, i) => (
            <tr key={exec.id || i}>
              <td>{i + 1}</td>
              <td>{exec.machineId || '-'}</td>
              <td>{exec.plannedLength || exec.fromUpstreamLength || '-'}</td>
              <td>{exec.drum_material || '-'}</td>
              <td>{exec.drum_diameter || '-'}</td>

              {phase === 'coiling' && (
                <td>
                  {exec.coilCount} × {exec.coilLength} ={' '}
                  <strong>
                    {(exec.coilCount || 0) * (exec.coilLength || 0)}
                  </strong>
                </td>
              )}

              {phase === 'rewinding' && (
                <td>
                  {exec.rewindCount} × {exec.rewindLength} ={' '}
                  <strong>
                    {(exec.rewindCount || 0) * (exec.rewindLength || 0)}
                  </strong>
                </td>
              )}

              <td>{statusBadges(exec, phase)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  // ----------------------------------------------------------
  // MERGE LOGIC: create ONE TABLE with SUMMED conductor lengths
  // ----------------------------------------------------------
  const mergePhase = phaseName => {
    const merged = {
      productNames: [],
      conductors: {}
    };

    // colectăm execuțiile
    standardData.forEach(product => {
      const phase = product.phases.find(p => p.name === phaseName);
      if (!phase) return;

      merged.productNames.push(productTitle(product));

      if (phase.conductors) {
        phase.conductors.forEach((c, idx) => {
          if (!merged.conductors[idx])
            merged.conductors[idx] = {
              color: c.isolation?.color,
              rows: []
            };

          c.execution.forEach(exec => merged.conductors[idx].rows.push(exec));
        });
      } else {
        if (!merged.conductors[0])
          merged.conductors[0] = { color: null, rows: [] };

        phase.execution.forEach(exec => merged.conductors[0].rows.push(exec));
      }
    });

    // -------------------------------
    // ACCUMULĂM SUMELE + expression + mismatch flag
    // -------------------------------
    Object.values(merged.conductors).forEach(c => {
      const lengths = c.rows.map(exec => {
        if (exec.coilCount && exec.coilLength)
          return exec.coilCount * exec.coilLength;
        if (exec.rewindCount && exec.rewindLength)
          return exec.rewindCount * exec.rewindLength;
        return exec.plannedLength || exec.fromUpstreamLength || 0;
      });

      const totalLength = lengths.reduce((a, b) => a + b, 0);
      const expression = lengths.join(' + ');
      const allEqual = lengths.every(v => v === lengths[0]);

      c.mergedRow = {
        machineId: c.rows[0]?.machineId || '-',
        drum_material: c.rows[0]?.drum_material || '-',
        drum_diameter: c.rows[0]?.drum_diameter || '-',
        mergedLength: totalLength,
        expression,
        allEqual
      };
    });

    return merged;
  };

  // ------------------------------------
  // MERGED TABLE UI
  // ------------------------------------
  const renderMergedTable = (phase, conductorData) => {
    const exec = conductorData.mergedRow;

    const highlightClass = exec.allEqual ? '' : 'length-mismatch';

    return (
      <table className="phase-table merged-table">
        <thead>
          <tr>
            <th>#</th>
            <th>Machine</th>
            <th>Total Length</th>
            <th>Drum</th>
            <th>Ø</th>
            <th>Status</th>
          </tr>
        </thead>

        <tbody>
          <tr className={highlightClass}>
            <td>1</td>
            <td>{exec.machineId}</td>
            <td>
              <strong>
                {exec.expression} = {exec.mergedLength} m
              </strong>
            </td>
            <td>{exec.drum_material}</td>
            <td>{exec.drum_diameter}</td>
            <td>{statusBadges(exec, phase)}</td>
          </tr>
        </tbody>
      </table>
    );
  };

  // ------------------------------------
  // RENDER PAGE
  // ------------------------------------
  return (
    <div className="review-wrapper">
      <h2 className="review-title">Final Production Review</h2>

      {/* The launch as the MACHINE will list it — one row per drum, merged
          drums collapsed, the lot named once. The per-phase tables below stay
          exactly as they were: this answers "is the program right", those
          answer "is this execution right", and they are different questions. */}
      <MachineProgramPreview
        standardData={standardData}
        mergedPhases={mergedPhases}
        groupByPhases={groupByPhases}
        orderedPhases={orderedPhases}
        labelOf={p => `${formatProductLabel(p)}mm²`}
      />

      {tandemProducts.length > 0 && (
        <div className="phase-card" style={{ marginBottom: 16 }}>
          <div className="phase-header">
            Tandem — Manta Interioara + Exterioara (o singură execuție)
          </div>
          <div className="product-body" style={{ padding: 12 }}>
            {tandemProducts.map(p => {
              const check = tandemCheck(p);
              return (
                <div
                  key={p._id}
                  className="d-flex align-items-start justify-content-between"
                  style={{ padding: '6px 0' }}
                >
                  <div>
                    <span className="fw-semibold">{productTitle(p)}</span>

                    {/* The totals, always. In a tandem the interior drum never
                        physically exists — the cable runs straight through both
                        heads onto the exterior drum — so its drum TYPE does not
                        matter. Its lengths do: they are what the pairing is
                        built from, and seeing both totals is how you spot a
                        mismatch before it is a broken job. */}
                    <div className="text-700" style={{ fontSize: 12 }}>
                      interior {check.interior.count} drum(s) ·{' '}
                      {check.interior.total.toLocaleString()} m
                      {check.interior.machine ? ` · ${check.interior.machine.toUpperCase()}` : ''}
                      {'  →  '}
                      exterior {check.exterior.count} drum(s) ·{' '}
                      {check.exterior.total.toLocaleString()} m
                      {check.exterior.machine ? ` · ${check.exterior.machine.toUpperCase()}` : ''}
                    </div>

                    {p.tandem &&
                      check.problems.map((msg, i) => (
                        <div key={i} className="text-danger" style={{ fontSize: 12 }}>
                          {msg}
                        </div>
                      ))}
                  </div>
                  <Form.Check
                    type="switch"
                    id={`tandem-${p._id}`}
                    label="Tandem"
                    checked={!!p.tandem}
                    onChange={() => toggleTandem(p._id)}
                  />
                </div>
              );
            })}
          </div>
        </div>
      )}

      {orderedPhases.map(phaseName => {
        const isMerged = mergedPhases?.[phaseName] === true;

        return (
          <div className="phase-card" key={phaseName}>
            <div className="phase-header">
              Phase: {phaseName.replaceAll('_', ' ')}
            </div>

            {/* MERGED MODE */}
            {isMerged &&
              (() => {
                const merged = mergePhase(phaseName);

                return (
                  <div
                    className={`product-block merged-block ${
                      isMerged ? 'open' : ''
                    }`}
                  >
                    <div className="product-header text-success fw-bold">
                      {merged.productNames.join(' + ')}
                    </div>

                    {Object.entries(merged.conductors).map(([idx, c]) => (
                      <div key={idx}>
                        <div className="conductor-label">
                          Conductor {Number(idx) + 1} —{' '}
                          {c.color && (
                            <Badge
                              bg=""
                              className="fw-bold"
                              style={cableColorBadgeStyle(c.color)}
                            >
                              {c.color}
                            </Badge>
                          )}
                        </div>

                        {renderMergedTable(phaseName, c)}
                      </div>
                    ))}
                  </div>
                );
              })()}

            {/* NORMAL MODE */}
            {!isMerged &&
              standardData.map(product => {
                const phase = product.phases.find(p => p.name === phaseName);
                if (!phase) return null;

                return (
                  <div className="product-block" key={product._id}>
                    <div className="product-header text-primary">
                      {productTitle(product)}
                    </div>

                    <div className="product-body">
                      {phase.conductors
                        ? phase.conductors.map((c, i) => (
                            <div key={i}>
                              <div className="conductor-label">
                                Conductor {i + 1} —{' '}
                                {c?.isolation?.color && (
                                  <Badge
                                    bg=""
                                    className="fw-bold"
                                    style={cableColorBadgeStyle(
                                      c.isolation.color
                                    )}
                                  >
                                    {c.isolation.color}
                                  </Badge>
                                )}
                              </div>
                              {renderExecutionTable(phaseName, c.execution)}
                            </div>
                          ))
                        : renderExecutionTable(phaseName, phase.execution)}
                    </div>
                  </div>
                );
              })}
          </div>
        );
      })}
    </div>
  );
};

const ProgressTracker = ({
  targetLength = 0,
  executionSource = [],
  showLabel = true,
  compact = false
}) => {
  const total = targetLength || 0;

  // --- CALCUL plannedTotal --- //
  let plannedTotal = executionSource.reduce((sum, exec) => {
    // suport pentru coiling
    if (exec.coilCount && exec.coilLength) {
      return sum + exec.coilCount * exec.coilLength;
    }

    if (exec.wireCount) {
      return sum + exec.wireCount * exec.plannedLength;
    }

    return sum + (parseFloat(exec.plannedLength) || 0);
  }, 0);

  const remaining = Math.max(total - plannedTotal, 0);
  const percent = total > 0 ? (plannedTotal / total) * 100 : 0;

  return (
    <div className="mt-2">
      {showLabel && (
        <div className="d-flex justify-content-between align-items-center mb-1">
          {!compact && (
            <small className="text-muted">
              {plannedTotal.toFixed(1)} m / {total} m
            </small>
          )}
        </div>
      )}

      <div className="progress" style={{ height: compact ? '12px' : '8px' }}>
        <div
          className={`progress-bar ${
            percent > 100
              ? 'bg-danger'
              : percent >= 90
                ? 'bg-success'
                : 'bg-warning'
          }`}
          style={{ width: `${Math.min(percent, 100)}%` }}
        />
      </div>

      {!compact && (
        <div className="d-flex justify-content-between small mt-1 text-secondary">
          <span>Planned: {plannedTotal.toFixed(1)} m</span>
          <span>Remaining: {remaining.toFixed(1)} m</span>
        </div>
      )}
    </div>
  );
};

// USE DEBOUNCE ON THIS COMPONENT
// The three states of the merge-group selector, written once so the two rows
// cannot disagree.
//
//   ''      Auto      — the backend numbers it 0,1,2… as it always did. This
//                       keeps an execution alone only while no other product
//                       happens to be numbered into the same slot: luck, not
//                       an instruction.
//   'solo'  Alone     — a slot above the pickable range, one per execution, so
//                       nothing can join it. This is the guarantee.
//   '0'..   Group N   — same number on two rows puts them on one drum, across
//                       any number of products.
//
// Both fields are always written, so switching away from Alone cannot leave a
// stale mergeSolo behind, and switching to it cannot leave a stale group.
const mergeChoiceToPatch = value => {
  if (value === 'solo') return { mergeSlot: null, mergeSolo: true };
  if (value === '') return { mergeSlot: null, mergeSolo: false };
  return { mergeSlot: Number(value), mergeSolo: false };
};

const ConductorExecutionRow = ({
  exec,
  execIdx,
  conductor,
  conductorIdx,
  product,
  phase,
  productId,
  mergeEnabled = false,
  productCount = 1,
  selectedUpstream,
  handleConductorChange,
  patchConductorExecution,
  handleRemoveExecution,
  handleAddCoilingPhase,
  handleAddRewindingPhase,
  drumLibrary = [],
  reconcile = false,
  reconcileTargets = [],
  reconcileSel = {},
  setReconcileSel = () => {},
  moveReconcileDrum = () => {},
  reconcileDrums = [],
  reconcileShares = [],
  reconcileDrumShares = new Map(),
  reconcileLen = {},
  setReconcileLength = () => {},
  onDragTokenChange = () => {},
  swapReconcileDrums = () => {}
}) => {
  const isPhantom = !!(exec.isPhantomSource && exec.phantomId);

  const [draftLength, setDraftLength] = useState(exec.plannedLength ?? '');

  useEffect(() => {
    setDraftLength(exec.plannedLength ?? '');
  }, [exec.plannedLength]);

  const { flush } = useDebouncedCommit({
    value: draftLength,
    delay: 350,
    onCommit: val => {
      const numericValue = Number(val);
      if (!Number.isFinite(numericValue) || numericValue <= 0) return;

      handleConductorChange(
        conductorIdx,
        'execution_plannedLength',
        numericValue,
        execIdx
      );
    }
  });

  const selectedMachine = String(exec.machineId || '').trim();
  const machineMaxDrum = getMachineMaxDrum(selectedMachine);

  const cableDiameter = Number(conductor?.isolation?.diameter || 0);

  const canUse800 = !machineMaxDrum || machineMaxDrum >= 800;
  const canUse1000 = !machineMaxDrum || machineMaxDrum >= 1000;

  const optimal800 = getConductorOptimalLot({
    product,
    conductor,
    drumDiameter: 800
  });

  const optimal1000 = getConductorOptimalLot({
    product,
    conductor,
    drumDiameter: 1000
  });

  const applyExecPatch = patch => {
    patchConductorExecution(conductorIdx, execIdx, patch);
  };

  const applyOptimalLot = drumDiameter => {
    const optimalLength = getConductorOptimalLot({
      product,
      conductor,
      drumDiameter
    });

    if (!optimalLength) return;

    applyExecPatch({
      drum_material: 'metal',
      drum_diameter: drumDiameter,
      plannedLength: optimalLength
    });

    setDraftLength(String(optimalLength));
  };

  const hasDrumSelected =
    String(exec.drum_material || '').trim() !== '' &&
    Number(exec.drum_diameter) > 0;

  const estimatedLength = useMemo(() => {
    const drumMaterial = exec.drum_material;
    const drumDiameter = exec.drum_diameter;

    if (!drumMaterial || !drumDiameter || !cableDiameter) return null;

    const drum = findDrumConfig(drumMaterial, drumDiameter, drumLibrary);
    if (!drum) return null;

    const usableWidthFactor = getRecommendedUsableWidthFactor(
      drum.drumDiameter
    );

    const estimate = estimateDrumCapacity({
      drumDiameter: drum.drumDiameter,
      barrelDiameter: drum.barrelDiameter,
      flangeWidth: drum.flangeWidth,
      cableDiameter,
      fillFactor: 0.8,
      usableWidthFactor
    });

    if (!estimate?.estimatedLength) return null;

    return estimate.estimatedLength;
  }, [exec.drum_material, exec.drum_diameter, cableDiameter]);

  // ── PRODUCED DRUM (edit mode): locked, cannot be changed or removed ──
  if (exec._locked) {
    const reconcileHome = String(product._id || product._parentJobId || '');
    // A merged drum is draggable too. Dropping it on the product it shares the
    // bobbin with means "all of this was really theirs" — the share goes there
    // and this one goes to zero. The drum itself does not move and its tokenId
    // does not change; that is what holds the merge together.
    const reconcileMovable = reconcile && !!exec.tokenId;
    const reconcileMovedFrom =
      exec._reconcileOrigin &&
      String(exec._reconcileOrigin) !== reconcileHome
        ? reconcileTargets.find(
            t => t.jobId === String(exec._reconcileOrigin)
          )?.label || String(exec._reconcileOrigin)
        : null;
    const reconcileRecoloredFrom =
      exec._reconcileOriginColor != null &&
      exec.color != null &&
      String(exec.color) !== String(exec._reconcileOriginColor)
        ? String(exec._reconcileOriginColor)
        : null;
    const reconcileChanged = !!(reconcileMovedFrom || reconcileRecoloredFrom);
    return (
      <Card
        className={`mb-3 shadow-sm ${
          reconcileChanged ? 'border-warning' : 'border-success'
        }`}
        style={{
          borderLeft: `5px solid ${reconcileChanged ? '#ffc107' : '#198754'}`,
          opacity: 0.9,
          cursor: 'default'
        }}
        // Dragging starts from the grip in the header, never from the card.
        //
        // A whole card that is draggable fires on a few pixels of movement, and
        // these cards sit close together — reaching for a field or a button
        // picked the drum up instead, and by the time the operator noticed, a
        // move had already happened. A handle costs one deliberate grab.
        draggable={false}
      >
        <Card.Header className="bg-success-subtle d-flex justify-content-between align-items-center flex-wrap gap-1">
          <div className="d-flex align-items-center gap-2">
            {reconcileMovable && (
              <span
                draggable
                onDragStart={e => {
                  // The source product travels with the drum: a merged token is
                  // in two of them and the drop has to know which was lifted.
                  e.dataTransfer.setData(
                    'text/plain',
                    `${String(exec.tokenId)}|${reconcileHome}`
                  );
                  onDragTokenChange?.(String(exec.tokenId));
                }}
                onDragEnd={() => onDragTokenChange?.(null)}
                title="Drag this drum to another product or conductor"
                className="px-1 text-700"
                style={{ cursor: 'grab', userSelect: 'none', lineHeight: 1 }}
              >
                ⠿
              </span>
            )}
            <Badge bg={exec._merged ? 'info' : 'success'}>
              {exec._merged ? 'MERGED' : 'PRODUCED'}
            </Badge>
            <span className="fw-semibold">
              {Number(
                exec._producedLength || exec.plannedLength || 0
              ).toLocaleString()}{' '}
              m
            </span>
            {exec.color && <Badge bg="dark">{exec.color}</Badge>}
          </div>
          <div className="d-flex align-items-center gap-1">
            {reconcileRecoloredFrom && (
              <Badge bg="warning" text="dark" className="fs-10">
                was {reconcileRecoloredFrom}
              </Badge>
            )}
            {reconcileMovedFrom && (
              <Badge bg="warning" text="dark" className="fs-10">
                moved from {reconcileMovedFrom}
              </Badge>
            )}
          </div>
        </Card.Header>
        <Card.Body className="py-2">
          <Row className="g-2">
            <Col md={4}>
              <Form.Group>
                <Form.Label>Machine<ExecWarning exec={exec} product={product} phase={phase} field="machine" /></Form.Label>
                <Form.Control
                  size="sm"
                  value={exec.machineId || '-'}
                  readOnly
                  disabled
                  className="bg-light"
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>Length (m)<ExecWarning exec={exec} product={product} phase={phase} field="length" /></Form.Label>
                {exec._merged && !exec._produced ? (
                  <Form.Control
                    size="sm"
                    type="number"
                    value={draftLength}
                    onChange={e => setDraftLength(e.target.value)}
                    onBlur={() => flush()}
                    title="Shared drum — this length is applied to every product on it"
                  />
                ) : (
                  <Form.Control
                    size="sm"
                    value={exec.plannedLength || ''}
                    readOnly
                    disabled
                    className="bg-light"
                  />
                )}
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>Drum Ø<ExecWarning exec={exec} product={product} phase={phase} field="drum" /></Form.Label>
                <Form.Control
                  size="sm"
                  value={exec.drum_diameter || '-'}
                  readOnly
                  disabled
                  className="bg-light"
                />
              </Form.Group>
            </Col>
          </Row>
          {reconcile && exec.tokenId && (
            <Row className="g-2 mt-1 pt-2 border-top">
              <Col md={8}>
                <Form.Group>
                  <Form.Label className="fs-10 fw-semibold text-primary mb-1">
                    Belongs to product{' '}
                    {!exec._merged && (
                      <span className="text-body-tertiary fw-normal">
                        (or drag the card)
                      </span>
                    )}
                  </Form.Label>
                  <div className="d-flex gap-2 align-items-center">
                    <Form.Select
                      size="sm"
                      value={reconcileHome}
                      onChange={e =>
                        moveReconcileDrum(
                          exec.tokenId,
                          e.target.value,
                          null,
                          null,
                          reconcileHome
                        )
                      }
                    >
                      {reconcileTargets.map(t => (
                        <option key={t.jobId} value={t.jobId}>
                          {t.label}
                        </option>
                      ))}
                    </Form.Select>
                    {reconcileChanged && (
                      <Button
                        size="sm"
                        variant="outline-secondary"
                        className="fs-10 flex-shrink-0"
                        title="Restore original product and colour"
                        onClick={() =>
                          moveReconcileDrum(
                            exec.tokenId,
                            String(exec._reconcileOrigin || reconcileHome),
                            exec._reconcileOriginColor ?? null
                          )
                        }
                      >
                        Undo
                      </Button>
                    )}
                  </div>
                </Form.Group>
              </Col>
              <Col md={4} className="d-flex align-items-end">
                <Form.Check
                  type="checkbox"
                  id={`reconcile-relaunch-${exec.tokenId}`}
                  label="Relaunch"
                  checked={!!reconcileSel[exec.tokenId]?.relaunch}
                  onChange={e =>
                    setReconcileSel(m => ({
                      ...m,
                      [exec.tokenId]: {
                        ...(m[exec.tokenId] || {}),
                        relaunch: e.target.checked
                      }
                    }))
                  }
                />
              </Col>
              {reconcileDrums.filter(
                d => String(d.tokenId) !== String(exec.tokenId)
              ).length > 0 && (
                  <Col md={12}>
                    <Form.Group>
                      <Form.Label className="fs-10 fw-semibold text-primary mb-1">
                        Swap colours with… (inversion — lengths kept)
                      </Form.Label>
                      <Form.Select
                        size="sm"
                        value=""
                        onChange={e => {
                          if (e.target.value)
                            swapReconcileDrums(exec.tokenId, e.target.value);
                        }}
                      >
                        <option value="">— pick a drum to swap colours —</option>
                        {reconcileDrums
                          .filter(
                            d => String(d.tokenId) !== String(exec.tokenId)
                          )
                          .map(d => (
                            <option key={d.tokenId} value={d.tokenId}>
                              {d.label} · {d.color || '—'} ·{' '}
                              {Number(d.length || 0).toLocaleString()} m
                              {d.merged ? ' · shared bobbin' : ''}
                            </option>
                          ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                )}
              {exec._merged && (
                <Col md={12}>
                  <div className="text-body-tertiary fs-10 fst-italic">
                    Shared bobbin. Drag it onto the other product to give it the
                    whole drum, use ⇄ to exchange the two shares, or type a
                    length. The drum stays where it is — only the split changes.
                  </div>
                </Col>
              )}
            </Row>
          )}
          {reconcile && exec._produced && (
            <ReconcileDrumLength
              exec={exec}
              jobId={reconcileHome}
              shares={reconcileDrumShares.get(String(exec.tokenId)) || []}
              reconcileLen={reconcileLen}
              setReconcileLength={setReconcileLength}
              otherDrums={reconcileShares.filter(
                d =>
                  d.phase === phase.name &&
                  d.key !== `${productId}::${exec.tokenId}`
              )}
            />
          )}
          <DrumCorrections exec={exec} />
          <div className="text-muted fs-10 mt-2 fst-italic">
            {mergedLockedNote(exec)}
          </div>
        </Card.Body>
      </Card>
    );
  }

  // ── PHANTOM EXECUTION: special locked card ──
  if (isPhantom) {
    return (
      <Card
        className="border-info mb-3 shadow-sm"
        style={{ borderLeft: '5px solid #0dcaf0' }}
      >
        <Card.Header className="bg-info-subtle d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center gap-2">
            <Badge bg="info">PHANTOM</Badge>
            <span className="fw-semibold">
              {Number(exec.plannedLength || 0).toLocaleString()} m
            </span>
            {(exec.phantomColor || exec.color) && (
              <Badge bg="dark">{exec.phantomColor || exec.color}</Badge>
            )}
            <span className="text-muted fs-10 font-monospace">
              {exec.phantomId}
            </span>
          </div>
          <Button
            variant="outline-danger"
            size="sm"
            onClick={() => handleRemoveExecution(conductorIdx, execIdx)}
          >
            Remove
          </Button>
        </Card.Header>
        <Card.Body className="py-2">
          <Row className="g-2">
            <Col md={6}>
              <Form.Group>
                <Form.Label>Machine<ExecWarning exec={exec} product={product} phase={phase} field="machine" /></Form.Label>
                <Form.Control
                  size="sm"
                  value="PHANTOM"
                  readOnly
                  disabled
                  className="bg-light fw-semibold text-info"
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Length (m) — from phantom</Form.Label>
                <Form.Control
                  size="sm"
                  type="number"
                  value={exec.plannedLength || ''}
                  disabled
                  className="bg-light"
                />
              </Form.Group>
            </Col>
          </Row>
          {exec.phantomNote && (
            <div className="text-muted fs-10 mt-2 fst-italic">
              {exec.phantomNote}
            </div>
          )}
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card className="border-info-subtle bg-light mb-3 shadow-sm">
      <Card.Header className="d-flex flex-wrap justify-content-between align-items-center gap-2">
        <div className="d-flex align-items-center gap-2 flex-wrap">
          <span className="fw-semibold">Execution #{execIdx + 1}</span>

          {isFinalDeliveryPhase(phase, product?.phases) && (
            <span className="ms-1">
              {exec.deliveryMode === 'to_coiling' && <Badge bg="primary">Next: Coiling</Badge>}
              {exec.deliveryMode === 'to_rewinding' && <Badge bg="warning">Next: Rewinding</Badge>}
              {exec.deliveryMode === 'coils_on_machine' && <Badge bg="info">Coils On Machine</Badge>}
              {(!exec.deliveryMode || exec.deliveryMode === 'final_product') && <Badge bg="success">Final Product</Badge>}
            </span>
          )}

          {optimal800 && canUse800 && (
            <Button
              size="sm"
              variant="primary"
              onClick={() => applyOptimalLot(800)}
            >
              M800 → {optimal800} m
            </Button>
          )}

          {optimal1000 && canUse1000 && (
            <Button
              size="sm"
              variant="primary"
              onClick={() => applyOptimalLot(1000)}
            >
              M1000 → {optimal1000} m
            </Button>
          )}
        </div>

        <div className="d-flex align-items-center gap-2">
          {estimatedLength ? (
            <>
              <Button
                size="sm"
                variant="success"
                disabled={!estimatedLength > product.length}
                onClick={() => {
                  console.log(
                    'WTF IS THIS ',
                    conductorIdx,
                    estimatedLength,
                    product.length
                  );
                  handleConductorChange(
                    conductorIdx,
                    'execution_plannedLength',
                    product.length,
                    execIdx
                  );
                }}
              >
                Plan
              </Button>
              <Badge bg="success">
                Estimated: {round(estimatedLength, 0)} m
              </Badge>
              {(() => {
                const w =
                  num(exec?.plannedLength) > 0
                    ? computeWeightForExec(
                        phase?.name,
                        num(exec.plannedLength),
                        product,
                        exec
                      )
                    : null;
                return w ? (
                  <Badge bg="dark" className="ms-1">
                    {w.totalKg} kg
                  </Badge>
                ) : null;
              })()}
            </>
          ) : hasDrumSelected ? (
            <Badge bg="secondary">No estimate</Badge>
          ) : null}

          <Button
            variant="outline-danger"
            size="sm"
            onClick={() => {
              flush();
              handleRemoveExecution(conductorIdx, execIdx);
            }}
          >
            Delete
          </Button>
        </div>
      </Card.Header>

      <Card.Body>
        <Row className="g-2">
          <Col lg={6}>
            <Form.Group>
              <Form.Label>Machine<ExecWarning exec={exec} product={product} phase={phase} field="machine" /></Form.Label>
              <Form.Select
                size="sm"
                value={exec.machineId || ''}
                onChange={e =>
                  handleConductorChange(
                    conductorIdx,
                    'execution_machineId',
                    e.target.value,
                    execIdx
                  )
                }
              >
                <option value="">Select machine</option>
                {MACHINE_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>

          <Col lg={6}>
            <Form.Group>
              <Form.Label>Length (m)<ExecWarning exec={exec} product={product} phase={phase} field="length" /></Form.Label>
              <Form.Control
                size="sm"
                type="number"
                inputMode="decimal"
                value={draftLength}
                onChange={e => setDraftLength(e.target.value)}
              />
            </Form.Group>
          </Col>

          {/* WHICH DRUM this execution shares.
              Merge used to pair by POSITION — the Nth execution of a colour in
              one product with the Nth of the same colour in another. That is
              wrong the moment two products need a different number of drums: a
              5x4 split 13 000 + 7 000 had its 13 000 merged with a 3x4's only
              5 000, when the planner wanted the 5 000 on the 7 000.
              Same group number, same drum — across any number of products.
              Left on Auto it numbers 0,1,2… exactly as before. */}
          {mergeEnabled && productCount > 1 && (
            <Col lg={6}>
              <Form.Group>
                <Form.Label>
                  Merge group{' '}
                  <span className="text-700 fs-10">— same group, one drum</span>
                </Form.Label>
                <Form.Select
                  size="sm"
                  value={exec.mergeSolo ? 'solo' : exec.mergeSlot ?? ''}
                  onChange={e =>
                    patchConductorExecution(
                      conductorIdx,
                      execIdx,
                      mergeChoiceToPatch(e.target.value)
                    )
                  }
                >
                  <option value="">Auto (by position)</option>
                  <option value="solo">Alone — never merge</option>
                  {Array.from({ length: 8 }, (_, n) => (
                    <option key={n} value={n}>
                      Group {n + 1}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
          )}

          <Col lg={6}>
            <Form.Group>
              <Form.Label>Drum Material</Form.Label>
              <Form.Select
                size="sm"
                value={exec.drum_material || ''}
                onChange={e =>
                  handleConductorChange(
                    conductorIdx,
                    'execution_drum_material',
                    e.target.value,
                    execIdx
                  )
                }
              >
                <option value="">Select material</option>
                <option value="wood">WOOD</option>
                <option value="metal">METAL</option>
                <option value="plastic">PLASTIC</option>
              </Form.Select>
            </Form.Group>
          </Col>

          <Col lg={6}>
            <Form.Group>
              <Form.Label>Drum Ø (mm)<ExecWarning exec={exec} product={product} phase={phase} field="drum" /></Form.Label>
              <Form.Control
                size="sm"
                type="number"
                step="1"
                value={exec.drum_diameter || ''}
                onChange={e =>
                  handleConductorChange(
                    conductorIdx,
                    'execution_drum_diameter',
                    Number(e.target.value),
                    execIdx
                  )
                }
              />
            </Form.Group>
          </Col>
        </Row>

        <div className="mt-2 small text-muted">
          Wire type: <b>{conductor?.wire?.type || '-'}</b> | Nominal section:{' '}
          <b>{getConductorNominalSection(product, conductor) || '-'}</b> mm²
          {machineMaxDrum ? (
            <>
              {' '}
              | Machine max drum: <b>{machineMaxDrum}</b> mm
            </>
          ) : null}
          {cableDiameter ? (
            <>
              {' '}
              | Cable Ø used for estimate: <b>{round(cableDiameter, 3)}</b> mm
            </>
          ) : null}
        </div>

        {isFinalDeliveryPhase(phase, product?.phases) && (
          <div className="mt-3 d-flex flex-wrap justify-content-end gap-2">
            <Button
              size="sm"
              variant={exec.deliveryMode === 'to_coiling' ? 'primary' : 'outline-primary'}
              onClick={() => {
                if (handleAddCoilingPhase) handleAddCoilingPhase(productId);
                applyExecPatch({ deliveryMode: 'to_coiling', nextPhase: 'coiling' });
              }}
            >
              → Send to Coiling
            </Button>
            <Button
              size="sm"
              variant={exec.deliveryMode === 'to_rewinding' ? 'warning' : 'outline-warning'}
              onClick={() => {
                if (handleAddRewindingPhase) handleAddRewindingPhase(productId);
                applyExecPatch({ deliveryMode: 'to_rewinding', nextPhase: 'rewinding' });
              }}
            >
              → Send to Rewinding
            </Button>
            <Button
              size="sm"
              variant={exec.deliveryMode === 'coils_on_machine' ? 'info' : 'outline-info'}
              onClick={() => applyExecPatch({ deliveryMode: 'coils_on_machine' })}
            >
              → Coils On Machine
            </Button>
            <Button
              size="sm"
              variant={!exec.deliveryMode || exec.deliveryMode === 'final_product' ? 'success' : 'outline-success'}
              onClick={() => applyExecPatch({ deliveryMode: 'final_product', nextPhase: null })}
            >
              → Final Product
            </Button>
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

const PhaseFormStep = ({
  productId,
  phase,
  productCount = 1,
  // Merge Execution is ON for this phase. Only then does choosing a drum group
  // mean anything — see ConductorExecutionRow.
  mergeEnabled = false,
  handleAddCoilingPhase,
  onChange,
  product,
  selectedUpstream,
  handleAddRewindingPhase,
  updateSelectedUpstream,
  getDrumPreset,
  setDrumPresetMaterial,
  setDrumPresetDiameter,
  drumLibrary = [],
  reconcileAutoCoiling,
  reconcile = false,
  reconcileTargets = [],
  reconcileSel = {},
  setReconcileSel = () => {},
  moveReconcileDrum = () => {},
  reconcileDrums = [],
  reconcileShares = [],
  reconcileDrumShares = new Map(),
  reconcileLen = {},
  setReconcileLength = () => {},
  onDragTokenChange = () => {},
  swapReconcileDrums = () => {},
  // continue-mode first-phase re-fill: lock conductors already planned (tokenId),
  // leave the unplanned ones (e.g. GREEN-YELLOW) editable.
  lockPlannedConductors = false
}) => {
  const upstreamSelection = selectedUpstream?.[productId]?.[phase.name] || null;
  // Multi-select phases (rope, pre_stranding, conductor_isolation and a flat
  // cable's exterior sheath) park an ARRAY here; the single-drum phases park the
  // drum itself. Keep `currentUpstream` meaning "the one selected drum".
  const currentUpstream = Array.isArray(upstreamSelection)
    ? null
    : upstreamSelection;

  // Flat cable (MYYUP): no rope, so the exterior sheath runs off one drum of
  // every conductor colour at once. Null for every other product/phase.
  const flatSheathRequiredColors = flatSheathColors(phase, product);
  const isFlatSheath = !!flatSheathRequiredColors;
  const flatSheathUpstreams = isFlatSheath && Array.isArray(upstreamSelection)
    ? upstreamSelection
    : [];

  // Reconcile mode: a "Belongs to product" swap control (+ Relaunch) rendered on
  // every produced/locked drum of this phase so it can be re-attributed to another
  // product. Guarded by the `reconcile` flag → invisible in create/edit.
  const [dragOverConductor, setDragOverConductor] = useState(null);
  const reconcileHome = String(product._id || product._parentJobId || '');
  const reconcileMovedFromLabel = exec =>
    exec?._reconcileOrigin && String(exec._reconcileOrigin) !== reconcileHome
      ? reconcileTargets.find(t => t.jobId === String(exec._reconcileOrigin))
          ?.label || String(exec._reconcileOrigin)
      : null;
  const renderReconcileSwap = exec => {
    if (!reconcile || !exec?.tokenId) return null;
    const movedFrom = reconcileMovedFromLabel(exec);
    const rediameteredFrom =
      exec._reconcileOriginWireDiameter != null &&
      exec.wireDiameter != null &&
      Number(exec.wireDiameter) !== Number(exec._reconcileOriginWireDiameter)
        ? exec._reconcileOriginWireDiameter
        : null;
    const changed = !!(movedFrom || rediameteredFrom);
    return (
      <Row className="g-2 mt-2 pt-2 border-top">
        <Col md={8}>
          <Form.Group>
            <Form.Label className="fs-10 fw-semibold text-primary mb-1">
              Belongs to product{' '}
              {!exec._merged && (
                <span className="text-body-tertiary fw-normal">
                  (or drag the card)
                </span>
              )}
            </Form.Label>
            <div className="d-flex gap-2 align-items-center">
              <Form.Select
                size="sm"
                value={reconcileHome}
                onChange={e =>
                  moveReconcileDrum(
                    exec.tokenId,
                    e.target.value,
                    null,
                    null,
                    reconcileHome
                  )
                }
              >
                {reconcileTargets.map(t => (
                  <option key={t.jobId} value={t.jobId}>
                    {t.label}
                  </option>
                ))}
              </Form.Select>
              {changed && (
                <Button
                  size="sm"
                  variant="outline-secondary"
                  className="fs-10 flex-shrink-0"
                  title="Restore original product and wire Ø"
                  onClick={() =>
                    moveReconcileDrum(
                      exec.tokenId,
                      String(exec._reconcileOrigin || reconcileHome),
                      null,
                      exec._reconcileOriginWireDiameter ?? null
                    )
                  }
                >
                  Undo
                </Button>
              )}
            </div>
          </Form.Group>
        </Col>
        <Col md={4} className="d-flex align-items-end">
          <Form.Check
            type="checkbox"
            id={`reconcile-relaunch-${exec.tokenId}`}
            label="Relaunch"
            checked={!!reconcileSel[exec.tokenId]?.relaunch}
            onChange={e =>
              setReconcileSel(m => ({
                ...m,
                [exec.tokenId]: {
                  ...(m[exec.tokenId] || {}),
                  relaunch: e.target.checked
                }
              }))
            }
          />
        </Col>
        {exec._merged && (
          <Col md={12}>
            <div className="text-body-tertiary fs-10 fst-italic">
              Merged drum (shared token) — pinned; re-attribute it from its own
              product.
            </div>
          </Col>
        )}
      </Row>
    );
  };

  // Produced/locked drum card for ANY non-conductor phase (unwinding, pre_stranding,
  // rope, sheath, coiling, rewinding), reconcile-aware: draggable, "moved from" badge,
  // and the "Belongs to product" + Relaunch control. Single source so every phase can
  // opt in with one line: `if (exec._locked) return renderReconcileLockedCard(exec, idx);`
  const renderReconcileLockedCard = (exec, idx) => {
    const movedFrom = reconcile ? reconcileMovedFromLabel(exec) : null;
    const rediameteredFrom =
      reconcile &&
      exec._reconcileOriginWireDiameter != null &&
      exec.wireDiameter != null &&
      Number(exec.wireDiameter) !== Number(exec._reconcileOriginWireDiameter)
        ? exec._reconcileOriginWireDiameter
        : null;
    const changed = !!(movedFrom || rediameteredFrom);
    const movable = reconcile && !!exec.tokenId;
    return (
      <Card
        className={`mb-2 ${changed ? 'border-warning' : 'border-success'}`}
        key={exec.id || idx}
        style={{
          borderLeft: `5px solid ${changed ? '#ffc107' : '#198754'}`,
          opacity: 0.9,
          cursor: movable ? 'grab' : 'default'
        }}
        draggable={false}
      >
        <Card.Header className="bg-success-subtle d-flex align-items-center gap-2 flex-wrap">
          {movable && (
            <span
              draggable
              onDragStart={e =>
                e.dataTransfer.setData(
                  'text/plain',
                  `${String(exec.tokenId)}|${reconcileHome}`
                )
              }
              title="Drag this drum to another product or conductor"
              className="px-1 text-700"
              style={{ cursor: 'grab', userSelect: 'none', lineHeight: 1 }}
            >
              ⠿
            </span>
          )}
          <Badge bg={exec._merged ? 'info' : 'success'}>
            {exec._merged ? 'MERGED' : 'PRODUCED'}
          </Badge>
          <span className="fw-semibold">
            {Number(
              exec._producedLength || exec.plannedLength || 0
            ).toLocaleString()}{' '}
            m
          </span>
          {exec.color && <Badge bg="dark">{exec.color}</Badge>}
          {exec.wireDiameter != null && (
            <Badge bg="secondary">Ø{exec.wireDiameter}</Badge>
          )}
          {exec.machineId && <Badge bg="dark">{exec.machineId}</Badge>}
          {rediameteredFrom && (
            <Badge bg="warning" text="dark" className="fs-10">
              was Ø{rediameteredFrom}
            </Badge>
          )}
          {movedFrom && (
            <Badge bg="warning" text="dark" className="fs-10 ms-auto">
              moved from {movedFrom}
            </Badge>
          )}
        </Card.Header>
        <Card.Body className="py-2">
          <div className="text-muted fs-10 fst-italic">
            {mergedLockedNote(exec)}
          </div>
          {renderReconcileSwap(exec)}
        </Card.Body>
      </Card>
    );
  };

  // Editing THIS launched phase: inputs are inherited from the existing tasks by
  // the backend, so add/split works without re-selecting/being-blocked-by upstream.
  const isEditPhase =
    !!product?._editMode && product?._editPhaseName === phase.name;

  // Fully-allocated guard (continue mode): if the SELECTED upstream inputs have no
  // remaining plannable length left (already reserved/consumed by an existing
  // task), block "Add execution" — the inputs are fully committed, so a new
  // execution would just duplicate/over-consume them (e.g. re-entering on a phase
  // whose upstream is already spoken for). Doesn't gate create-new or when nothing
  // is selected; a not-yet-produced (plan-ahead) input has reserved 0 → allowed.
  const upstreamFullyAllocated = (() => {
    if (!product?._continueMode) return false;
    const sel = selectedUpstream?.[productId]?.[phase.name];
    const list = Array.isArray(sel) ? sel : sel ? [sel] : [];
    if (!list.length) return false;
    const existing = product._existingTokens || {};
    // local coercion — PhaseFormStep has its own block-scoped `num` declared
    // later, so referencing it here would hit the temporal dead zone.
    const toN = v => (Number.isFinite(Number(v)) ? Number(v) : 0);
    const remainingOf = u => {
      const cap = toN(u.plannedLength) || toN(u.plannedLengthInput) || 0;
      const tok = u.tokenId ? existing[u.tokenId] : null;
      const used = tok
        ? Math.max(toN(tok.reservedLength), toN(tok.consumedLength))
        : 0;
      return Math.max(0, cap - used);
    };

    // Flat cable: a sheath metre costs a metre of EVERY colour, so what is still
    // plannable is the shortest colour — not the sum of all the drums. Summing
    // would keep "Add execution" enabled on a selection that has brown left but
    // no blue, and the length allocator would then reject every value typed.
    // Executions already added in THIS wizard session count too: they hold no
    // reservation yet, but their metres are just as spoken for.
    if (isFlatSheath) {
      const planned = buildConsumedMapForPhase(phase);
      const byColor = {};
      for (const u of list) {
        if (!u?.color) continue;
        const cap = toN(u.plannedLength) || toN(u.plannedLengthInput) || 0;
        const left = Math.min(
          remainingOf(u),
          Math.max(0, cap - toN(planned[u.id]))
        );
        byColor[u.color] = (byColor[u.color] || 0) + left;
      }
      const perColor = flatSheathRequiredColors.map(c => byColor[c] || 0);
      return !perColor.length || Math.min(...perColor) <= 0.0001;
    }

    const remaining = list.reduce((s, u) => s + remainingOf(u), 0);
    return remaining <= 0.0001;
  })();

  useEffect(() => {
    updateSelectedUpstream(productId, phase.name, null);
  }, [phase.name]);

  const normalizeForProgress = exec => ({
    ...exec,
    plannedLength: Number(exec.plannedLengthInput ?? exec.plannedLength ?? 0)
  });

  const getPhaseProgressConfig = (phase, product) => {
    switch (phase.name) {
      case 'unwinding':
        return {
          targetLength:
            phase.execution.reduce(
              (sum, exec) => sum + (exec.wireCount || 0) * product.length,
              0
            ) * product.numberOfConductors,
          executionSource: phase.execution
        };
      case 'pre_stranding':
        if (Array.isArray(phase.rope)) {
          return {
            targetLength: product.length * (product.numberOfConductors + 1),
            executionSource: phase.execution
          };
        }

        return {
          targetLength: product.length * product.numberOfConductors,
          executionSource: phase.execution
        };
      case 'conductor_isolation':
        return {
          targetLength: product.length * phase.conductors.length,
          executionSource: phase.conductors
            .flatMap(c => c.execution)
            .map(normalizeForProgress)
        };

      case 'rope':
      case 'interior_sheat':
      case 'exterior_sheat':
      case 'armouring':
        return {
          targetLength: product.length,
          executionSource: phase.execution
        };

      case 'coiling':
        return {
          targetLength: product.length,
          executionSource: phase.execution
        };

      default:
        return {
          targetLength: product.length,
          executionSource: phase.execution
        };
    }
  };

  const { targetLength, executionSource } = getPhaseProgressConfig(
    phase,
    product
  );

  const handleRopePlannedLengthChange = (execIdx, value) => {
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue) || numericValue <= 0) return;

    const upstreams = selectedUpstream?.[productId]?.[phase.name] || [];
    const requiredColors = getRequiredRopeColors(product);

    const consumed = buildConsumedMapForPhase(phase, {
      excludeExecutionId: phase.execution?.[execIdx]?.id
    });

    const { ok, fromUpstreams } = allocateRopeFlowByColor(
      upstreams,
      consumed,
      numericValue,
      requiredColors
    );

    if (!ok) {
      alert('Not enough remaining length in upstream drums');
      return;
    }

    const executions = [...phase.execution];
    const exec = { ...executions[execIdx] };

    exec.plannedLength = numericValue;
    exec.fromUpstreams = fromUpstreams;
    exec.fromUpstreamIds = [...new Set(fromUpstreams.map(u => u.id))];
    exec.fromUpstreamColors = requiredColors;

    executions[execIdx] = exec;
    onChange(productId, phase.name, 'execution', executions);
  };

  // Metres of flat-cable sheath still plannable: the colour with the least cable
  // left behind it. `excludeExecutionId` leaves one execution's own take out, so
  // the card being edited measures against the length it would give back.
  const flatSheathRemainingLength = (excludeExecutionId = undefined) => {
    if (!isFlatSheath) return 0;

    const consumed = buildConsumedMapForPhase(phase, { excludeExecutionId });
    const byColor = {};
    for (const u of flatSheathUpstreams) {
      if (!u?.color) continue;
      const left = Math.max(
        0,
        (Number(u.plannedLength) || 0) - (Number(consumed[u.id]) || 0)
      );
      byColor[u.color] = (byColor[u.color] || 0) + left;
    }

    const perColor = flatSheathRequiredColors.map(c => byColor[c] || 0);
    return perColor.length ? Math.min(...perColor) : 0;
  };

  // Flat cable (MYYUP): the exterior sheath is extruded over the bare cores, so
  // one run eats `value` metres of EVERY conductor colour — the same allocation
  // rope does, only the output is a sheathed cable instead of a strand. Each
  // colour is spread over the selected drums FIFO, so a colour can be spliced
  // from several drums (3×10 km blue against one 20 km + one 10 km brown).
  const handleFlatSheathPlannedLengthChange = (execIdx, value) => {
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue) || numericValue <= 0) return;

    const consumed = buildConsumedMapForPhase(phase, {
      excludeExecutionId: phase.execution?.[execIdx]?.id
    });

    const { ok, fromUpstreams } = allocateRopeFlowByColor(
      flatSheathUpstreams,
      consumed,
      numericValue,
      flatSheathRequiredColors
    );

    if (!ok) {
      alert('Not enough remaining length in the selected conductor drums');
      return;
    }

    const executions = [...phase.execution];
    const exec = { ...executions[execIdx] };

    exec.plannedLength = numericValue;
    exec.fromUpstreams = fromUpstreams;
    exec.fromUpstreamIds = [...new Set(fromUpstreams.map(u => u.id))];
    exec.fromUpstreamColors = flatSheathRequiredColors;

    executions[execIdx] = exec;
    onChange(productId, phase.name, 'execution', executions);
  };

  // pre_stranding flow allocation — mirrors rope's flow, but distributes by wire
  // DIAMETER (need per Ø = wiresOfThisØ × ropeLength). This lets a single wire be
  // fed by several drums (e.g. a short real drum + a phantom remainder), so the
  // phantom lands in fromUpstreamIds → task.inputs → operator HMI, and no drum is
  // ever billed more than it physically holds.
  const allocatePreStrandFlowByDiameter = (
    upstreams,
    consumedMap,
    lengthPerWire,
    requiredMap
  ) => {
    // remaining length per drum + accumulated take (one aggregated entry per drum)
    const remainingById = {};
    for (const u of upstreams || []) {
      const cap = num(u.plannedLength) || num(u.plannedLengthInput) || 0;
      remainingById[u.id] = Math.max(0, cap - (num(consumedMap[u.id]) || 0));
    }
    const takeById = {};
    const addTake = (id, take) => {
      takeById[id] = (takeById[id] || 0) + take;
    };

    for (const [diamStr, qty] of Object.entries(requiredMap || {})) {
      const diam = Number(diamStr);
      const count = Number(qty) || 0;
      if (count <= 0) continue;

      const cand = (upstreams || [])
        .filter(u => Number(u.wireDiameter) === diam)
        .sort((a, b) => Number(a.id) - Number(b.id));

      let wiresLeft = count;

      // PASS 1 — spread ONE full wire (lengthPerWire) per drum, round-robin. Each
      // wire on a stranding line runs off its OWN drum, so distribute wires across
      // the drums evenly instead of draining one drum before touching the next.
      let progressed = true;
      while (wiresLeft > 0 && progressed) {
        progressed = false;
        for (const u of cand) {
          if (wiresLeft <= 0) break;
          if (remainingById[u.id] >= lengthPerWire - 0.001) {
            addTake(u.id, lengthPerWire);
            remainingById[u.id] -= lengthPerWire;
            wiresLeft--;
            progressed = true;
          }
        }
      }

      // PASS 2 — any wire no single drum can still fully feed: combine leftovers
      // (short real drum + … + phantom remainder), one wire at a time.
      while (wiresLeft > 0) {
        let needWire = lengthPerWire;
        let took = false;
        for (const u of cand) {
          if (needWire <= 0.001) break;
          const rem = remainingById[u.id];
          if (rem <= 0.001) continue;
          const take = Math.min(rem, needWire);
          addTake(u.id, take);
          remainingById[u.id] -= take;
          needWire -= take;
          took = true;
        }
        if (!took || needWire > 0.001) {
          return {
            ok: false,
            fromUpstreams: [],
            shortDiameter: diam,
            shortBy: needWire + (wiresLeft - 1) * lengthPerWire
          };
        }
        wiresLeft--;
      }
    }

    const result = [];
    for (const u of upstreams || []) {
      const take = takeById[u.id];
      if (take > 0.001) result.push({ id: u.id, take, diameter: Number(u.wireDiameter) });
    }
    return { ok: true, fromUpstreams: result };
  };

  const handlePreStrandPlannedLengthChange = (execIdx, value) => {
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue) || numericValue <= 0) return;

    // No upstream dependency → pre_stranding is launched DIRECTLY (no unwinding,
    // no incoming drums to consume). There's nothing to flow-allocate, so just
    // set the length like before. Flow allocation (combining short drum +
    // phantom) only applies when this phase depends on an upstream phase.
    if (!phase.dependsOn) {
      handleGenericExecutionChange(execIdx, 'plannedLength', numericValue);
      return;
    }

    const upRaw = selectedUpstream?.[productId]?.[phase.name];
    const upstreams = Array.isArray(upRaw) ? upRaw : upRaw ? [upRaw] : [];
    const requiredMap = buildRequiredMap(phase.wireDiameter, phase.numberOfWires);

    const consumed = buildConsumedMapForPhase(phase, {
      excludeExecutionId: phase.execution?.[execIdx]?.id
    });

    const { ok, fromUpstreams, shortDiameter, shortBy } =
      allocatePreStrandFlowByDiameter(
        upstreams,
        consumed,
        numericValue,
        requiredMap
      );

    if (!ok) {
      alert(
        `Not enough remaining length for Ø${shortDiameter} (short by ` +
          `${Math.round(shortBy)} m). Select more upstream drums of this diameter.`
      );
      return;
    }

    const executions = [...phase.execution];
    const exec = { ...executions[execIdx] };

    exec.plannedLength = numericValue;
    exec.fromUpstreams = fromUpstreams;
    exec.fromUpstreamIds = [...new Set(fromUpstreams.map(u => u.id))];

    executions[execIdx] = exec;
    onChange(productId, phase.name, 'execution', executions);
  };
  // const setExteriorExecutionRoute = (idx, mode) => {
  //   if (mode === 'to_coiling') {
  //     createCoilingPhaseIfMissing();
  //     handleGenericExecutionChange(idx, 'deliveryMode', 'to_coiling');
  //     handleGenericExecutionChange(idx, 'nextPhase', 'coiling');
  //     return;
  //   }

  //   if (mode === 'to_rewinding') {
  //     createRewindingPhaseIfMissing();
  //     handleGenericExecutionChange(idx, 'deliveryMode', 'to_rewinding');
  //     handleGenericExecutionChange(idx, 'nextPhase', 'rewinding');
  //     return;
  //   }

  //   if (mode === 'coils_on_machine') {
  //     handleGenericExecutionChange(idx, 'deliveryMode', 'coils_on_machine');
  //     handleGenericExecutionChange(idx, 'nextPhase', null);
  //     return;
  //   }

  //   handleGenericExecutionChange(idx, 'deliveryMode', 'final_product');
  //   handleGenericExecutionChange(idx, 'nextPhase', null);
  // };
  // const handleGenericExecutionChange = (index, field, value) => {
  //   const updatedExec = [...phase.execution];
  //   const exec = { ...updatedExec[index] };

  //   // 🔥 fix critic — garantăm că exec-ul are upstream setat,
  //   // chiar dacă React pierde referința la primul render
  //   if (!exec.fromUpstreamId && currentUpstream) {
  //     exec.fromUpstreamId = currentUpstream.id;
  //     exec.fromUpstreamLength = currentUpstream.plannedLength;
  //     exec.fromUpstreamColor = currentUpstream.color || null;
  //   }

  //   exec[field] = value;
  //   updatedExec[index] = exec;

  //   // if (phase.name === 'rope') {
  //   //   const upstreams = selectedUpstream?.[productId]?.[phase.name] || [];

  //   //   if (!exec.fromUpstreamIds && upstreams.length) {
  //   //     exec.fromUpstreamIds = upstreams.map(u => u.id);
  //   //     exec.fromUpstreamColors = upstreams.map(u => u.color).filter(Boolean);
  //   //   }
  //   // }

  //   onChange(productId, phase.name, 'execution', updatedExec);
  // };

  const setExteriorExecutionRoute = (idx, mode) => {
    updateExecutionAt(idx, prev => {
      const next = { ...prev };

      if (mode === 'to_coiling') {
        createCoilingPhaseIfMissing();
        next.deliveryMode = 'to_coiling';
        next.nextPhase = 'coiling';

        if (next.plannedLength === '' || next.plannedLength == null) {
          next.plannedLength = 0;
        }

        delete next.coilCount;
        delete next.coilLength;
        delete next.packaging;

        return next;
      }

      if (mode === 'to_rewinding') {
        createRewindingPhaseIfMissing();
        next.deliveryMode = 'to_rewinding';
        next.nextPhase = 'rewinding';

        if (next.plannedLength === '' || next.plannedLength == null) {
          next.plannedLength = 0;
        }

        delete next.coilCount;
        delete next.coilLength;
        delete next.packaging;

        return next;
      }

      if (mode === 'coils_on_machine') {
        next.deliveryMode = 'coils_on_machine';
        next.nextPhase = null;

        next.coilCount = Number(next.coilCount) || 0;
        next.coilLength = Number(next.coilLength) || 0;
        next.packaging = next.packaging || '';

        next.plannedLength = '';
        next.drum_material = '';
        next.drum_diameter = '';

        // Flat cable: the drum length this execution had reserved on the
        // conductors is gone — release it until the coil figures re-cut it.
        if (isFlatSheath) {
          next.fromUpstreams = [];
          next.fromUpstreamIds = [];
        }

        return next;
      }

      next.deliveryMode = 'final_product';
      next.nextPhase = null;

      if (next.plannedLength === '' || next.plannedLength == null) {
        next.plannedLength = 0;
      }

      delete next.coilCount;
      delete next.coilLength;
      delete next.packaging;

      return next;
    });
  };

  const handleGenericExecutionChange = (index, fieldOrPatch, value) => {
    const updatedExec = [...phase.execution];
    const exec = { ...updatedExec[index] };

    // garantăm upstream-ul
    if (!exec.fromUpstreamId && currentUpstream) {
      exec.fromUpstreamId = currentUpstream.id;
      exec.fromUpstreamLength = currentUpstream.plannedLength;
      exec.fromUpstreamColor = currentUpstream.color || null;
    }

    if (
      fieldOrPatch &&
      typeof fieldOrPatch === 'object' &&
      !Array.isArray(fieldOrPatch)
    ) {
      Object.assign(exec, fieldOrPatch);
    } else {
      exec[fieldOrPatch] = value;
    }

    // Flat cable: coils made straight on the line carry no plannedLength — their
    // run is coilCount × coilLength — so re-cut the conductor drums whenever
    // either changes, or the incoming cards keep showing metres this execution
    // already spoke for.
    if (
      isFlatSheath &&
      flatSheathUpstreams.length > 0 &&
      (fieldOrPatch === 'coilCount' || fieldOrPatch === 'coilLength')
    ) {
      const total = getExecTotalLength(phase.name, exec);
      const consumedMap = buildConsumedMapForPhase(phase, {
        excludeExecutionId: exec.id
      });

      const { ok, fromUpstreams } = allocateRopeFlowByColor(
        flatSheathUpstreams,
        consumedMap,
        total,
        flatSheathRequiredColors
      );

      if (!ok) {
        alert('Not enough remaining length in the selected conductor drums');
        return;
      }

      exec.fromUpstreams = fromUpstreams;
      exec.fromUpstreamIds = [...new Set(fromUpstreams.map(u => u.id))];
      exec.fromUpstreamColors = flatSheathRequiredColors;
    }

    updatedExec[index] = exec;
    onChange(productId, phase.name, 'execution', updatedExec);
  };
  const updateExecutionAt = (idx, updater) => {
    const updatedExec = [...phase.execution];
    const prev = { ...(updatedExec[idx] || {}) };
    const next = updater(prev);

    // păstrăm upstream-ul dacă lipsește
    if (!next.fromUpstreamId && currentUpstream) {
      next.fromUpstreamId = currentUpstream.id;
      next.fromUpstreamLength = currentUpstream.plannedLength;
      next.fromUpstreamColor = currentUpstream.color || null;
    }

    updatedExec[idx] = next;
    onChange(productId, phase.name, 'execution', updatedExec);
  };

  const handleConductorChange = (condIdx, key, value, execIdx = null) => {
    const updatedConductors = [...phase.conductors];
    const targetConductor = { ...updatedConductors[condIdx] };

    if (key.startsWith('execution_') && execIdx !== null) {
      const execKey = key.replace('execution_', '');
      const executions = [...(targetConductor.execution || [])];
      const exec = { ...executions[execIdx] };

      if (execKey === 'plannedLength') {
        const numericValue = Number(value);
        if (!Number.isFinite(numericValue) || numericValue <= 0) return;

        if (phase.name === 'conductor_isolation' && phase.dependsOn) {
          const consumed = buildConsumedMapForPhase(phase, {
            excludeExecutionId: exec.id
          });

          const upstreams = selectedUpstream?.[productId]?.[phase.name] || [];

          const { ok, fromUpstreams } = allocateConductorFlow(
            upstreams,
            consumed,
            numericValue
          );

          if (!ok) {
            alert('Not enough remaining length in upstream drums');
            return;
          }

          exec.fromUpstreams = fromUpstreams;
          exec.fromUpstreamIds = fromUpstreams.map(u => u.id);
        }

        exec.plannedLength = numericValue;
      } else {
        exec[execKey] = value;
      }

      executions[execIdx] = exec;
      targetConductor.execution = executions;
      updatedConductors[condIdx] = targetConductor;

      onChange(productId, phase.name, 'conductors', updatedConductors);
      return;
    }

    const [group, field] = key.split('_');

    if (group === 'wire') {
      targetConductor.wire = {
        ...targetConductor.wire,
        [field]: parseFloat(value)
      };
    }

    if (group === 'isolation') {
      targetConductor.isolation = {
        ...targetConductor.isolation,
        [field]: parseFloat(value)
      };
    }

    updatedConductors[condIdx] = targetConductor;
    onChange(productId, phase.name, 'conductors', updatedConductors);
  };

  const patchConductorExecution = (condIdx, execIdx, patch) => {
    const updatedConductors = [...phase.conductors];
    const targetConductor = { ...updatedConductors[condIdx] };
    const executions = [...(targetConductor.execution || [])];
    const exec = { ...executions[execIdx], ...patch };

    if (patch.plannedLength != null) {
      const numericValue = Number(patch.plannedLength);

      if (!Number.isFinite(numericValue) || numericValue <= 0) return;

      if (phase.name === 'conductor_isolation' && phase.dependsOn) {
        const consumed = buildConsumedMapForPhase(phase, {
          excludeExecutionId: exec.id
        });

        const upstreams = selectedUpstream?.[productId]?.[phase.name] || [];

        const { ok, fromUpstreams } = allocateConductorFlow(
          upstreams,
          consumed,
          numericValue
        );

        if (!ok) {
          alert('Not enough remaining length in upstream drums');
          return;
        }

        exec.fromUpstreams = fromUpstreams;
        exec.fromUpstreamIds = fromUpstreams.map(u => u.id);
      }

      exec.plannedLength = numericValue;
    }

    executions[execIdx] = exec;
    targetConductor.execution = executions;
    updatedConductors[condIdx] = targetConductor;

    onChange(productId, phase.name, 'conductors', updatedConductors);
  };

  const getExecTotalCoilLength = exec => {
    const coils = Number(exec?.coilCount) || 0;
    const len = Number(exec?.coilLength) || 0;
    return coils * len;
  };
  const renderCoilsOnMachineExecutionBody = (exec, idx, drumCap) => {
    const totalLength = getExecTotalCoilLength(exec);

    return (
      <>
        <Row className="g-2">
          <Col md={6}>
            <Form.Group>
              <Form.Label>Machine<ExecWarning exec={exec} product={product} phase={phase} field="machine" /></Form.Label>
              <Form.Select
                value={exec.machineId || ''}
                onChange={e =>
                  handleGenericExecutionChange(idx, 'machineId', e.target.value)
                }
              >
                <option value="">Select</option>
                {MACHINE_OPTIONS.map(m => (
                  <option key={m.value} value={m.value}>
                    {m.label}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>

          <Col md={6}>
            <Form.Group>
              <Form.Label>Coil Count</Form.Label>
              <Form.Control
                type="number"
                value={exec.coilCount || 0}
                onChange={e =>
                  handleGenericExecutionChange(
                    idx,
                    'coilCount',
                    Number(e.target.value)
                  )
                }
              />
            </Form.Group>
          </Col>

          <Col md={6}>
            <Form.Group>
              <Form.Label>Coil Length</Form.Label>
              <Form.Control
                type="number"
                value={exec.coilLength || 0}
                onChange={e =>
                  handleGenericExecutionChange(
                    idx,
                    'coilLength',
                    Number(e.target.value)
                  )
                }
              />
            </Form.Group>
          </Col>

          <Col md={6}>
            <Form.Group>
              <Form.Label>Packaging</Form.Label>
              <Form.Control
                type="text"
                value={exec.packaging || ''}
                onChange={e =>
                  handleGenericExecutionChange(idx, 'packaging', e.target.value)
                }
              />
            </Form.Group>
          </Col>
        </Row>

        <div className="mt-3">
          <ProgressTracker
            compact
            targetLength={drumCap}
            executionSource={[{ plannedLength: totalLength || 0 }]}
          />
        </div>
      </>
    );
  };
  const handleAddExecution = (conductorIndex = null) => {
    const needsUpstream = [
      'interior_sheat',
      'armouring',
      'exterior_sheat',
      'coiling',
      'rewinding'
    ];

    // A flat cable's exterior sheath picks a drum per colour instead of one
    // drum, so it is gated below like rope, not by the single-drum check.
    const mustSelect = needsUpstream.includes(phase.name) && !isFlatSheath;

    if (mustSelect && !currentUpstream && !isEditPhase) {
      alert('Select a drum from previous phase first.');
      return;
    }

    const consumedLengths = {};

    if (phase.name === 'conductor_isolation' && phase.conductors) {
      phase.conductors.forEach(c =>
        c.execution?.forEach(exec => {
          const len = Number(exec.plannedLength) || 0;
          exec.fromUpstreamIds?.forEach(id => {
            consumedLengths[id] = (consumedLengths[id] || 0) + len;
          });
        })
      );
    }

    const itemKey =
      phase.name === 'conductor_isolation' && conductorIndex != null
        ? `conductor-${conductorIndex}`
        : 'default';

    const preset = getDrumPreset(productId, phase.name, itemKey);

    const newExec = {
      id: Date.now(),
      machineId: '',
      plannedLength: '',
      drum_material: String(preset?.material || '').toLowerCase(),
      drum_diameter: preset?.diameter || '',
      deliveryMode:
        isFinalDeliveryPhase(phase, product?.phases) ? 'final_product' : undefined,
      nextPhase: isFinalDeliveryPhase(phase, product?.phases) ? null : undefined
    };

    if (phase.name === 'coiling') {
      newExec.coilCount = 0;
      newExec.coilLength = 0;
      newExec.packaging = '';
    }

    if (mustSelect && currentUpstream) {
      newExec.fromUpstreamId = currentUpstream.id;
      newExec.fromUpstreamLength = currentUpstream.plannedLength;
      newExec.fromUpstreamColor = currentUpstream.color || null;
    }

    if (phase.name === 'rope') {
      const upstreams = selectedUpstream?.[productId]?.[phase.name] || [];

      if (!upstreams.length && !isEditPhase) {
        alert('Select at least one conductor drum first.');
        return;
      }

      // In edit mode the backend inherits the phase's inputs, so a new drum can be
      // added without re-selecting conductors.
      newExec.fromUpstreamIds = upstreams.map(u => u.id);
      newExec.fromUpstreamColors = upstreams.map(u => u.color).filter(Boolean);
    }

    if (isFlatSheath) {
      const missing = flatSheathRequiredColors.filter(
        c => !flatSheathUpstreams.some(u => u.color === c)
      );

      if (missing.length && !isEditPhase) {
        alert(
          `Select an incoming drum for every conductor colour first — missing: ${missing.join(
            ', '
          )}.`
        );
        return;
      }

      // In edit mode nothing is selected — the backend inherits the launched
      // phase's inputs — so leave the upstream refs off entirely.
      if (flatSheathUpstreams.length) {
        newExec.fromUpstreamIds = flatSheathUpstreams.map(u => u.id);
        newExec.fromUpstreamColors = flatSheathUpstreams
          .map(u => u.color)
          .filter(Boolean);
      }
    }

    if (Array.isArray(phase.conductors)) {
      const updated = [...phase.conductors];
      const target = { ...updated[conductorIndex] };

      target.execution = [...(target.execution || []), newExec];
      updated[conductorIndex] = target;

      onChange(productId, phase.name, 'conductors', updated);
      return;
    }

    onChange(productId, phase.name, 'execution', [
      ...(phase.execution || []),
      newExec
    ]);
  };

  const handleRemoveExecution = (conductorIndex = null, execIdx) => {
    if (Array.isArray(phase.conductors)) {
      // cazul conductor_isolation
      const updatedConductors = [...phase.conductors];
      const targetConductor = { ...updatedConductors[conductorIndex] };

      targetConductor.execution = targetConductor.execution.filter(
        (_, idx) => idx !== execIdx
      );
      updatedConductors[conductorIndex] = targetConductor;

      onChange(productId, phase.name, 'conductors', updatedConductors);
    } else {
      // cazul generic (rope, sheath, etc.)
      const updatedExecution = phase.execution.filter(
        (_, idx) => idx !== execIdx
      );

      onChange(productId, phase.name, 'execution', updatedExecution);

      // Removing an exterior coil-routed exec must also drop its auto-created
      // coiling run (revert), so the leftover drum frees up again.
      if (phase.name === 'exterior_sheat') {
        reconcileAutoCoiling?.(productId);
      }
    }
  };

  const createCoilingPhaseIfMissing = () => {
    const hasCoiling = product.phases.some(p => p.name === 'coiling');

    if (hasCoiling) {
      return product.phases.find(p => p.name === 'coiling');
    }

    handleAddCoilingPhase(productId);
  };

  const createRewindingPhaseIfMissing = () => {
    const hasRewinding = product.phases.some(p => p.name === 'rewinding');

    if (hasRewinding) {
      return product.phases.find(p => p.name === 'rewinding');
    }

    handleAddRewindingPhase(productId);
  };

  const handleGenericPhaseChange = (key, value) => {
    onChange(productId, phase.name, key, value);
  };

  const renderGenericFields = () => {
    const needsUpstream = ['interior_sheat', 'armouring', 'exterior_sheat'];
    const mustSelect = needsUpstream.includes(phase.name) && !isFlatSheath;
    // In continue mode, don't block — upstream data may not be fully reconstructed
    const isContinueMode = product?._continueMode;

    const used = currentUpstream
      ? phase.execution
          .filter(e => e.fromUpstreamId === currentUpstream.id)
          .reduce((s, e) => {
            if (
              isFinalDeliveryPhase(phase, product?.phases) &&
              e.deliveryMode === 'coils_on_machine'
            ) {
              return s + getExecTotalCoilLength(e);
            }

            return s + (parseFloat(e.plannedLength) || 0);
          }, 0)
      : 0;

    // ── flat cable (MYYUP): capacity is the bottleneck colour ──
    // Every metre of sheath costs a metre of each colour, so the run can never be
    // longer than the colour with the least cable behind it.
    const flatColorsSelected = isFlatSheath
      ? [...new Set(flatSheathUpstreams.map(u => u.color).filter(Boolean))]
      : [];
    const flatMissingColors = isFlatSheath
      ? flatSheathRequiredColors.filter(c => !flatColorsSelected.includes(c))
      : [];
    const flatCap = isFlatSheath
      ? (() => {
          const byColor = {};
          for (const u of flatSheathUpstreams) {
            if (!u?.color) continue;
            byColor[u.color] =
              (byColor[u.color] || 0) + (Number(u.plannedLength) || 0);
          }
          const perColor = flatSheathRequiredColors.map(c => byColor[c] || 0);
          return perColor.length ? Math.min(...perColor) : 0;
        })()
      : 0;
    const flatRemaining = flatSheathRemainingLength();

    const drumCap = isFlatSheath ? flatCap : currentUpstream?.plannedLength || 0;
    const consumed = currentUpstream && used >= drumCap;
    // Edit-mode DOWNSTREAM phase (re-planning against the just-edited drums): there
    // is no pre-selected upstream like plan-next-phase, so enforce the selection
    // here too (continue mode otherwise skips this guard). The edited phase itself
    // (isEditPhase) and real continue mode keep the relaxed behaviour.
    const isEditDownstreamPhase =
      !!product?._editMode && product?._editPhaseName !== phase.name;
    // Editing the launched phase re-uses its own inputs, so there is nothing to
    // pick and nothing to re-allocate — the plain length field applies there.
    const flatSheathPlanning = isFlatSheath && !isEditPhase;

    const isBlocked = isFlatSheath
      ? // flat cable: a run is only possible once every colour has a drum behind
        // it, in continue mode too — that IS the constraint being planned here.
        flatSheathPlanning && flatMissingColors.length > 0
      : (!isContinueMode || isEditDownstreamPhase) &&
        mustSelect &&
        (!currentUpstream || consumed);

    const itemKey = 'default';
    const preset = getDrumPreset(productId, phase.name, itemKey);

    return (
      <>
        {!reconcile && (
        <Row className="g-3 mb-3">
          {Object.entries(phase)
            .filter(
              ([key]) => !['name', 'execution', 'dependsOn'].includes(key)
            )
            .map(([key, value]) => {
              const isNumber = typeof value === 'number';

              return (
                <Col md={6} key={key}>
                  <Form.Group controlId={`${phase.name}-${key}`}>
                    <Form.Label className="text-capitalize">
                      {key.replaceAll('_', ' ')}
                    </Form.Label>

                    {isNumber ? (
                      <Form.Control
                        type="number"
                        step="0.01"
                        value={value}
                        onChange={e =>
                          handleGenericPhaseChange(
                            key,
                            parseFloat(e.target.value)
                          )
                        }
                      />
                    ) : (
                      <Form.Control
                        type="text"
                        value={value}
                        disabled
                        readOnly
                        className="bg-light"
                      />
                    )}
                  </Form.Group>
                </Col>
              );
            })}
        </Row>
        )}

        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
          <h6 className="fw-semibold mb-0">Execution Plan</h6>

          <HeaderProgressSlot>
            {isFlatSheath && flatSheathUpstreams.length > 0 && (
              <ProgressTracker
                targetLength={flatCap}
                executionSource={phase.execution.map(exec => ({
                  plannedLength: getExecTotalLength(phase.name, exec)
                }))}
              />
            )}

            {!isFlatSheath &&
              currentUpstream &&
              phase.name !== 'coiling' &&
              phase.name !== 'rewinding' && (
                <ProgressTracker
                  targetLength={drumCap}
                  executionSource={phase.execution
                    .filter(exec => exec.fromUpstreamId === currentUpstream.id)
                    .map(exec => {
                      if (
                        isFinalDeliveryPhase(phase, product?.phases) &&
                        exec.deliveryMode === 'coils_on_machine'
                      ) {
                        return {
                          plannedLength: getExecTotalCoilLength(exec)
                        };
                      }

                      return {
                        plannedLength: Number(exec.plannedLength) || 0
                      };
                    })}
                />
              )}
          </HeaderProgressSlot>

          <Button
            variant={
              (isBlocked || upstreamFullyAllocated) && !isEditPhase
                ? 'secondary'
                : 'outline-primary'
            }
            size="sm"
            disabled={(isBlocked || upstreamFullyAllocated) && !isEditPhase}
            title={
              upstreamFullyAllocated && !isEditPhase
                ? 'Selected input is fully allocated — no remaining length to plan'
                : undefined
            }
            onClick={() => handleAddExecution()}
          >
            + Add Execution
          </Button>
        </div>

        <DrumQuickPresetSelector
          title="Quick drum preset"
          reconcile={reconcile}
          preset={preset}
          onSelectMaterial={material =>
            setDrumPresetMaterial(productId, phase.name, itemKey, material)
          }
          onSelectDiameter={diameter =>
            setDrumPresetDiameter(productId, phase.name, itemKey, diameter)
          }
          drumLibrary={drumLibrary}
        />

        {isBlocked && (
          <p className="text-danger small fst-italic mb-3">
            {isFlatSheath
              ? `Flat cable — select an incoming drum for every conductor colour before adding executions. Missing: ${flatMissingColors.join(
                  ', '
                )}.`
              : 'Select a drum from the previous phase before adding executions.'}
          </p>
        )}

        {flatSheathPlanning && !isBlocked && flatSheathUpstreams.length > 0 && (
          <p className="text-muted small fst-italic mb-3">
            Flat cable — every metre of sheath consumes a metre of each colour (
            {flatSheathRequiredColors.join(' + ')}). Bottleneck colour:{' '}
            {flatRemaining.toFixed(1)} m left of {flatCap.toFixed(1)} m.
          </p>
        )}

        {phase.execution.length ? (
          phase.execution.map((exec, idx) => {
            const isCoilsOnMachineExec =
              isFinalDeliveryPhase(phase, product?.phases) &&
              exec.deliveryMode === 'coils_on_machine';

            const canEstimate =
              canEstimateDrumLengthForPhase(phase.name, exec) &&
              String(exec.drum_material || '').trim() !== '' &&
              Number(exec.drum_diameter) > 0;

            const estimatedLength = estimateExecutionLengthFromDrum({
              phase,
              exec,
              product,
              drumLibrary
            });

            // ── PRODUCED DRUM (edit mode): locked ──
            if (exec._locked) {
              return (
                <Card
                  className={`mb-2 ${
                    reconcile && reconcileMovedFromLabel(exec)
                      ? 'border-warning'
                      : 'border-success'
                  }`}
                  key={idx}
                  style={{
                    borderLeft: `5px solid ${
                      reconcile && reconcileMovedFromLabel(exec)
                        ? '#ffc107'
                        : '#198754'
                    }`,
                    opacity: 0.9,
                    cursor:
                      reconcile && !!exec.tokenId
                        ? 'grab'
                        : 'default'
                  }}
                  draggable={false}
                  onDragStart={
                    reconcile && !!exec.tokenId
                      ? e =>
                          e.dataTransfer.setData(
                            'text/plain',
                            `${String(exec.tokenId)}|${reconcileHome}`
                          )
                      : undefined
                  }
                >
                  <Card.Header className="bg-success-subtle d-flex align-items-center gap-2">
                    {reconcile && exec.tokenId && (
                      <span
                        draggable
                        onDragStart={e => {
                          e.dataTransfer.setData(
                            'text/plain',
                            `|`
                          );
                          onDragTokenChange?.(String(exec.tokenId));
                        }}
                        onDragEnd={() => onDragTokenChange?.(null)}
                        title="Drag this drum to another product or conductor"
                        className="px-1 text-700"
                        style={{ cursor: 'grab', userSelect: 'none', lineHeight: 1 }}
                      >
                        &#10303;
                      </span>
                    )}
                    <Badge bg={exec._merged ? 'info' : 'success'}>
              {exec._merged ? 'MERGED' : 'PRODUCED'}
            </Badge>
                    <span className="fw-semibold">
                      {Number(
                        exec._producedLength || exec.plannedLength || 0
                      ).toLocaleString()}{' '}
                      m
                    </span>
                    {exec.machineId && <Badge bg="dark">{exec.machineId}</Badge>}
                    {reconcile && reconcileMovedFromLabel(exec) && (
                      <Badge bg="warning" text="dark" className="fs-10 ms-auto">
                        moved from {reconcileMovedFromLabel(exec)}
                      </Badge>
                    )}
                  </Card.Header>
                  <Card.Body className="py-2">
                    <div className="text-muted fs-10 fst-italic">
                      {mergedLockedNote(exec)}
                    </div>
                    {renderReconcileSwap(exec)}
                    {reconcile && exec._produced && (
                      <ReconcileDrumLength
                        exec={exec}
                        jobId={reconcileHome}
                        shares={
                          reconcileDrumShares.get(String(exec.tokenId)) || []
                        }
                        reconcileLen={reconcileLen}
                        setReconcileLength={setReconcileLength}
                        otherDrums={reconcileShares.filter(
                          d =>
                            d.phase === phase.name &&
                            d.key !== `${productId}::${exec.tokenId}`
                        )}
                      />
                    )}
                    <DrumCorrections exec={exec} />
                  </Card.Body>
                </Card>
              );
            }

            // ── PHANTOM EXECUTION: special locked card ──
            if (exec.isPhantomSource && exec.phantomId) {
              return (
                <Card
                  className="mb-2 border-info"
                  key={idx}
                  style={{ borderLeft: '5px solid #0dcaf0' }}
                >
                  <Card.Header className="bg-info-subtle d-flex justify-content-between align-items-center">
                    <div className="d-flex align-items-center gap-2">
                      <Badge bg="info">PHANTOM</Badge>
                      <span className="fw-semibold">
                        {Number(exec.plannedLength || 0).toLocaleString()} m
                      </span>
                      {exec.phantomColor && (
                        <Badge bg="dark">{exec.phantomColor}</Badge>
                      )}
                      <span className="text-muted fs-10 font-monospace">
                        {exec.phantomId}
                      </span>
                    </div>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => handleRemoveExecution(null, idx)}
                    >
                      Remove
                    </Button>
                  </Card.Header>
                  <Card.Body className="py-2">
                    <Row className="g-2">
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label>Machine<ExecWarning exec={exec} product={product} phase={phase} field="machine" /></Form.Label>
                          <Form.Control
                            size="sm"
                            value="PHANTOM"
                            readOnly
                            disabled
                            className="bg-light fw-semibold text-info"
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label>Length (m) — from phantom</Form.Label>
                          <Form.Control
                            size="sm"
                            type="number"
                            value={exec.plannedLength || ''}
                            disabled
                            className="bg-light"
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                    {exec.phantomNote && (
                      <div className="text-muted fs-10 mt-2 fst-italic">
                        {exec.phantomNote}
                      </div>
                    )}
                  </Card.Body>
                </Card>
              );
            }

            return (
              <Card className="mb-2" key={idx}>
                <Card.Header className="d-flex flex-wrap justify-content-between align-items-center gap-2">
                  <div className="d-flex align-items-center gap-2 flex-wrap">
                    <span>Execution #{idx + 1}</span>

                    {exec.fromUpstreamId && (
                      <small className="text-muted ms-2">
                        From Drum #{exec.fromUpstreamId}
                      </small>
                    )}

                    {isFlatSheath &&
                      Array.isArray(exec.fromUpstreamIds) &&
                      exec.fromUpstreamIds.length > 0 && (
                        <small className="text-muted ms-2">
                          From {exec.fromUpstreamIds.length} conductor drums
                          {(exec.fromUpstreamColors || []).map(c => (
                            <Badge key={c} bg="secondary" className="ms-1">
                              {c}
                            </Badge>
                          ))}
                        </small>
                      )}

                    {isFinalDeliveryPhase(phase, product?.phases) && (
                      <span className="ms-2">
                        {exec.deliveryMode === 'to_coiling' && (
                          <Badge bg="primary">Next: Coiling</Badge>
                        )}

                        {exec.deliveryMode === 'to_rewinding' && (
                          <Badge bg="warning">Next: Rewinding</Badge>
                        )}

                        {exec.deliveryMode === 'coils_on_machine' && (
                          <Badge bg="info">Coils On Machine</Badge>
                        )}

                        {(!exec.deliveryMode ||
                          exec.deliveryMode === 'final_product') && (
                          <Badge bg="success">Final Product</Badge>
                        )}
                      </span>
                    )}
                  </div>

                  <div className="d-flex align-items-center gap-2 flex-wrap">
                    {canEstimate && estimatedLength && (
                      <>
                        <Button
                          size="sm"
                          variant="success"
                          disabled={
                            flatSheathPlanning
                              ? !(flatSheathRemainingLength(exec.id) > 0)
                              : estimatedLength < drumCap
                          }
                          onClick={() => {
                            if (flatSheathPlanning) {
                              // fill this drum with what the bottleneck colour
                              // still has left (its own take released first),
                              // capped by what the drum can physically hold
                              handleFlatSheathPlannedLengthChange(
                                idx,
                                Math.min(
                                  flatSheathRemainingLength(exec.id),
                                  estimatedLength
                                )
                              );
                              return;
                            }

                            handleGenericExecutionChange(
                              idx,
                              'plannedLength',
                              drumCap
                            );
                          }}
                        >
                          Plan
                        </Button>

                        <Badge bg="success">
                          Estimated: {round(estimatedLength, 0)} m
                        </Badge>
                        {(() => {
                          const w =
                            num(exec?.plannedLength) > 0
                              ? computeWeightForExec(
                                  phase?.name,
                                  num(exec.plannedLength),
                                  product,
                                  exec
                                )
                              : null;
                          return w ? (
                            <Badge bg="dark" className="ms-1">
                              {w.totalKg} kg
                            </Badge>
                          ) : null;
                        })()}
                      </>
                    )}

                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => handleRemoveExecution(null, idx)}
                    >
                      Delete
                    </Button>
                  </div>
                </Card.Header>

                <Card.Body>
                  {isCoilsOnMachineExec ? (
                    renderCoilsOnMachineExecutionBody(exec, idx, drumCap)
                  ) : (
                    <Row className="g-2">
                      <Col lg={6} md={6} sm={12}>
                        <Form.Group>
                          <Form.Label>Machine<ExecWarning exec={exec} product={product} phase={phase} field="machine" /></Form.Label>
                          <Form.Select
                            size="sm"
                            value={exec.machineId || ''}
                            onChange={e =>
                              handleGenericExecutionChange(
                                idx,
                                'machineId',
                                e.target.value
                              )
                            }
                          >
                            <option value="">Select machine</option>
                            {MACHINE_OPTIONS.map(m => (
                              <option key={m.value} value={m.value}>
                                {m.label}
                              </option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      </Col>

                      <Col lg={6} md={6} sm={12}>
                        <Form.Group>
                          <Form.Label>Length (m)<ExecWarning exec={exec} product={product} phase={phase} field="length" /></Form.Label>
                          <Form.Control
                            size="sm"
                            type="number"
                            step="0.1"
                            value={exec.plannedLength || ''}
                            onChange={e =>
                              flatSheathPlanning
                                ? handleFlatSheathPlannedLengthChange(
                                    idx,
                                    Number(e.target.value)
                                  )
                                : handleGenericExecutionChange(
                                    idx,
                                    'plannedLength',
                                    Number(e.target.value)
                                  )
                            }
                          />
                        </Form.Group>
                      </Col>

                      {/* Same control as on a conductor row, for the phases
                          keyed on something other than colour — unwinding
                          groups by wire Ø, rope/sheaths by machine and drum.
                          The merge mechanism is identical, so the fix is too. */}
                      {mergeEnabled && productCount > 1 && (
                        <Col lg={6} md={6} sm={12}>
                          <Form.Group>
                            <Form.Label>
                              Merge group{' '}
                              <span className="text-700 fs-10">
                                — same group, one drum
                              </span>
                            </Form.Label>
                            <Form.Select
                              size="sm"
                              value={
                                exec.mergeSolo ? 'solo' : exec.mergeSlot ?? ''
                              }
                              onChange={e =>
                                handleGenericExecutionChange(
                                  idx,
                                  mergeChoiceToPatch(e.target.value)
                                )
                              }
                            >
                              <option value="">Auto (by position)</option>
                              <option value="solo">Alone — never merge</option>
                              {Array.from({ length: 8 }, (_, n) => (
                                <option key={n} value={n}>
                                  Group {n + 1}
                                </option>
                              ))}
                            </Form.Select>
                          </Form.Group>
                        </Col>
                      )}

                      <Col lg={6} md={6} sm={12}>
                        <Form.Group>
                          <Form.Label>Drum Material</Form.Label>
                          <Form.Select
                            size="sm"
                            value={exec.drum_material || ''}
                            onChange={e =>
                              handleGenericExecutionChange(
                                idx,
                                'drum_material',
                                e.target.value
                              )
                            }
                          >
                            <option value="">Select material</option>
                            <option value="wood">Wood</option>
                            <option value="metal">Metal</option>
                            <option value="plastic">Plastic</option>
                          </Form.Select>
                        </Form.Group>
                      </Col>

                      <Col lg={6} md={6} sm={12}>
                        <Form.Group>
                          <Form.Label>Drum Ø (mm)<ExecWarning exec={exec} product={product} phase={phase} field="drum" /></Form.Label>
                          <Form.Control
                            size="sm"
                            type="number"
                            step="1"
                            value={exec.drum_diameter || ''}
                            onChange={e =>
                              handleGenericExecutionChange(
                                idx,
                                'drum_diameter',
                                Number(e.target.value)
                              )
                            }
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                  )}

                  {isFinalDeliveryPhase(phase, product?.phases) && (
                    <div className="mt-3 d-flex flex-wrap justify-content-end gap-2">
                      <Button
                        size="sm"
                        variant={
                          exec.deliveryMode === 'to_coiling'
                            ? 'primary'
                            : 'outline-primary'
                        }
                        onClick={() =>
                          setExteriorExecutionRoute(idx, 'to_coiling')
                        }
                      >
                        → Send to Coiling
                      </Button>

                      <Button
                        size="sm"
                        variant={
                          exec.deliveryMode === 'to_rewinding'
                            ? 'warning'
                            : 'outline-warning'
                        }
                        onClick={() =>
                          setExteriorExecutionRoute(idx, 'to_rewinding')
                        }
                      >
                        → Send to Rewinding
                      </Button>

                      <Button
                        size="sm"
                        variant={
                          exec.deliveryMode === 'coils_on_machine'
                            ? 'info'
                            : 'outline-info'
                        }
                        onClick={() =>
                          setExteriorExecutionRoute(idx, 'coils_on_machine')
                        }
                      >
                        ✔ Coils On Machine
                      </Button>

                      <Button
                        size="sm"
                        variant={
                          !exec.deliveryMode ||
                          exec.deliveryMode === 'final_product'
                            ? 'success'
                            : 'outline-success'
                        }
                        onClick={() =>
                          setExteriorExecutionRoute(idx, 'final_product')
                        }
                      >
                        ✔ Final Product
                      </Button>
                    </div>
                  )}
                </Card.Body>
              </Card>
            );
          })
        ) : (
          <p className="fst-italic text-muted">No executions added yet.</p>
        )}
      </>
    );
  };

  const hasUpstreamSelected =
    !phase.dependsOn ||
    (Array.isArray(selectedUpstream?.[productId]?.[phase.name]) &&
      selectedUpstream[productId][phase.name].length > 0);

  const renderConductorIsolation = () => (
    <>
      {phase.conductors?.map((conductor, idx) => {
        const itemKey = `conductor-${idx}`;
        const preset = getDrumPreset(productId, phase.name, itemKey);
        // Already-planned conductor (its execution carries a tokenId) → lock it so
        // only genuinely new conductors can be filled during first-phase re-fill.
        const conductorLocked =
          lockPlannedConductors &&
          (conductor.execution || []).some(e => e.tokenId);

        const condColor = conductor.isolation?.color ?? null;
        const condDropActive =
          reconcile && dragOverConductor === `${product._id}:${idx}`;
        return (
          <Card
            key={idx}
            className={`mb-4 shadow-sm ${
              condDropActive ? 'border-info border-2' : 'border-secondary-subtle'
            }`}
            style={conductorLocked ? { opacity: 0.6 } : undefined}
            onDragOver={
              reconcile
                ? e => {
                    e.preventDefault();
                    e.stopPropagation();
                    e.dataTransfer.dropEffect = 'move';
                  }
                : undefined
            }
            onDragEnter={
              reconcile
                ? e => {
                    e.stopPropagation();
                    setDragOverConductor(`${product._id}:${idx}`);
                  }
                : undefined
            }
            onDragLeave={
              reconcile
                ? e => {
                    if (!e.currentTarget.contains(e.relatedTarget))
                      setDragOverConductor(null);
                  }
                : undefined
            }
            onDrop={
              reconcile
                ? e => {
                    e.preventDefault();
                    e.stopPropagation();
                    const [tok, src] = String(e.dataTransfer.getData('text/plain') || '').split('|');
                    setDragOverConductor(null);
                    if (tok)
                      moveReconcileDrum(tok, String(product._id), condColor, null, src || null);
                  }
                : undefined
            }
          >
            <Card.Header className="d-flex flex-wrap justify-content-between align-items-center gap-2">
              <span className="fw-semibold d-inline-flex align-items-center gap-2">
                Conductor {idx + 1} —
                {conductor.isolation?.color && (
                  <Badge
                    bg=""
                    className="fw-bold"
                    style={cableColorBadgeStyle(conductor.isolation.color)}
                  >
                    {conductor.isolation.color}
                  </Badge>
                )}
                {conductorLocked && (
                  <Badge bg="success" className="fs-10">
                    Planned
                  </Badge>
                )}
              </span>

              <HeaderProgressSlot>
                <ProgressTracker
                  mode="conductor"
                  targetLength={product.length}
                  executionSource={conductor.execution.map(e => {
                    // In reconcile the drums are already made. The bar has to
                    // answer "how much of this conductor EXISTS", not "how much
                    // has been planned onto drums" — planned is what it was
                    // going to be, and reconcile is the screen for saying it
                    // was not. Reading the staged value first is what makes it
                    // move while the operator is working.
                    if (reconcile && e._locked && e.tokenId) {
                      const draft = reconcileLen[`${productId}::${e.tokenId}`];
                      const staged =
                        draft == null || draft === ''
                          ? null
                          : Number(String(draft).replace(',', '.'));
                      return {
                        plannedLength: Number.isFinite(staged)
                          ? staged
                          : Number(e._producedLength || 0)
                      };
                    }
                    return {
                      plannedLength: Number(
                        e.plannedLengthInput ?? e.plannedLength ?? 0
                      )
                    };
                  })}
                />
              </HeaderProgressSlot>

              <Button
                variant="outline-primary"
                size="sm"
                disabled={!hasUpstreamSelected || conductorLocked}
                onClick={() => handleAddExecution(idx)}
              >
                + Add Execution
              </Button>
            </Card.Header>

            <Card.Body
              style={conductorLocked ? { pointerEvents: 'none' } : undefined}
            >
              <DrumQuickPresetSelector
                title="Quick drum preset"
          reconcile={reconcile}
                preset={preset}
                onSelectMaterial={material =>
                  setDrumPresetMaterial(
                    productId,
                    phase.name,
                    itemKey,
                    material
                  )
                }
                onSelectDiameter={diameter =>
                  setDrumPresetDiameter(
                    productId,
                    phase.name,
                    itemKey,
                    diameter
                  )
                }
                drumLibrary={drumLibrary}
              />

              {!reconcile && (
              <Row className="g-2 mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Wire Diameter (mm)</Form.Label>
                    <Form.Control
                      type="number"
                      step="0.01"
                      value={conductor.wire.diameter}
                      onChange={e =>
                        handleConductorChange(
                          idx,
                          'wire_diameter',
                          e.target.value
                        )
                      }
                    />
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Insulation Diameter (mm)</Form.Label>
                    <Form.Control
                      type="number"
                      step="0.01"
                      value={conductor.isolation.diameter}
                      onChange={e =>
                        handleConductorChange(
                          idx,
                          'isolation_diameter',
                          e.target.value
                        )
                      }
                    />
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Min Radial (mm)</Form.Label>
                    <Form.Control
                      type="number"
                      step="0.01"
                      value={conductor.isolation.minRadial}
                      onChange={e =>
                        handleConductorChange(
                          idx,
                          'isolation_minRadial',
                          e.target.value
                        )
                      }
                    />
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Max Radial (mm)</Form.Label>
                    <Form.Control
                      type="number"
                      step="0.01"
                      value={conductor.isolation.maxRadial}
                      onChange={e =>
                        handleConductorChange(
                          idx,
                          'isolation_maxRadial',
                          e.target.value
                        )
                      }
                    />
                  </Form.Group>
                </Col>
              </Row>
              )}

              {conductor.execution.length > 0 ? (
                conductor.execution.map((exec, execIdx) => (
                  <ConductorExecutionRow
                    key={execIdx}
                    exec={exec}
                    execIdx={execIdx}
                    conductor={conductor}
                    conductorIdx={idx}
                    product={product}
                    phase={phase}
                    productId={productId}
                    mergeEnabled={mergeEnabled}
                    productCount={productCount}
                    selectedUpstream={selectedUpstream}
                    handleConductorChange={handleConductorChange}
                    patchConductorExecution={patchConductorExecution}
                    handleRemoveExecution={handleRemoveExecution}
                    handleAddCoilingPhase={handleAddCoilingPhase}
                    handleAddRewindingPhase={handleAddRewindingPhase}
                    drumLibrary={drumLibrary}
                    reconcile={reconcile}
                    reconcileTargets={reconcileTargets}
                    reconcileSel={reconcileSel}
                    setReconcileSel={setReconcileSel}
                    moveReconcileDrum={moveReconcileDrum}
                    reconcileDrums={reconcileDrums}
                    reconcileShares={reconcileShares}
                    reconcileDrumShares={reconcileDrumShares}
                    reconcileLen={reconcileLen}
                    onDragTokenChange={onDragTokenChange}
                    setReconcileLength={setReconcileLength}
                    swapReconcileDrums={swapReconcileDrums}
                  />
                ))
              ) : (
                <p className="text-muted fst-italic">
                  No executions added yet.
                </p>
              )}
            </Card.Body>
          </Card>
        );
      })}
    </>
  );

  const sortUnwindingExecutions = execs => {
    return [...execs].sort((a, b) => {
      if (a.wireDiameter !== b.wireDiameter) {
        return a.wireDiameter - b.wireDiameter;
      }
      return a.id - b.id; // stabilitate
    });
  };

  const addUnwindingBatch = () => {
    const preStranding = product.phases.find(p => p.name === 'pre_stranding');
    if (!preStranding) return;

    const newExecs = [];

    preStranding.numberOfWires.forEach((count, i) => {
      const qty = parseInt(count, 10) || 0;
      const diameter = Number(preStranding.wireDiameter[i]);

      for (let j = 0; j < qty; j++) {
        newExecs.push({
          id: Date.now() + Math.random(),
          wireDiameter: diameter,
          machineId: '',
          plannedLength: '',
          drum_material: '',
          drum_diameter: ''
        });
      }
    });

    const merged = [...(phase.execution || []), ...newExecs];

    const sorted = sortUnwindingExecutions(merged);

    onChange(productId, phase.name, 'execution', sorted);
  };

  // ===============================
  // UNWINDING (supports mixed sections + normal)
  // Copy-paste inside your component file
  // Needs: Card, Row, Col, Form, Button, Badge, ProgressTracker,
  //        MACHINE_OPTIONS, onChange, handleRemoveExecution, handleGenericExecutionChange
  // Uses: phase, product, productId
  // ===============================

  const num = v => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  };

  const estimateSectionFromRope = rope => {
    // area ≈ Σ (count * π * (d/2)^2)
    if (!rope?.wireDiameter || !rope?.numberOfWires) return 0;
    return rope.wireDiameter.reduce((s, d, i) => {
      const r = num(d) / 2;
      return s + num(rope.numberOfWires[i]) * Math.PI * r * r;
    }, 0);
  };

  const formatRopeStructure = rope => {
    if (!rope?.wireDiameter || !rope?.numberOfWires) return '-';
    return rope.wireDiameter
      .map((d, i) => `${num(rope.numberOfWires[i]) || 0}×Ø${num(d)}`)
      .join(' + ');
  };

  const buildUnwindingSpecs = (product, phase) => {
    // Returns a normalized list of rope specs:
    // [
    //   { ropeIndex: 0|null, label, conductorMultiplier, wireCountMap: {diam: qty}, structure, estSection }
    // ]
    const pre = product?.phases?.find(p => p.name === 'pre_stranding');

    // -------- MIXED (rope array exists on pre_stranding) --------
    if (Array.isArray(pre?.rope) && pre.rope.length) {
      // decide VG rope = smallest estimated section (your rule)
      const sections = pre.rope.map(r => estimateSectionFromRope(r));
      const minIdx = sections.reduce(
        (bestIdx, val, idx) => (val < sections[bestIdx] ? idx : bestIdx),
        0
      );

      return pre.rope.map((rope, idx) => {
        const wireCountMap = {};
        (rope.wireDiameter || []).forEach((d, i) => {
          wireCountMap[num(d)] = num(rope.numberOfWires?.[i]) || 0;
        });

        const isVG = idx === minIdx; // <- VG rope by smallest section
        const conductorMultiplier = isVG
          ? 1
          : num(product.numberOfConductors) || 0;

        return {
          ropeIndex: idx,
          isVG,
          label: isVG ? `Rope ${idx + 1} — VG` : `Rope ${idx + 1} — MAIN`,
          conductorMultiplier,
          material: rope.material,
          type: rope.type,
          structure: formatRopeStructure(rope),
          estSection: estimateSectionFromRope(rope),
          wireCountMap
        };
      });
    }

    // -------- NORMAL (single spec on unwinding phase OR pre_stranding) --------
    const numberOfWires = phase?.numberOfWires ?? pre?.numberOfWires ?? [];
    const wireDiameter = phase?.wireDiameter ?? pre?.wireDiameter ?? [];

    const wireCountMap = {};
    if (Array.isArray(wireDiameter) && Array.isArray(numberOfWires)) {
      wireDiameter.forEach((d, i) => {
        wireCountMap[num(d)] = num(numberOfWires[i]) || 0;
      });
    }

    return [
      {
        ropeIndex: null,
        isVG: false,
        label: 'Unwinding',
        conductorMultiplier: num(product.numberOfConductors) || 0,
        material: phase?.material ?? pre?.material,
        type: phase?.type ?? pre?.type,
        structure:
          Array.isArray(wireDiameter) && Array.isArray(numberOfWires)
            ? wireDiameter
                .map((d, i) => `${num(numberOfWires[i]) || 0}×Ø${num(d)}`)
                .join(' + ')
            : '-',
        estSection: 0,
        wireCountMap
      }
    ];
  };

  const groupExecutionsByRopeAndDiameter = execs => {
    // returns:
    // {
    //   [ropeIndexKey]: {
    //     ropeIndex: number|null,
    //     diameters: {
    //       [diamKey]: { wireDiameter, execs: [] }
    //     }
    //   }
    // }
    const map = {};

    execs.forEach(exec => {
      const ropeKey =
        exec.ropeIndex === null || exec.ropeIndex === undefined
          ? 'null'
          : String(exec.ropeIndex);
      const diamKey = String(num(exec.wireDiameter));

      if (!map[ropeKey]) {
        map[ropeKey] = { ropeIndex: exec.ropeIndex ?? null, diameters: {} };
      }
      if (!map[ropeKey].diameters[diamKey]) {
        map[ropeKey].diameters[diamKey] = {
          wireDiameter: num(exec.wireDiameter),
          execs: []
        };
      }
      map[ropeKey].diameters[diamKey].execs.push(exec);
    });

    return map;
  };

  const renderUnwindingPhase = () => {
    if (phase.name !== 'unwinding') return null;

    const execs = Array.isArray(phase.execution) ? phase.execution : [];
    const length = num(product?.length);

    // Products sit side-by-side (1/1, 1/2 or 1/3 width by count). In a narrow
    // column the drum cards' own multi-field grid gets squeezed unreadable, so
    // widen them as the product column narrows: 1 product → 3/row, 2 → 2/row,
    // 3+ → 1/row (full column width).
    const drumCol =
      productCount >= 3
        ? { xs: 12 }
        : productCount === 2
          ? { md: 6, xs: 12 }
          : { lg: 4, md: 6, sm: 12 };

    // specs (mixed or normal)
    const specs = buildUnwindingSpecs(product, phase);

    // grouped executions (by ropeIndex + diameter)
    const grouped = groupExecutionsByRopeAndDiameter(execs);

    // total target (for header progress)
    const totalTargetLengthRequired = specs.reduce((sum, spec) => {
      const conductorMult = num(spec.conductorMultiplier);
      const wireTarget = Object.entries(spec.wireCountMap).reduce(
        (s2, [diam, qty]) => s2 + num(qty) * length * conductorMult,
        0
      );
      return sum + wireTarget;
    }, 0);

    // global add (adds a drum to the first available spec + first diameter)
    const addUnwindingBatch = () => {
      const firstSpec = specs[0];
      const firstDiam = Number(Object.keys(firstSpec.wireCountMap)[0] ?? 0);

      const newExec = {
        id: Date.now(),
        ropeIndex: firstSpec.ropeIndex ?? null,
        wireDiameter: firstDiam || 0,
        machineId: '',
        plannedLength: '',
        drum_material: '',
        drum_diameter: ''
      };

      onChange(productId, phase.name, 'execution', [...execs, newExec]);
    };

    // UI
    return (
      <div className="mb-3">
        {/* HEADER */}
        <div
          className={`d-flex gap-2 mb-3 ${
            productCount >= 3
              ? 'flex-column align-items-stretch'
              : 'flex-wrap justify-content-between align-items-center'
          }`}
        >
          <h6 className="fw-semibold mb-0 text-primary">
            Unwinding Execution Plan
          </h6>

          <HeaderProgressSlot>
            <ProgressTracker
              mode="conductor"
              targetLength={totalTargetLengthRequired}
              executionSource={execs}
            />
          </HeaderProgressSlot>

          {/* <Button
            variant="outline-primary"
            size="sm"
            onClick={addUnwindingBatch}
          >
            + Add Execution
          </Button> */}
        </div>

        {/* SPECS (rope cards) */}
        {specs.map(spec => {
          const ropeKey =
            spec.ropeIndex === null || spec.ropeIndex === undefined
              ? 'null'
              : String(spec.ropeIndex);

          const ropeGroups = grouped[ropeKey]?.diameters ?? {};

          // rope total target
          const ropeTarget = Object.entries(spec.wireCountMap).reduce(
            (s, [diam, qty]) =>
              s + num(qty) * length * num(spec.conductorMultiplier),
            0
          );

          // rope execution source = all execs from this ropeIndex (any diameter)
          const ropeExecs = Object.values(ropeGroups).flatMap(g => g.execs);

          return (
            <Card key={ropeKey} className="mb-4 border-info-subtle shadow-sm">
              <Card.Header
                className={`d-flex gap-2 ${
                  productCount >= 3
                    ? 'flex-column align-items-stretch'
                    : 'flex-wrap justify-content-between align-items-center'
                }`}
              >
                <div className="fw-semibold">
                  {spec.label}
                  {spec.structure && (
                    <span className="text-muted ms-2">— {spec.structure}</span>
                  )}
                </div>

                <HeaderProgressSlot>
                  <ProgressTracker
                    mode="conductor"
                    targetLength={ropeTarget}
                    executionSource={ropeExecs}
                  />
                </HeaderProgressSlot>

                <div className="text-nowrap">
                  {spec.isVG ? (
                    <Badge bg="success">VG</Badge>
                  ) : (
                    <Badge bg="secondary">MAIN</Badge>
                  )}
                </div>
              </Card.Header>

              <Card.Body>
                {/* rope technical mini-fields (same “style idea” like your other phases) */}
                {!reconcile && (
                <Row className="g-2 mb-3">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Material</Form.Label>
                      <Form.Control value={spec.material || '-'} disabled />
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Type</Form.Label>
                      <Form.Control value={spec.type || '-'} disabled />
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Conductor multiplier</Form.Label>
                      <Form.Control
                        value={String(spec.conductorMultiplier || 0)}
                        disabled
                      />
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Estimated section (mm²)</Form.Label>
                      <Form.Control
                        value={
                          spec.estSection
                            ? `~${spec.estSection.toFixed(1)}`
                            : '-'
                        }
                        disabled
                      />
                    </Form.Group>
                  </Col>
                </Row>
                )}

                {/* Diameter groups for this rope */}
                {Object.keys(spec.wireCountMap).length ? (
                  Object.entries(spec.wireCountMap).map(([diamStr, qty]) => {
                    const diam = num(diamStr);
                    const groupKey = String(diam);
                    const groupExecs = ropeGroups[groupKey]?.execs ?? [];

                    const targetForThisDiameter =
                      num(qty) * length * num(spec.conductorMultiplier);

                    const dropKey = `${product._id}:${ropeKey}:${groupKey}`;
                    return (
                      <Card
                        key={`${ropeKey}-${groupKey}`}
                        className={`bg-light mb-3 shadow-sm ${
                          reconcile && dragOverConductor === dropKey
                            ? 'border-info border-2'
                            : 'border-info-subtle'
                        }`}
                        onDragOver={
                          reconcile
                            ? e => {
                                e.preventDefault();
                                e.stopPropagation();
                                e.dataTransfer.dropEffect = 'move';
                              }
                            : undefined
                        }
                        onDragEnter={
                          reconcile
                            ? e => {
                                e.stopPropagation();
                                setDragOverConductor(dropKey);
                              }
                            : undefined
                        }
                        onDragLeave={
                          reconcile
                            ? e => {
                                if (!e.currentTarget.contains(e.relatedTarget))
                                  setDragOverConductor(null);
                              }
                            : undefined
                        }
                        onDrop={
                          reconcile
                            ? e => {
                                e.preventDefault();
                                e.stopPropagation();
                                const [tok, src] = String(e.dataTransfer.getData('text/plain') || '').split('|');
                                setDragOverConductor(null);
                                if (tok)
                                  moveReconcileDrum(
                                    tok,
                                    String(product._id),
                                    null,
                                    diam,
                                    src || null
                                  );
                              }
                            : undefined
                        }
                      >
                        <Card.Header
                          className={`d-flex gap-3 ${
                            productCount >= 3
                              ? 'flex-column align-items-stretch'
                              : 'align-items-center'
                          }`}
                        >
                          <div className="fw-semibold text-nowrap">
                            Ø {diam} mm
                            <span className="text-muted ms-2">
                              (need {qty} wires, {groupExecs.length} drums)
                            </span>
                          </div>

                          <div className="flex-grow-1 px-2">
                            <ProgressTracker
                              mode="conductor"
                              targetLength={targetForThisDiameter}
                              executionSource={groupExecs}
                            />
                          </div>

                          {/* ✅ ADD DRUM HERE */}
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => {
                              const newExec = {
                                id: Date.now(),
                                ropeIndex: spec.ropeIndex ?? null,
                                wireDiameter: diam,
                                machineId: '',
                                plannedLength: '',
                                drum_material: '',
                                drum_diameter: ''
                              };

                              onChange(productId, phase.name, 'execution', [
                                ...execs,
                                newExec
                              ]);
                            }}
                          >
                            + Add Drum
                          </Button>
                        </Card.Header>

                        <Card.Body>
                          <Row className="g-3">
                            {groupExecs.map((exec, idx) => {
                              const globalIdx = execs.findIndex(
                                e => e.id === exec.id
                              );

                              if (exec._locked) {
                                return (
                                  <Col {...drumCol} key={exec.id ?? idx}>
                                    {renderReconcileLockedCard(exec, globalIdx)}
                                  </Col>
                                );
                              }

                              if (exec.isPhantomSource && exec.phantomId) {
                                return (
                                  <Col {...drumCol} key={exec.id}>
                                    <Card className="h-100 border-info" style={{ borderLeft: '4px solid #0dcaf0' }}>
                                      <Card.Header className="bg-info-subtle d-flex justify-content-between align-items-center py-2">
                                        <div className="d-flex align-items-center gap-1">
                                          <Badge bg="info" style={{ fontSize: 9 }}>PHANTOM</Badge>
                                          <span className="fw-semibold small">{Number(exec.plannedLength || 0).toLocaleString()} m</span>
                                        </div>
                                        <Button variant="outline-danger" size="sm" onClick={() => handleRemoveExecution(null, globalIdx)}>Remove</Button>
                                      </Card.Header>
                                      <Card.Body className="py-2">
                                        <Form.Control size="sm" value="PHANTOM" readOnly disabled className="bg-light fw-semibold text-info" />
                                        <Form.Control
                                          size="sm"
                                          className="mt-1"
                                          type="number"
                                          placeholder="Used length (m)"
                                          value={exec.plannedLength || ''}
                                          max={num(exec.phantomMax) || undefined}
                                          onChange={e => {
                                            const cap = num(exec.phantomMax) || num(exec.plannedLength);
                                            let v = Number(e.target.value);
                                            if (!Number.isFinite(v) || v < 0) v = 0;
                                            if (cap && v > cap) v = cap;
                                            handleGenericExecutionChange(globalIdx, 'plannedLength', v);
                                          }}
                                        />
                                        {num(exec.phantomMax) > 0 && (
                                          <div className="text-muted fs-10 mt-1">
                                            available: {num(exec.phantomMax).toLocaleString()} m
                                          </div>
                                        )}
                                        {exec.phantomNote && <div className="text-muted fs-10 mt-1 fst-italic">{exec.phantomNote}</div>}
                                      </Card.Body>
                                    </Card>
                                  </Col>
                                );
                              }

                              const wireDiam = num(exec.wireDiameter);
                              const hasDrumSel =
                                String(exec.drum_material || '').trim() !== '' &&
                                Number(exec.drum_diameter) > 0;

                              const estimatedLen =
                                hasDrumSel && wireDiam > 0
                                  ? getPhaseEstimatedLength({
                                      phaseDiameter: wireDiam,
                                      drumMaterial: exec.drum_material,
                                      drumDiameter: exec.drum_diameter,
                                      drumLibrary
                                    })
                                  : null;

                              // remaining target for this diameter group
                              const alreadyPlanned = groupExecs.reduce(
                                (s, e) => s + num(e.plannedLength),
                                0
                              );
                              const planMax = Math.max(
                                targetForThisDiameter - alreadyPlanned + num(exec.plannedLength),
                                0
                              );

                              return (
                                <Col {...drumCol} key={exec.id}>
                                  <Card className="h-100">
                                    {/* Two rows, not one. Five controls on a
                                        single non-wrapping flex line overflowed
                                        the card as soon as there was more than
                                        one product on screen: with two products
                                        a drum card is a quarter of the page
                                        wide, and the weight badge and Delete
                                        button hung outside it entirely. The
                                        identity and the destructive action keep
                                        the top line; the planning controls wrap
                                        underneath as they need to. */}
                                    <Card.Header className="py-2">
                                      <div className="d-flex align-items-center gap-2">
                                        <span className="fw-semibold small text-truncate">
                                          Drum #{idx + 1}
                                        </span>
                                        <Button
                                          variant="outline-danger"
                                          size="sm"
                                          className="ms-auto flex-shrink-0 py-0 px-2 fs-10"
                                          title="Remove this drum"
                                          onClick={() =>
                                            handleRemoveExecution(null, globalIdx)
                                          }
                                        >
                                          Delete
                                        </Button>
                                      </div>

                                      <div className="d-flex flex-wrap align-items-center gap-2 mt-1">
                                        {hasDrumSel && (
                                          <Button
                                            size="sm"
                                            variant="success"
                                            className="py-0 px-2 fs-10"
                                            onClick={() => {
                                              const val = estimatedLen
                                                ? Math.min(planMax, estimatedLen)
                                                : planMax;
                                              handleGenericExecutionChange(
                                                globalIdx,
                                                'plannedLength',
                                                Number(val.toFixed(1))
                                              );
                                            }}
                                          >
                                            Plan
                                          </Button>
                                        )}

                                        {estimatedLen ? (
                                          <Badge bg="success" className="fs-10">
                                            Max: {round(estimatedLen, 0)} m
                                          </Badge>
                                        ) : hasDrumSel ? (
                                          <Badge bg="secondary" className="fs-10">
                                            No estimate
                                          </Badge>
                                        ) : null}

                                        {num(exec.plannedLength) > 0 &&
                                          (() => {
                                            const w = computeWeightForExec(
                                              'unwinding',
                                              num(exec.plannedLength),
                                              product,
                                              exec
                                            );
                                            return w ? (
                                              <Badge bg="dark" className="fs-10">
                                                {w.totalKg} kg
                                              </Badge>
                                            ) : null;
                                          })()}
                                      </div>
                                    </Card.Header>

                                    <Card.Body className="py-2">
                                      <Row className="g-2">
                                        {/* MACHINE */}
                                        <Col md={6} xs={12}>
                                          <div className="d-flex align-items-center gap-1">
                                          <Form.Select
                                            size="sm"
                                            value={exec.machineId || ''}
                                            onChange={e =>
                                              handleGenericExecutionChange(
                                                globalIdx,
                                                'machineId',
                                                e.target.value
                                              )
                                            }
                                          >
                                            <option value="">Machine</option>
                                            {MACHINE_OPTIONS.map(m => (
                                              <option
                                                key={m.value}
                                                value={m.value}
                                              >
                                                {m.label}
                                              </option>
                                            ))}
                                          </Form.Select>
                                          <ExecWarning exec={exec} product={product} phase={phase} field="machine" />
                                          </div>
                                        </Col>

                                        {/* LENGTH */}
                                        <Col md={6} xs={12}>
                                          <div className="d-flex align-items-center gap-1">
                                          <Form.Control
                                            size="sm"
                                            type="number"
                                            placeholder="Length (m)"
                                            value={exec.plannedLength || ''}
                                            onChange={e =>
                                              handleGenericExecutionChange(
                                                globalIdx,
                                                'plannedLength',
                                                Number(e.target.value)
                                              )
                                            }
                                          />
                                          <ExecWarning exec={exec} product={product} phase={phase} field="length" />
                                          </div>
                                        </Col>

                                        {/* DRUM MATERIAL */}
                                        <Col md={6} xs={12}>
                                          <Form.Select
                                            size="sm"
                                            value={exec.drum_material || ''}
                                            onChange={e =>
                                              handleGenericExecutionChange(
                                                globalIdx,
                                                'drum_material',
                                                e.target.value
                                              )
                                            }
                                          >
                                            <option value="">
                                              Drum material
                                            </option>
                                            <option value="wood">Wood</option>
                                            <option value="metal">Metal</option>
                                          </Form.Select>
                                        </Col>

                                        {/* DRUM Ø */}
                                        <Col md={6} xs={12}>
                                          <div className="d-flex align-items-center gap-1">
                                          <Form.Control
                                            size="sm"
                                            type="number"
                                            placeholder="Ø (mm)"
                                            value={exec.drum_diameter || ''}
                                            onChange={e =>
                                              handleGenericExecutionChange(
                                                globalIdx,
                                                'drum_diameter',
                                                Number(e.target.value)
                                              )
                                            }
                                          />
                                          <ExecWarning exec={exec} product={product} phase={phase} field="drum" />
                                          </div>
                                        </Col>
                                      </Row>
                                    </Card.Body>
                                  </Card>
                                </Col>
                              );
                            })}
                          </Row>

                          {groupExecs.length === 0 && (
                            <p className="fst-italic text-muted mb-0">
                              No drums for Ø {diam} yet.
                            </p>
                          )}
                        </Card.Body>
                      </Card>
                    );
                  })
                ) : (
                  <p className="fst-italic text-muted mb-0">
                    No wire spec found for this rope.
                  </p>
                )}

                {ropeExecs.length === 0 && (
                  <p className="fst-italic text-muted">
                    No unwinding drums added yet.
                  </p>
                )}
              </Card.Body>
            </Card>
          );
        })}

        {/* Reconcile: produced drums moved in from another product whose wire-Ø isn't
            in THIS product's spec would otherwise render nowhere (they group by Ø).
            Keep them visible + draggable so they can be re-slotted onto a Ø group
            above, or moved to another product. */}
        {reconcile &&
          (() => {
            const covered = new Set();
            specs.forEach(spec => {
              const rk =
                spec.ropeIndex == null ? 'null' : String(spec.ropeIndex);
              Object.keys(spec.wireCountMap || {}).forEach(d =>
                covered.add(`${rk}:${String(num(d))}`)
              );
            });
            const extras = execs.filter(
              e =>
                e._locked &&
                !covered.has(
                  `${e.ropeIndex == null ? 'null' : String(e.ropeIndex)}:${String(
                    num(e.wireDiameter)
                  )}`
                )
            );
            if (!extras.length) return null;
            return (
              <Card className="mb-3 border-warning shadow-sm">
                <Card.Header className="bg-warning-subtle fw-semibold d-flex align-items-center gap-2 flex-wrap">
                  Reconciled drums
                  <span className="text-body-tertiary fw-normal fs-10">
                    (Ø not in this product&apos;s spec — drag onto a Ø group above
                    to re-slot, or to another product)
                  </span>
                </Card.Header>
                <Card.Body>
                  <Row className="g-3">
                    {extras.map(exec => {
                      const globalIdx = execs.findIndex(e => e.id === exec.id);
                      return (
                        <Col {...drumCol} key={exec.id ?? globalIdx}>
                          {renderReconcileLockedCard(exec, globalIdx)}
                        </Col>
                      );
                    })}
                  </Row>
                </Card.Body>
              </Card>
            );
          })()}

        {execs.length === 0 && (
          <p className="fst-italic text-muted">No unwinding drums added yet.</p>
        )}
      </div>
    );
  };

  const renderRewindingPhase = () => {
    if (!currentUpstream) {
      const depLabel = phase.dependsOn?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) || 'previous phase';
      return (
        <p className="text-danger">Select a drum from {depLabel} first.</p>
      );
    }

    const drumCap = Number(currentUpstream.plannedLength || 0);
    const safeNum = v => (isNaN(Number(v)) ? 0 : Number(v));

    const allExecs = phase.execution.map(exec => {
      const rewCount = safeNum(exec.rewindCount);
      const rewLen = safeNum(exec.rewindLength);

      return {
        ...exec,
        totalLength: rewCount * rewLen
      };
    });

    const currentExecs = allExecs.filter(
      e => Number(e.fromUpstreamId) === Number(currentUpstream.id)
    );

    return (
      <div className="mb-3">
        {/* HEADER */}
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
          <h6 className="fw-semibold mb-0">Rewinding Plan</h6>

          <HeaderProgressSlot>
            <ProgressTracker
              key={currentUpstream.id}
              targetLength={drumCap}
              executionSource={currentExecs.map(e => ({
                plannedLength: e.totalLength
              }))}
            />
          </HeaderProgressSlot>

          <Button
            variant="outline-primary"
            size="sm"
            onClick={() => handleAddExecution()}
          >
            + Add Rewind
          </Button>
        </div>

        {/* EXECUTIONS */}
        {allExecs.map((exec, idx) => {
          if (exec._locked) return renderReconcileLockedCard(exec, idx);
          const isCurrent =
            Number(exec.fromUpstreamId) === Number(currentUpstream.id);

          return (
            <Card
              key={exec.id}
              className={`mb-3 shadow-sm ${
                isCurrent ? 'border-primary' : 'opacity-75'
              }`}
            >
              <Card.Header className="d-flex justify-content-between">
                <span>
                  Rewind — {exec.totalLength || 0} m
                  <small className="ms-2 text-muted">
                    (Drum {exec.fromUpstreamId})
                  </small>
                </span>

                <Button
                  variant="outline-danger"
                  size="sm"
                  onClick={() => handleRemoveExecution(null, idx)}
                >
                  Delete
                </Button>
              </Card.Header>

              <Card.Body>
                <Form.Check
                  type="switch"
                  id={`reserve-rew-${exec.id}`}
                  className="mb-2"
                  label="Reserve only (don't schedule to a machine yet — just mark it as a pending phase to plan later)"
                  checked={!!exec.reserve}
                  onChange={e =>
                    handleGenericExecutionChange(idx, {
                      reserve: e.target.checked,
                      machineId: e.target.checked ? '' : exec.machineId
                    })
                  }
                />

                <Row className="g-2">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Machine<ExecWarning exec={exec} product={product} phase={phase} field="machine" /></Form.Label>
                      <Form.Select
                        value={exec.machineId}
                        disabled={!!exec.reserve}
                        onChange={e =>
                          handleGenericExecutionChange(
                            idx,
                            'machineId',
                            e.target.value
                          )
                        }
                      >
                        <option value="">
                          {exec.reserve ? 'Reserved (no machine)' : 'Select'}
                        </option>
                        {MACHINE_OPTIONS.map(m => (
                          <option key={m.value} value={m.value}>
                            {m.label}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Rewind Count</Form.Label>
                      <Form.Control
                        type="number"
                        value={exec.rewindCount}
                        onChange={e =>
                          handleGenericExecutionChange(
                            idx,
                            'rewindCount',
                            Number(e.target.value)
                          )
                        }
                      />
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Rewind Length (m)</Form.Label>
                      <Form.Control
                        type="number"
                        value={exec.rewindLength}
                        onChange={e =>
                          handleGenericExecutionChange(
                            idx,
                            'rewindLength',
                            Number(e.target.value)
                          )
                        }
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <div className="mt-3">
                  <ProgressTracker
                    compact
                    targetLength={drumCap}
                    executionSource={[{ plannedLength: exec.totalLength || 0 }]}
                  />
                </div>
              </Card.Body>
            </Card>
          );
        })}
      </div>
    );
  };

  const renderCoilingPhase = () => {
    const safeNum = v => (isNaN(Number(v)) ? 0 : Number(v));
    const hasExistingExecs =
      Array.isArray(phase.execution) && phase.execution.length > 0;

    // Nothing planned yet AND no drum picked → prompt to pick a drum. If coil
    // runs already exist (e.g. auto-created from exterior routing), show them
    // even before a drum is selected so they're visible/editable on arrival.
    if (!currentUpstream && !hasExistingExecs) {
      const depLabel =
        phase.dependsOn
          ?.replace(/_/g, ' ')
          .replace(/\b\w/g, c => c.toUpperCase()) || 'previous phase';
      return (
        <p className="text-danger">Select a drum from {depLabel} first.</p>
      );
    }

    const drumCap = Number(currentUpstream?.plannedLength || 0);

    const allExecs = phase.execution.map(exec => {
      const coils = safeNum(exec.coilCount);
      const len = safeNum(exec.coilLength);

      return {
        ...exec,
        totalLength: coils * len
      };
    });

    const currentExecs = currentUpstream
      ? allExecs.filter(
          e => Number(e.fromUpstreamId) === Number(currentUpstream.id)
        )
      : [];

    return (
      <div className="mb-3">
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
          <h6 className="fw-semibold mb-0">Coiling Plan</h6>

          <HeaderProgressSlot>
            {currentUpstream && (
              <ProgressTracker
                key={currentUpstream.id}
                targetLength={drumCap}
                executionSource={currentExecs.map(e => ({
                  plannedLength: e.totalLength
                }))}
              />
            )}
          </HeaderProgressSlot>

          <Button
            variant="outline-primary"
            size="sm"
            onClick={() => handleAddExecution()}
            disabled={!currentUpstream}
            title={
              currentUpstream
                ? 'Add a coil run for the selected drum'
                : 'Select an incoming drum first to add a coil run'
            }
          >
            + Add Coil
          </Button>
        </div>

        {!currentUpstream && (
          <p className="text-muted small mb-3">
            Select an incoming drum to add or edit its coil run. Runs
            auto-created from exterior coil routing are shown below.
          </p>
        )}

        {allExecs.map((exec, idx) => {
          if (exec._locked) return renderReconcileLockedCard(exec, idx);
          const isCurrent =
            !!currentUpstream &&
            Number(exec.fromUpstreamId) === Number(currentUpstream.id);
          // keep auto-created runs readable even when not the current drum
          const highlighted = isCurrent || exec.autoFromExterior;

          return (
            <Card
              key={exec.id}
              className={`mb-3 shadow-sm ${
                highlighted ? 'border-primary' : 'opacity-75'
              }`}
            >
              <Card.Header className="d-flex justify-content-between">
                <span>
                  Coil — {exec.totalLength || 0} m
                  <small className="ms-2 text-muted">
                    (Drum {exec.fromUpstreamId})
                  </small>
                  {exec.autoFromExterior && (
                    <Badge bg="info" className="ms-2">
                      Auto from exterior
                    </Badge>
                  )}
                </span>

                <Button
                  variant="outline-danger"
                  size="sm"
                  onClick={() => handleRemoveExecution(null, idx)}
                >
                  Delete
                </Button>
              </Card.Header>

              <Card.Body>
                <Form.Check
                  type="switch"
                  id={`reserve-coil-${exec.id}`}
                  className="mb-2"
                  label="Reserve only (don't schedule to a machine yet — just mark it as a pending phase to plan later)"
                  checked={!!exec.reserve}
                  onChange={e =>
                    handleGenericExecutionChange(idx, {
                      reserve: e.target.checked,
                      machineId: e.target.checked ? '' : exec.machineId
                    })
                  }
                />

                <Row className="g-2">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Machine<ExecWarning exec={exec} product={product} phase={phase} field="machine" /></Form.Label>
                      <Form.Select
                        value={exec.machineId}
                        disabled={!!exec.reserve}
                        onChange={e =>
                          handleGenericExecutionChange(
                            idx,
                            'machineId',
                            e.target.value
                          )
                        }
                      >
                        <option value="">
                          {exec.reserve ? 'Reserved (no machine)' : 'Select'}
                        </option>
                        {MACHINE_OPTIONS.map(m => (
                          <option key={m.value} value={m.value}>
                            {m.label}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Coil Count</Form.Label>
                      <Form.Control
                        type="number"
                        value={exec.coilCount}
                        onChange={e =>
                          handleGenericExecutionChange(
                            idx,
                            'coilCount',
                            Number(e.target.value)
                          )
                        }
                      />
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Coil Length</Form.Label>
                      <Form.Control
                        type="number"
                        value={exec.coilLength}
                        onChange={e =>
                          handleGenericExecutionChange(
                            idx,
                            'coilLength',
                            Number(e.target.value)
                          )
                        }
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <div className="mt-3">
                  <ProgressTracker
                    compact
                    targetLength={drumCap}
                    executionSource={[{ plannedLength: exec.totalLength || 0 }]}
                  />
                </div>
              </Card.Body>
            </Card>
          );
        })}
      </div>
    );
  };

  const calculateRopeCapacity = (upstreams, product) => {
    if (!upstreams.length) return { max: 0, perColor: {} };

    // 1️⃣ total lungime per culoare
    const totalByColor = {};
    upstreams.forEach(u => {
      if (!u.color) return;
      totalByColor[u.color] =
        (totalByColor[u.color] || 0) + (u.plannedLength || 0);
    });

    // 2️⃣ număr conductori per culoare (din conductor_isolation)
    const conductorsByColor = {};
    product.phases
      .find(p => p.name === 'conductor_isolation')
      ?.conductors.forEach(c => {
        const color = c.isolation.color;
        conductorsByColor[color] = (conductorsByColor[color] || 0) + 1;
      });

    // 3️⃣ capacitate per culoare
    const capacityPerColor = {};
    Object.keys(totalByColor).forEach(color => {
      const count = conductorsByColor[color] || 1;
      capacityPerColor[color] = totalByColor[color] / count;
    });

    // 4️⃣ limita finală
    const max = Math.min(...Object.values(capacityPerColor));

    return { max, perColor: capacityPerColor };
  };

  const RopeProgress = ({ phase, upstreams, product }) => {
    const { max } = calculateRopeCapacity(upstreams, product);

    const planned = phase.execution.reduce(
      (s, e) => s + (parseFloat(e.plannedLength) || 0),
      0
    );

    const percent = max > 0 ? (planned / max) * 100 : 0;
    const remaining = Math.max(max - planned, 0);

    return (
      <div>
        <small className="text-muted">
          {planned.toFixed(1)} m / {max.toFixed(1)} m
        </small>

        <div className="progress mt-1" style={{ height: '8px' }}>
          <div
            className={`progress-bar ${
              percent >= 100
                ? 'bg-success'
                : percent >= 90
                  ? 'bg-warning'
                  : 'bg-info'
            }`}
            style={{ width: `${Math.min(percent, 100)}%` }}
          />
        </div>

        <div className="small text-secondary mt-1">
          Remaining: {remaining.toFixed(1)} m
        </div>
      </div>
    );
  };

  const getRequiredRopeColors = product => {
    const isoPhase = product.phases.find(p => p.name === 'conductor_isolation');

    if (!isoPhase?.conductors) return [];

    return [...new Set(isoPhase.conductors.map(c => c.isolation.color))];
  };

  const getSelectedUpstreamColors = upstreams => {
    return [...new Set(upstreams.map(u => u.color).filter(Boolean))];
  };

  const renderRopePhase = () => {
    const upstreams = selectedUpstream?.[productId]?.[phase.name] || [];

    const hasUpstream = upstreams.length > 0;
    const requiredColors = getRequiredRopeColors(product);
    const selectedColors = getSelectedUpstreamColors(upstreams);
    const hasAllRequiredColors = requiredColors.every(color =>
      selectedColors.includes(color)
    );

    const ropeDiameter = Number(phase?.diameter || 0);
    const itemKey = 'default';
    const preset = getDrumPreset(productId, phase.name, itemKey);

    return (
      <>
        <Row className="g-3 mb-3">
          {Object.entries(phase)
            .filter(
              ([key]) => !['name', 'execution', 'dependsOn'].includes(key)
            )
            .map(([key, value]) => (
              <Col md={6} key={key}>
                <Form.Group>
                  <Form.Label className="text-capitalize">
                    {key.replaceAll('_', ' ')}
                  </Form.Label>
                  <Form.Control
                    value={value}
                    readOnly
                    disabled
                    className="bg-light"
                  />
                </Form.Group>
              </Col>
            ))}
        </Row>

        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
          <h6 className="fw-semibold mb-0">Rope Execution Plan</h6>

          <HeaderProgressSlot>
            {hasUpstream && (
              <RopeProgress
                phase={phase}
                upstreams={upstreams}
                product={product}
              />
            )}
          </HeaderProgressSlot>

          <Button
            variant={
              isEditPhase || (hasAllRequiredColors && !upstreamFullyAllocated)
                ? 'outline-primary'
                : 'secondary'
            }
            size="sm"
            disabled={!isEditPhase && (!hasAllRequiredColors || upstreamFullyAllocated)}
            title={
              upstreamFullyAllocated
                ? 'Selected conductors are fully allocated — no remaining length to plan'
                : undefined
            }
            onClick={() => handleAddExecution()}
          >
            + Add Rope Execution
          </Button>
        </div>

        <DrumQuickPresetSelector
          title="Quick drum preset"
          reconcile={reconcile}
          preset={preset}
          onSelectMaterial={material =>
            setDrumPresetMaterial(productId, phase.name, itemKey, material)
          }
          onSelectDiameter={diameter =>
            setDrumPresetDiameter(productId, phase.name, itemKey, diameter)
          }
          drumLibrary={drumLibrary}
        />

        {!hasUpstream && !isEditPhase && (
          <p className="text-danger small fst-italic mb-3">
            Select at least one conductor drum from the previous phase.
          </p>
        )}

        {phase.execution.length ? (
          phase.execution.map((exec, idx) => {
            if (exec._locked) {
              return (
                <Card
                  className={`mb-2 ${
                    reconcile && reconcileMovedFromLabel(exec)
                      ? 'border-warning'
                      : 'border-success'
                  }`}
                  key={exec.id || idx}
                  style={{
                    borderLeft: `5px solid ${
                      reconcile && reconcileMovedFromLabel(exec)
                        ? '#ffc107'
                        : '#198754'
                    }`,
                    opacity: 0.9,
                    cursor:
                      reconcile && !!exec.tokenId
                        ? 'grab'
                        : 'default'
                  }}
                  draggable={false}
                  onDragStart={
                    reconcile && !!exec.tokenId
                      ? e =>
                          e.dataTransfer.setData(
                            'text/plain',
                            `${String(exec.tokenId)}|${reconcileHome}`
                          )
                      : undefined
                  }
                >
                  <Card.Header className="bg-success-subtle d-flex align-items-center gap-2">
                    {reconcile && exec.tokenId && (
                      <span
                        draggable
                        onDragStart={e => {
                          e.dataTransfer.setData(
                            'text/plain',
                            `|`
                          );
                          onDragTokenChange?.(String(exec.tokenId));
                        }}
                        onDragEnd={() => onDragTokenChange?.(null)}
                        title="Drag this drum to another product or conductor"
                        className="px-1 text-700"
                        style={{ cursor: 'grab', userSelect: 'none', lineHeight: 1 }}
                      >
                        &#10303;
                      </span>
                    )}
                    <Badge bg={exec._merged ? 'info' : 'success'}>
              {exec._merged ? 'MERGED' : 'PRODUCED'}
            </Badge>
                    <span className="fw-semibold">
                      {Number(
                        exec._producedLength || exec.plannedLength || 0
                      ).toLocaleString()}{' '}
                      m
                    </span>
                    {exec.machineId && <Badge bg="dark">{exec.machineId}</Badge>}
                    {reconcile && reconcileMovedFromLabel(exec) && (
                      <Badge bg="warning" text="dark" className="fs-10 ms-auto">
                        moved from {reconcileMovedFromLabel(exec)}
                      </Badge>
                    )}
                  </Card.Header>
                  <Card.Body className="py-2">
                    <div className="text-muted fs-10 fst-italic">
                      {mergedLockedNote(exec)}
                    </div>
                    {renderReconcileSwap(exec)}
                    {reconcile && exec._produced && (
                      <ReconcileDrumLength
                        exec={exec}
                        jobId={reconcileHome}
                        shares={
                          reconcileDrumShares.get(String(exec.tokenId)) || []
                        }
                        reconcileLen={reconcileLen}
                        setReconcileLength={setReconcileLength}
                        otherDrums={reconcileShares.filter(
                          d =>
                            d.phase === phase.name &&
                            d.key !== `${productId}::${exec.tokenId}`
                        )}
                      />
                    )}
                    <DrumCorrections exec={exec} />
                  </Card.Body>
                </Card>
              );
            }
            const selectedMachine = String(exec.machineId || '').trim();
            const machineMaxDrum = getMachineMaxDrum(selectedMachine);

            const hasDrumSelected =
              String(exec.drum_material || '').trim() !== '' &&
              Number(exec.drum_diameter) > 0;

            const drumTooLarge =
              machineMaxDrum &&
              Number(exec.drum_diameter || 0) > machineMaxDrum;

            const estimatedLength =
              hasDrumSelected && ropeDiameter > 0
                ? getPhaseEstimatedLength({
                    phaseDiameter: ropeDiameter,
                    drumMaterial: exec.drum_material,
                    drumDiameter: exec.drum_diameter,
                    drumLibrary
                  })
                : null;

            return (
              <Card className="mb-2" key={exec.id}>
                <Card.Header className="d-flex flex-wrap justify-content-between align-items-center gap-2">
                  <div className="d-flex align-items-center gap-3 flex-wrap w-100">
                    <div>
                      <span>Rope Execution #{idx + 1}</span>
  
                      {Array.isArray(exec.fromUpstreamIds) && (
                        <small className="text-muted ms-2">
                          From {exec.fromUpstreamIds.length} conductors
                        </small>
                      )}
                    </div>

                    <div className="d-flex align-items-center gap-2 flex-wrap ms-auto me-2">
                      {hasDrumSelected && (
                        <Button
                          size="sm"
                          variant="success"
                          onClick={() => {
                            const maxLength = calculateRopeCapacity(
                              upstreams,
                              product
                            );

                            handleRopePlannedLengthChange(
                              idx,
                              Number(maxLength.max)
                            );
                          }}
                        >
                          Plan
                        </Button>
                      )}

                      {estimatedLength ? (
                        <>
                          <Badge bg="success">
                            Estimated: {round(estimatedLength, 0)} m
                          </Badge>
                          {(() => {
                            const w =
                              num(exec?.plannedLength) > 0
                                ? computeWeightForExec(
                                    phase?.name,
                                    num(exec.plannedLength),
                                    product,
                                    exec
                                  )
                                : null;
                            return w ? (
                              <Badge bg="dark" className="ms-1">
                                {w.totalKg} kg
                              </Badge>
                            ) : null;
                          })()}
                        </>
                      ) : hasDrumSelected ? (
                        <Badge bg="secondary">No estimate</Badge>
                      ) : null}
                    </div>

                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => handleRemoveExecution(null, idx)}
                    >
                      Delete
                    </Button>
                  </div>
                </Card.Header>

                <Card.Body>
                  {exec.fromUpstreamColors?.length > 0 && (
                    <div className="mb-2">
                      {exec.fromUpstreamColors.map(c => (
                        <Badge key={c} bg="secondary" className="me-1">
                          {c}
                        </Badge>
                      ))}
                    </div>
                  )}

                  <Row className="g-2">
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Machine<ExecWarning exec={exec} product={product} phase={phase} field="machine" /></Form.Label>
                        <Form.Select
                          value={exec.machineId || ''}
                          onChange={e =>
                            handleGenericExecutionChange(
                              idx,
                              'machineId',
                              e.target.value
                            )
                          }
                        >
                          <option value="">Select machine</option>
                          {MACHINE_OPTIONS.map(m => (
                            <option key={m.value} value={m.value}>
                              {m.label}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>

                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Length (m)<ExecWarning exec={exec} product={product} phase={phase} field="length" /></Form.Label>
                        <Form.Control
                          type="number"
                          step="0.1"
                          value={exec.plannedLength || ''}
                          onChange={e =>
                            handleRopePlannedLengthChange(
                              idx,
                              Number(e.target.value)
                            )
                          }
                        />
                      </Form.Group>
                    </Col>

                    <Col lg={6} md={6} sm={12}>
                      <Form.Group>
                        <Form.Label>Drum Material</Form.Label>
                        <Form.Select
                          size="sm"
                          value={exec.drum_material || ''}
                          onChange={e =>
                            handleGenericExecutionChange(
                              idx,
                              'drum_material',
                              e.target.value
                            )
                          }
                        >
                          <option value="">Select material</option>
                          <option value="wood">Wood</option>
                          <option value="metal">Metal</option>
                          <option value="plastic">Plastic</option>
                        </Form.Select>
                      </Form.Group>
                    </Col>

                    <Col lg={6} md={6} sm={12}>
                      <Form.Group>
                        <Form.Label>Drum Ø (mm)<ExecWarning exec={exec} product={product} phase={phase} field="drum" /></Form.Label>
                        <Form.Control
                          size="sm"
                          type="number"
                          step="1"
                          value={exec.drum_diameter || ''}
                          onChange={e =>
                            handleGenericExecutionChange(
                              idx,
                              'drum_diameter',
                              Number(e.target.value)
                            )
                          }
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <div className="mt-2 small text-muted">
                    Rope Ø used for estimate: <b>{round(ropeDiameter, 3)}</b> mm
                    {machineMaxDrum ? (
                      <>
                        {' '}
                        | Machine max drum: <b>{machineMaxDrum}</b> mm
                      </>
                    ) : null}
                    {drumTooLarge ? (
                      <>
                        {' '}
                        |{' '}
                        <span className="text-danger">
                          Drum exceeds machine limit
                        </span>
                      </>
                    ) : null}
                  </div>
                </Card.Body>
              </Card>
            );
          })
        ) : (
          <p className="fst-italic text-muted">No rope executions added yet.</p>
        )}
      </>
    );
  };

  const buildRequiredMap = (wireDiameter = [], numberOfWires = []) => {
    const wd = Array.isArray(wireDiameter) ? wireDiameter : wireDiameter != null ? [wireDiameter] : [];
    const nw = Array.isArray(numberOfWires) ? numberOfWires : numberOfWires != null ? [numberOfWires] : [];
    const map = {};
    wd.forEach((d, i) => {
      map[Number(d)] = Number(nw[i]) || 0;
    });
    return map;
  };

  const countByDiameter = drums => {
    if (!Array.isArray(drums)) return {};
    return drums.reduce((acc, d) => {
      acc[d.wireDiameter] = (acc[d.wireDiameter] || 0) + 1;
      return acc;
    }, {});
  };

  const renderPreStrandingMixedConductorSections = () => {
    if (phase.name !== 'pre_stranding') return null;
    if (!Array.isArray(phase.rope)) return null;

    const estimateRopeSection = rope =>
      rope.wireDiameter.reduce((sum, d, i) => {
        const r = d / 2;
        return sum + rope.numberOfWires[i] * Math.PI * r * r;
      }, 0);

    const getTargetLengthForRope = (rope, product, allRopes) => {
      const sections = allRopes.map(r => estimateRopeSection(r));
      const minSection = Math.min(...sections);
      const thisSection = estimateRopeSection(rope);

      if (thisSection === minSection) {
        return product.length;
      }

      return product.length * product.numberOfConductors;
    };

    const selectedUpstreams = selectedUpstream?.[productId]?.[phase.name] || [];

    return (
      <>
        {phase.rope.map((rope, idx) => {
          const executionsForRope = (phase.execution || []).filter(
            e => e.ropeIndex === idx
          );

          const selectedForThisRope = selectedUpstreams.filter(
            u => u.ropeIndex === idx
          );

          const canAddExecution =
            phase.dependsOn === null || selectedForThisRope.length > 0;

          const targetLength = getTargetLengthForRope(
            rope,
            product,
            phase.rope
          );

          const plannedTotal = executionsForRope.reduce(
            (s, e) => s + Number(e.plannedLength || 0),
            0
          );

          const remaining = Math.max(targetLength - plannedTotal, 0);
          const finished = remaining <= 0;

          const formatStructure = rope.wireDiameter
            .map((d, i) => `${rope.numberOfWires[i]}×Ø${d}`)
            .join(' + ');

          const itemKey = `rope-${idx}`;
          const preset = getDrumPreset(productId, phase.name, itemKey);
          const ropeDiameter = Number(rope?.diameter || phase?.diameter || 0);

          return (
            <Card key={idx} className="mb-4 border-secondary-subtle shadow-sm">
              <Card.Header className="d-flex flex-wrap justify-content-between align-items-center gap-2">
                <div className="fw-semibold">
                  Rope {idx + 1} — {formatStructure}
                  {finished && (
                    <Badge bg="success" className="ms-2">
                      Finished
                    </Badge>
                  )}
                </div>

                <HeaderProgressSlot>
                  <ProgressTracker
                    mode="rope"
                    targetLength={targetLength}
                    executionSource={executionsForRope.map(e => ({
                      plannedLength: Number(e.plannedLength || 0)
                    }))}
                  />
                </HeaderProgressSlot>

                <Button
                  variant="outline-primary"
                  size="sm"
                  disabled={!canAddExecution || finished}
                  onClick={() => {
                    const normalizedDrumMaterial = String(
                      preset?.material || ''
                    )
                      .trim()
                      .toLowerCase();

                    const newExec = {
                      id: Date.now(),
                      ropeIndex: idx,
                      fromUpstreamIds: selectedForThisRope.map(u => u.id),
                      machineId: '',
                      plannedLength: '',
                      drum_material: normalizedDrumMaterial,
                      drum_diameter: Number(preset?.diameter) || ''
                    };

                    onChange(productId, phase.name, 'execution', [
                      ...(phase.execution || []),
                      newExec
                    ]);
                  }}
                >
                  + Add Execution
                </Button>
              </Card.Header>

              <Card.Body>
                <DrumQuickPresetSelector
                  title="Quick drum preset"
          reconcile={reconcile}
                  preset={preset}
                  onSelectMaterial={material =>
                    setDrumPresetMaterial(
                      productId,
                      phase.name,
                      itemKey,
                      material
                    )
                  }
                  onSelectDiameter={diameter =>
                    setDrumPresetDiameter(
                      productId,
                      phase.name,
                      itemKey,
                      diameter
                    )
                  }
                  drumLibrary={drumLibrary}
                />

                {!reconcile && (
                <Row className="g-2 mb-3">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Material</Form.Label>
                      <Form.Control value={rope.material} disabled />
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Type</Form.Label>
                      <Form.Control value={rope.type} disabled />
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Wire Diameters (mm)</Form.Label>
                      <Form.Control
                        value={rope.wireDiameter.join(', ')}
                        disabled
                      />
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Number of Wires</Form.Label>
                      <Form.Control
                        value={rope.numberOfWires.join(', ')}
                        disabled
                      />
                    </Form.Group>
                  </Col>
                </Row>
                )}

                {executionsForRope.length ? (
                  executionsForRope.map((exec, execIdx) => {
                    const realExecIndex = phase.execution.findIndex(
                      e => e.id === exec.id
                    );
                    if (exec._locked)
                      return renderReconcileLockedCard(exec, realExecIndex);

                    const selectedMachine = String(exec.machineId || '').trim();
                    const machineMaxDrum = getMachineMaxDrum(selectedMachine);

                    const hasDrumSelected =
                      String(exec.drum_material || '').trim() !== '' &&
                      Number(exec.drum_diameter) > 0;

                    const drumTooLarge =
                      machineMaxDrum &&
                      Number(exec.drum_diameter || 0) > machineMaxDrum;

                    const estimatedLength =
                      hasDrumSelected && ropeDiameter > 0
                        ? getPhaseEstimatedLength({
                            phaseDiameter: ropeDiameter,
                            drumMaterial: exec.drum_material,
                            drumDiameter: exec.drum_diameter,
                            drumLibrary
                          })
                        : null;

                    return (
                      <Card key={exec.id} className="mb-2">
                        <Card.Header className="d-flex flex-wrap justify-content-between align-items-center gap-2">
                          <div className="d-flex align-items-center gap-3 flex-wrap w-100">
                            <div>
                              Execution #{execIdx + 1}
                                        {exec.fromUpstreamIds?.length > 0 && (
                                <div className="mt-1">
                                  {exec.fromUpstreamIds.map(id => {
                                    const u = selectedUpstreams.find(
                                      x => x.id === id
                                    );
                                    return (
                                      <Badge
                                        key={id}
                                        bg="info"
                                        className="me-1"
                                      >
                                        Ø {u?.wireDiameter} mm
                                      </Badge>
                                    );
                                  })}
                                </div>
                              )}
                            </div>

                            <div className="d-flex align-items-center gap-2 flex-wrap ms-auto me-2">
                              {hasDrumSelected && (
                                <Button
                                  size="sm"
                                  variant="success"
                                  onClick={() => {
                                    handleGenericExecutionChange(
                                      realExecIndex,
                                      'plannedLength',
                                      Number(remaining)
                                    );
                                  }}
                                >
                                  Plan
                                </Button>
                              )}

                              {estimatedLength ? (
                                <Badge bg="success">
                                  Estimated: {round(estimatedLength, 0)} m
                                </Badge>
                              ) : hasDrumSelected ? (
                                <Badge bg="secondary">No estimate</Badge>
                              ) : null}
                              {/* Weight is independent of the drum-capacity
                                  estimate — show it whenever a length is planned,
                                  even when the drum estimate can't be computed. */}
                              {(() => {
                                const w =
                                  num(exec?.plannedLength) > 0
                                    ? computeWeightForExec(
                                        phase?.name,
                                        num(exec.plannedLength),
                                        product,
                                        exec
                                      )
                                    : null;
                                return w ? (
                                  <Badge bg="dark" className="ms-1">
                                    {w.totalKg} kg
                                  </Badge>
                                ) : null;
                              })()}
                            </div>

                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() =>
                                handleRemoveExecution(null, realExecIndex)
                              }
                            >
                              Delete
                            </Button>
                          </div>
                        </Card.Header>

                        <Card.Body>
                          <Row className="g-2">
                            <Col md={6}>
                              <Form.Group>
                                <Form.Label>Machine<ExecWarning exec={exec} product={product} phase={phase} field="machine" /></Form.Label>
                                <Form.Select
                                  size="sm"
                                  value={exec.machineId || ''}
                                  onChange={e =>
                                    handleGenericExecutionChange(
                                      realExecIndex,
                                      'machineId',
                                      e.target.value
                                    )
                                  }
                                >
                                  <option value="">Select machine</option>
                                  {MACHINE_OPTIONS.map(m => (
                                    <option key={m.value} value={m.value}>
                                      {m.label}
                                    </option>
                                  ))}
                                </Form.Select>
                              </Form.Group>
                            </Col>

                            <Col md={6}>
                              <Form.Group>
                                <Form.Label>Length (m)<ExecWarning exec={exec} product={product} phase={phase} field="length" /></Form.Label>
                                <Form.Control
                                  size="sm"
                                  type="number"
                                  step="0.1"
                                  value={exec.plannedLength || ''}
                                  onChange={e =>
                                    handleGenericExecutionChange(
                                      realExecIndex,
                                      'plannedLength',
                                      Number(e.target.value)
                                    )
                                  }
                                />
                              </Form.Group>
                            </Col>

                            <Col md={6}>
                              <Form.Group>
                                <Form.Label>Drum Material</Form.Label>
                                <Form.Select
                                  size="sm"
                                  value={exec.drum_material || ''}
                                  onChange={e =>
                                    handleGenericExecutionChange(
                                      realExecIndex,
                                      'drum_material',
                                      e.target.value
                                    )
                                  }
                                >
                                  <option value="">Drum material</option>
                                  <option value="wood">Wood</option>
                                  <option value="metal">Metal</option>
                                  <option value="plastic">Plastic</option>
                                </Form.Select>
                              </Form.Group>
                            </Col>

                            <Col md={6}>
                              <Form.Group>
                                <Form.Label>Drum Ø (mm)<ExecWarning exec={exec} product={product} phase={phase} field="drum" /></Form.Label>
                                <Form.Control
                                  size="sm"
                                  type="number"
                                  step="1"
                                  value={exec.drum_diameter || ''}
                                  onChange={e =>
                                    handleGenericExecutionChange(
                                      realExecIndex,
                                      'drum_diameter',
                                      Number(e.target.value)
                                    )
                                  }
                                />
                              </Form.Group>
                            </Col>
                          </Row>

                          <div className="mt-2 small text-muted">
                            Rope Ø used for estimate:{' '}
                            <b>{round(ropeDiameter, 3)}</b> mm
                            {machineMaxDrum ? (
                              <>
                                {' '}
                                | Machine max drum: <b>{machineMaxDrum}</b> mm
                              </>
                            ) : null}
                            {drumTooLarge ? (
                              <>
                                {' '}
                                |{' '}
                                <span className="text-danger">
                                  Drum exceeds machine limit
                                </span>
                              </>
                            ) : null}
                          </div>
                        </Card.Body>
                      </Card>
                    );
                  })
                ) : (
                  <p className="text-muted fst-italic">
                    No executions added yet.
                  </p>
                )}
              </Card.Body>
            </Card>
          );
        })}
      </>
    );
  };

  const renderPreStrandingPhase = () => {
    if (phase.name !== 'pre_stranding') return null;

    if (Array.isArray(phase.rope)) {
      return renderPreStrandingMixedConductorSections();
    }

    const upstreamRaw = selectedUpstream?.[productId]?.[phase.name];
    const upstreams = Array.isArray(upstreamRaw)
      ? upstreamRaw
      : upstreamRaw
        ? [upstreamRaw]
        : [];

    const hasDependency = !!phase.dependsOn;

    const unwindingPhase = product.phases.find(p => p.name === 'unwinding');
    const allUpstreams = unwindingPhase?.execution || [];

    const requiredMap = buildRequiredMap(
      phase.wireDiameter,
      phase.numberOfWires
    );

    const selectedCount = countByDiameter(upstreams);

    const hasAllRequired = hasDependency
      ? Object.entries(requiredMap).every(
          ([diam, qty]) => (selectedCount[diam] || 0) >= qty
        )
      : true;

    const consumedLengths = {};

    (phase.execution || []).forEach(exec => {
      // FLOW: per-drum takes (a wire can span a short drum + a phantom) — bill
      // each drum only its take, so nothing is counted over its capacity.
      if (Array.isArray(exec.fromUpstreams) && exec.fromUpstreams.length) {
        exec.fromUpstreams.forEach(u => {
          if (u?.id == null) return;
          consumedLengths[u.id] =
            (consumedLengths[u.id] || 0) + Number(u.take || 0);
        });
        return;
      }

      // FALLBACK (legacy bundle): full planned length per drum
      const len = parseFloat(exec.plannedLength) || 0;
      if (Array.isArray(exec.fromUpstreamIds)) {
        exec.fromUpstreamIds.forEach(id => {
          consumedLengths[id] = (consumedLengths[id] || 0) + len;
        });
      }
    });

    const freeUpstreams = allUpstreams.filter(u => {
      const maxLen = parseFloat(u.plannedLength) || 0;
      const consumed = consumedLengths[u.id] || 0;
      return consumed < maxLen;
    });

    const canStillProduce = hasDependency
      ? Object.entries(requiredMap).every(
          ([diam, qty]) =>
            freeUpstreams.filter(
              u =>
                u.wireDiameter === Number(diam) &&
                getRemainingForUpstream(u, consumedLengths) > 0
            ).length >= qty
        )
      : true;

    const itemKey = 'default';
    const preset = getDrumPreset(productId, phase.name, itemKey);
    const phaseDiameter = Number(phase?.diameter || 0);

    // pentru pre-stranding simplu, target-ul total este
    // lungimea produsului × numărul de conductori
    const targetLength =
      Number(product?.length || 0) * Number(product?.numberOfConductors || 0);

    const handleAddPreStrandingExecution = () => {
      let selected = [];

      if (hasDependency) {
        Object.entries(requiredMap).forEach(([diam, qty]) => {
          upstreams
            .filter(u => {
              if (u.wireDiameter !== Number(diam)) return false;

              const cap = num(u.plannedLength);
              const used = num(consumedLengths[u.id]);
              return cap - used > 0;
            })
            .slice(0, qty)
            .forEach(u => selected.push(u));
        });

        if (!selected.length) return;
      }

      const normalizedDrumMaterial = String(preset?.material || '')
        .trim()
        .toLowerCase();

      const newExec = {
        id: Date.now(),
        fromUpstreamIds: selected.map(u => u.id),
        machineId: '',
        plannedLength: '',
        drum_material: normalizedDrumMaterial,
        drum_diameter: Number(preset?.diameter) || ''
      };

      onChange(productId, phase.name, 'execution', [
        ...(phase.execution || []),
        newExec
      ]);
    };

    return (
      <>
        <Row className="g-3 mb-3">
          {Object.entries(phase)
            .filter(([k]) => !['name', 'execution', 'dependsOn'].includes(k))
            .map(([k, v]) => (
              <Col md={6} key={k}>
                <Form.Group>
                  <Form.Label className="text-capitalize">
                    {k.replaceAll('_', ' ')}
                  </Form.Label>
                  <Form.Control value={v} readOnly disabled />
                </Form.Group>
              </Col>
            ))}
        </Row>

        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
          <h6 className="fw-semibold mb-0">Pre-Stranding Execution Plan</h6>

          <HeaderProgressSlot>
            <ProgressTracker
              targetLength={targetLength}
              executionSource={(phase.execution || []).map(exec => ({
                plannedLength: Number(exec.plannedLength || 0)
              }))}
            />
          </HeaderProgressSlot>

          <Button
            variant={hasAllRequired ? 'outline-primary' : 'secondary'}
            size="sm"
            disabled={!hasAllRequired || !canStillProduce}
            onClick={handleAddPreStrandingExecution}
          >
            + Add Execution
          </Button>
        </div>

        <DrumQuickPresetSelector
          title="Quick drum preset"
          reconcile={reconcile}
          preset={preset}
          onSelectMaterial={material =>
            setDrumPresetMaterial(productId, phase.name, itemKey, material)
          }
          onSelectDiameter={diameter =>
            setDrumPresetDiameter(productId, phase.name, itemKey, diameter)
          }
          drumLibrary={drumLibrary}
        />

        {hasDependency && !hasAllRequired && canStillProduce && (
          <p className="text-danger small fst-italic">
            Select at least{' '}
            {Object.entries(requiredMap)
              .map(([d, q]) => `${q}×Ø${d}`)
              .join(', ')}{' '}
            from upstream.
          </p>
        )}

        {hasDependency && !canStillProduce && (
          <p className="text-success small fst-italic">
            All required drums are fully consumed. Phase finished.
          </p>
        )}

        {(phase.execution || []).length ? (
          (phase.execution || []).map((exec, idx) => {
            if (exec._locked) return renderReconcileLockedCard(exec, idx);
            const selectedMachine = String(exec.machineId || '').trim();
            const machineMaxDrum = getMachineMaxDrum(selectedMachine);

            const hasDrumSelected =
              String(exec.drum_material || '').trim() !== '' &&
              Number(exec.drum_diameter) > 0;

            const drumTooLarge =
              machineMaxDrum &&
              Number(exec.drum_diameter || 0) > machineMaxDrum;

            const estimatedLength =
              hasDrumSelected && phaseDiameter > 0
                ? getPhaseEstimatedLength({
                    phaseDiameter,
                    drumMaterial: exec.drum_material,
                    drumDiameter: exec.drum_diameter,
                    drumLibrary
                  })
                : null;

            // target rămas pentru execuția curentă,
            // excluzând cât are deja ea
            const totalOtherPlanned = (phase.execution || []).reduce(
              (sum, e, i) => {
                if (i === idx) return sum;
                return sum + (Number(e.plannedLength) || 0);
              },
              0
            );

            const remainingTargetForThisExec = Math.max(
              targetLength - totalOtherPlanned,
              0
            );

            // consumed by OTHER executions (flow-aware: counts per-drum takes)
            const consumedLengthsExcludingCurrent = buildConsumedMapForPhase(
              phase,
              { excludeExecutionId: exec.id }
            );

            // Per-wire capacity across the SELECTED upstream drums. A wire of a
            // given Ø may be fed by several drums (e.g. a short real drum + a
            // phantom remainder), so capacity is the TOTAL remaining for that Ø
            // divided by the number of wires of that Ø — NOT the smallest drum
            // (which would wrongly cap the rope at a tiny phantom's length).
            let wireCapacity = Infinity;
            Object.entries(requiredMap).forEach(([diamStr, qty]) => {
              const diam = Number(diamStr);
              const count = Number(qty) || 0;
              if (count <= 0) return;
              const totalRem = upstreams
                .filter(u => Number(u.wireDiameter) === diam)
                .reduce(
                  (s, u) =>
                    s +
                    Math.max(
                      num(u.plannedLength) -
                        (num(consumedLengthsExcludingCurrent[u.id]) || 0),
                      0
                    ),
                  0
                );
              wireCapacity = Math.min(wireCapacity, totalRem / count);
            });

            const upstreamPlanLength = hasDependency
              ? Number.isFinite(wireCapacity)
                ? wireCapacity
                : 0
              : remainingTargetForThisExec;

            const planLength = Math.max(
              0,
              Math.min(
                remainingTargetForThisExec,
                upstreamPlanLength || remainingTargetForThisExec
              )
            );

            if (exec.isPhantomSource && exec.phantomId) {
              return (
                <Card key={exec.id} className="mb-2 border-info" style={{ borderLeft: '5px solid #0dcaf0' }}>
                  <Card.Header className="bg-info-subtle d-flex justify-content-between align-items-center">
                    <div className="d-flex align-items-center gap-2">
                      <Badge bg="info">PHANTOM</Badge>
                      <span className="fw-semibold">{Number(exec.plannedLength || 0).toLocaleString()} m</span>
                      {exec.phantomColor && <Badge bg="dark">{exec.phantomColor}</Badge>}
                      <span className="text-muted fs-10 font-monospace">{exec.phantomId}</span>
                    </div>
                    <Button variant="outline-danger" size="sm" onClick={() => handleRemoveExecution(null, idx)}>Remove</Button>
                  </Card.Header>
                  <Card.Body className="py-2">
                    <Row className="g-2">
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label>Machine<ExecWarning exec={exec} product={product} phase={phase} field="machine" /></Form.Label>
                          <Form.Control size="sm" value="PHANTOM" readOnly disabled className="bg-light fw-semibold text-info" />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label>Length (m) — from phantom</Form.Label>
                          <Form.Control size="sm" type="number" value={exec.plannedLength || ''} disabled className="bg-light" />
                        </Form.Group>
                      </Col>
                    </Row>
                    {exec.phantomNote && <div className="text-muted fs-10 mt-2 fst-italic">{exec.phantomNote}</div>}
                  </Card.Body>
                </Card>
              );
            }

            return (
              <Card key={exec.id} className="mb-2">
                <Card.Header className="d-flex flex-wrap justify-content-between align-items-center gap-2">
                  <div className="d-flex align-items-center gap-3 flex-wrap w-100">
                    <div>
                      <span>Execution #{idx + 1}</span>
                        <small className="text-muted ms-2">
                        {(exec.fromUpstreamIds || []).length} drums
                      </small>
                    </div>

                    <div className="d-flex align-items-center gap-2 flex-wrap ms-auto me-2">
                      <Button
                        size="sm"
                        variant="success"
                        disabled={planLength <= 0}
                        onClick={() => {
                          handlePreStrandPlannedLengthChange(
                            idx,
                            Number(planLength)
                          );
                        }}
                      >
                        Plan
                      </Button>

                      {estimatedLength ? (
                        <>
                          <Badge bg="success">
                            Estimated: {round(estimatedLength, 0)} m
                          </Badge>
                          {(() => {
                            const w =
                              num(exec?.plannedLength) > 0
                                ? computeWeightForExec(
                                    phase?.name,
                                    num(exec.plannedLength),
                                    product,
                                    exec
                                  )
                                : null;
                            return w ? (
                              <Badge bg="dark" className="ms-1">
                                {w.totalKg} kg
                              </Badge>
                            ) : null;
                          })()}
                        </>
                      ) : hasDrumSelected ? (
                        <Badge bg="secondary">No estimate</Badge>
                      ) : (
                        <Badge bg="light" text="dark">
                          Select drum
                        </Badge>
                      )}
                    </div>

                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => handleRemoveExecution(null, idx)}
                    >
                      Delete
                    </Button>
                  </div>
                </Card.Header>

                <Card.Body>
                  <Row className="g-2">
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Machine<ExecWarning exec={exec} product={product} phase={phase} field="machine" /></Form.Label>
                        <Form.Select
                          size="sm"
                          value={exec.machineId || ''}
                          onChange={e =>
                            handleGenericExecutionChange(
                              idx,
                              'machineId',
                              e.target.value
                            )
                          }
                        >
                          <option value="">Select machine</option>
                          {MACHINE_OPTIONS.map(m => (
                            <option key={m.value} value={m.value}>
                              {m.label}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>

                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Length (m)<ExecWarning exec={exec} product={product} phase={phase} field="length" /></Form.Label>
                        <Form.Control
                          size="sm"
                          type="number"
                          step="0.1"
                          value={exec.plannedLength || ''}
                          onChange={e =>
                            handlePreStrandPlannedLengthChange(
                              idx,
                              Number(e.target.value)
                            )
                          }
                        />
                      </Form.Group>
                    </Col>

                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Drum Material</Form.Label>
                        <Form.Select
                          size="sm"
                          value={exec.drum_material || ''}
                          onChange={e =>
                            handleGenericExecutionChange(
                              idx,
                              'drum_material',
                              e.target.value
                            )
                          }
                        >
                          <option value="">Select material</option>
                          <option value="wood">Wood</option>
                          <option value="metal">Metal</option>
                          <option value="plastic">Plastic</option>
                        </Form.Select>
                      </Form.Group>
                    </Col>

                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Drum Ø (mm)<ExecWarning exec={exec} product={product} phase={phase} field="drum" /></Form.Label>
                        <Form.Control
                          size="sm"
                          type="number"
                          step="1"
                          value={exec.drum_diameter || ''}
                          onChange={e =>
                            handleGenericExecutionChange(
                              idx,
                              'drum_diameter',
                              Number(e.target.value)
                            )
                          }
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <div className="mt-2 small text-muted">
                    Output Ø used for estimate: <b>{round(phaseDiameter, 3)}</b>{' '}
                    mm | Target: <b>{round(targetLength, 0)}</b> m | Remaining
                    for this exec: <b>{round(remainingTargetForThisExec, 0)}</b>{' '}
                    m
                    {machineMaxDrum ? (
                      <>
                        {' '}
                        | Machine max drum: <b>{machineMaxDrum}</b> mm
                      </>
                    ) : null}
                    {drumTooLarge ? (
                      <>
                        {' '}
                        |{' '}
                        <span className="text-danger">
                          Drum exceeds machine limit
                        </span>
                      </>
                    ) : null}
                  </div>
                </Card.Body>
              </Card>
            );
          })
        ) : (
          <p className="fst-italic text-muted">
            No pre-stranding executions added yet.
          </p>
        )}
      </>
    );
  };

  const renderPhaseContent = () => {
    switch (phase.name) {
      case 'unwinding':
        return renderUnwindingPhase();

      case 'pre_stranding':
        return renderPreStrandingPhase();

      case 'rope':
        return renderRopePhase();

      case 'conductor_isolation':
        return renderConductorIsolation();

      case 'coiling':
        return renderCoilingPhase();

      case 'rewinding':
        return renderRewindingPhase();

      default:
        return renderGenericFields();
    }
  };

  return (
    <Card className="mb-2 shadow-sm">
      <Card.Header className="fw-bold text-capitalize d-flex justify-content-between align-items-center">
        <span className="me-3">{phase.name.replaceAll('_', ' ')}</span>

        {/* NU arătăm progress tracker-ul global PENTRU COILING */}
        {phase.name !== 'coiling' &&
          phase.name !== 'rewinding' &&
          phase.name !== 'unwinding' && (
            <div style={{ flex: 1 }}>
              <ProgressTracker
                targetLength={targetLength}
                executionSource={executionSource}
              />
            </div>
          )}
      </Card.Header>

      <Card.Body>{renderPhaseContent()}</Card.Body>
    </Card>
  );
};

const PhaseDependencyPreview = ({
  product,
  phaseName,
  selectedUpstream,
  updateSelectedUpstream
}) => {
  const phase = product?.phases?.find(p => p.name === phaseName);
  const previousPhase =
    phase?.dependsOn && product?.phases?.find(p => p.name === phase.dependsOn);

  if (!phase || !previousPhase) return null;

  const ALLOW_SELECTION = [
    'pre_stranding',
    'rope',
    'conductor_isolation',
    'interior_sheat',
    'armouring',
    'exterior_sheat',
    'coiling',
    'rewinding'
  ];

  const isSelectionEnabled = ALLOW_SELECTION.includes(phaseName);
  // A flat cable's exterior sheath consumes one drum per conductor colour in the
  // same run, so it picks several incoming drums like rope does.
  const isMultiSelect =
    ['rope', 'pre_stranding', 'conductor_isolation'].includes(phaseName) ||
    isFlatSheathPhase(phase, product);

  // =========================
  // previousExecutions (upstreams)
  // =========================
  const previousExecutions = [];

  if (previousPhase.conductors) {
    previousPhase.conductors.forEach(cond => {
      (cond.execution || []).forEach(exec => {
        previousExecutions.push({
          color: cond.isolation.color,
          ...exec
        });
      });
    });
  } else if (previousPhase.execution) {
    const allExec = previousPhase.execution.map((exec, idx) => ({
      ...exec,
      id: exec.id ?? idx + 1
    }));

    if (phaseName === 'coiling') {
      const toCoiling = allExec.filter(e => e.nextPhase === 'coiling');
      previousExecutions.push(...(toCoiling.length ? toCoiling : allExec));
    } else if (phaseName === 'rewinding') {
      const toRew = allExec.filter(e => e.nextPhase === 'rewinding');
      previousExecutions.push(...(toRew.length ? toRew : allExec));
    } else {
      previousExecutions.push(...allExec);
    }
  }

  // inject phantom tokens from _parentTokenIds that aren't in previousExecutions
  if (
    product?._continueMode &&
    product?._existingTokens &&
    product?._parentTokenIds
  ) {
    const existingIds = new Set(
      previousExecutions.map(e => e.tokenId).filter(Boolean)
    );

    for (const tid of product._parentTokenIds) {
      if (!tid.startsWith('PH-') || existingIds.has(tid)) continue;
      const tok = product._existingTokens[tid];
      if (!tok) continue;

      previousExecutions.push({
        id: tid,
        tokenId: tid,
        plannedLength: tok.availableLength || tok.plannedLength || 0,
        color: tok.color || null,
        isPhantom: true,
        phantomData: tok.phantomData || null
      });
    }
  }

  if (!previousExecutions.length) return null;

  // =========================
  // consumed map (NEW + backward compatible)
  // =========================
  const consumedLengths = buildConsumedMapForPhase(phase);

  // In continue mode: enrich consumed with actual token data (reservedLength + consumedLength)
  // so the "remaining" display reflects what's already reserved/consumed in production.
  //
  // When EDITING this phase we still want the REAL launched allocation shown on open
  // (fully / partially allocated as planned) — but it must RECALCULATE as the user edits:
  // deleting a downstream execution has to free the upstream it consumed. So instead of the
  // static token.reservedLength (which only shrinks on backend submit), we recompute each
  // upstream drum's usage from its reservedBy entries, counting an edit-phase reservation
  // ONLY while its downstream execution (matched by taskId) is still present in the wizard.
  // Reservations from OTHER phases always count; a floor at consumedLength never frees what
  // was physically produced. Deleting an exec drops its taskId → its reservation is released.
  // How much of each incoming token already has its downstream PLANNED (not yet
  // produced): sum of child tokens' planned length. Reliable even when reservedLength
  // stayed 0 (plan-ahead / chained plans don't bump it). Drives the "Fully programmed"
  // badge + disables re-selecting an already-fully-planned token.
  const programmedByExec = {};
  if (product?._continueMode && product?._existingTokens) {
    const isEditingThisPhase =
      !!product?._editMode && product?._editPhaseName === phase.name;

    const presentTaskIds = new Set();
    if (isEditingThisPhase) {
      const liveExecs = [];
      if (Array.isArray(phase.execution)) liveExecs.push(...phase.execution);
      if (Array.isArray(phase.conductors))
        for (const c of phase.conductors)
          if (Array.isArray(c.execution)) liveExecs.push(...c.execution);
      for (const e of liveExecs) if (e?.taskId) presentTaskIds.add(e.taskId);
    }

    const tokenList = Object.values(product._existingTokens);

    for (const exec of previousExecutions) {
      if (!exec.tokenId) continue;
      const tok = product._existingTokens[exec.tokenId];
      if (!tok) continue;

      const reserved = num(tok.reservedLength);
      const consumed = num(tok.consumedLength);

      // downstream already planned FROM this token = Σ planned length of its children
      // A child fed by SEVERAL drums at once (flat-cable sheath) records what it
      // took from each one in `parentTakes`; without that a 25 km run would count
      // as 25 km against every drum it touched, including the one it only took
      // 5 km from. Children with no takes recorded bill their whole length, as before.
      let programmed = 0;
      for (const ch of tokenList) {
        const par = Array.isArray(ch?.parents) ? ch.parents.map(String) : [];
        if (!par.includes(String(exec.tokenId))) continue;
        const take = ch?.parentTakes?.[exec.tokenId];
        programmed += take != null ? num(take) : num(ch.plannedLength);
      }
      programmedByExec[exec.id] = programmed;

      let tokenUsed = Math.max(reserved, consumed, programmed);

      if (isEditingThisPhase) {
        let recomputed = 0;
        for (const r of tok.reservedBy || []) {
          if (!r) continue;
          const isEditPhaseRes =
            r.nextPhase === phase.name ||
            String(r.groupKey || '').endsWith(`::${phase.name}`);
          if (isEditPhaseRes) {
            // count only while the downstream execution is still in the wizard
            if (r.taskId && presentTaskIds.has(r.taskId))
              recomputed += num(r.plannedLength);
          } else {
            recomputed += num(r.plannedLength);
          }
        }
        // editing THIS phase re-opens its own children, so don't count them as
        // programmed (they're being re-planned) — fall back to reservedBy recompute.
        tokenUsed = Math.max(consumed, recomputed);
        programmedByExec[exec.id] = 0;
      }

      // use the larger of what buildConsumedMapForPhase found vs actual token usage
      const currentUsed = num(consumedLengths[exec.id]);
      if (tokenUsed > currentUsed) {
        consumedLengths[exec.id] = tokenUsed;
      }
    }
  }

  // =========================
  // selection
  // =========================
  const currentSelectionRaw = selectedUpstream?.[product._id]?.[phaseName];

  const currentSelection = isMultiSelect
    ? Array.isArray(currentSelectionRaw)
      ? currentSelectionRaw
      : []
    : currentSelectionRaw || null;

  const toggleSelect = exec => {
    if (!isSelectionEnabled) return;

    const getExecLength = exec =>
      num(exec.plannedLength) ?? num(exec.plannedLengthInput) ?? 0;

    let cap = getExecLength(exec);
    const used = num(consumedLengths[exec.id]);
    const remaining = Math.max(cap - used, 0);
    if (remaining <= 0) return;

    if (isMultiSelect) {
      const exists = currentSelection.some(e => e.id === exec.id);
      if (exists) {
        updateSelectedUpstream(
          product._id,
          phaseName,
          currentSelection.filter(e => e.id !== exec.id)
        );
      } else {
        updateSelectedUpstream(product._id, phaseName, [
          ...currentSelection,
          exec
        ]);
      }
    } else {
      if (currentSelection?.id === exec.id) {
        updateSelectedUpstream(product._id, phaseName, null);
      } else {
        updateSelectedUpstream(product._id, phaseName, exec);
      }
    }
  };

  return (
    <Card
      className="mb-3 border-0 shadow-sm bg-light-subtle"
      style={{ borderLeft: '5px solid #0d6efd' }}
    >
      <Card.Header className="bg-primary-subtle fw-semibold py-2">
        Incoming executions from "{previousPhase.name.replaceAll('_', ' ')}"
      </Card.Header>

      <Card.Body className="pt-3 pb-2">
        <Row className="g-2">
          {previousExecutions.map((exec, idx) => {
            const getExecLength = exec =>
              num(exec.plannedLength) ?? num(exec.plannedLengthInput) ?? 0;

            let cap = getExecLength(exec);

            // upstream lengths for coiling/rewinding upstream items
            if (exec.coilCount !== undefined && exec.coilLength !== undefined) {
              cap = num(exec.coilCount) * num(exec.coilLength);
            }
            if (
              exec.rewindCount !== undefined &&
              exec.rewindLength !== undefined
            ) {
              cap = num(exec.rewindCount) * num(exec.rewindLength);
            }

            // in continue mode: use actual token available if present
            if (
              product?._continueMode &&
              exec.tokenId &&
              product?._existingTokens
            ) {
              const tok = product._existingTokens[exec.tokenId];
              if (tok) {
                const produced = num(tok.producedLength);
                const available = num(tok.availableLength);
                // What the drum HOLDS is the capacity. Not `Math.max` with the
                // plan, which is what this used to do: that handles the drum
                // that over-ran — 23 660 m against 20 000 is routine and its
                // real length must be offered — but it silently refuses to come
                // DOWN for a drum that came off short. A CYYF 4x16 rope drum
                // planned for 4 120 m and holding 2 108.21 therefore offered
                // 4 120 m of inner sheath, which is 2 012 m of cable that does
                // not exist.
                //
                // `available` is what is still free (produced minus whatever a
                // later phase has already drawn); `produced` is the fallback for
                // a drum nothing has drawn on yet. The planned figure is only
                // used when there is no token to ask, which is create-new mode.
                if (available > 0) cap = available;
                else if (produced > 0) cap = produced;
              }
            }

            const used = num(consumedLengths[exec.id]);
            const remaining = Math.max(cap - used, 0);
            const finished = remaining <= 0;

            // What the drum was SUPPOSED to hold. An operator does not stop the
            // line on the number, so this and `cap` routinely differ by tens of
            // metres — and that difference is exactly what decides how long the
            // next run can be, since a rope is only as long as its shortest core.
            // Worth seeing on the card rather than inferring from three figures.
            const drumToken =
              product?._continueMode && exec.tokenId
                ? product?._existingTokens?.[exec.tokenId] || null
                : null;
            const plannedCap =
              num(drumToken?.plannedLength) ||
              num(exec.plannedLength) ||
              num(exec.plannedLengthInput) ||
              0;
            // Only once something has actually been wound: in create-new there
            // is no drum yet, so planned and cap are the same number and the
            // delta would be noise on every card.
            const capDelta =
              plannedCap > 0 && num(drumToken?.producedLength) > 0
                ? cap - plannedCap
                : null;
            // finished because its downstream is fully PLANNED (not yet produced) —
            // vs consumed/produced. Drives the "Fully programmed" badge.
            const producedTok =
              exec.tokenId && product?._existingTokens?.[exec.tokenId]
                ? num(product._existingTokens[exec.tokenId].producedLength)
                : 0;
            const fullyProgrammed =
              finished &&
              producedTok <= 0 &&
              (programmedByExec[exec.id] || 0) >= cap - 0.5;

            const isSelected = isMultiSelect
              ? currentSelection.some(e => e.id === exec.id) && !finished
              : currentSelection?.id === exec.id && !finished;

            return (
              <Col xs={12} md={6} key={exec.id ?? idx}>
                <div
                  className={`p-3 rounded border shadow-sm h-100 ${
                    finished
                      ? 'border-success bg-success-subtle'
                      : isSelected
                        ? 'border-primary bg-primary-subtle'
                        : 'bg-white'
                  }`}
                  style={{
                    borderLeft: finished
                      ? '6px solid green'
                      : isSelected
                        ? '6px solid #0d6efd'
                        : '4px solid #0d6efd',
                    cursor: finished
                      ? 'not-allowed'
                      : isSelectionEnabled
                        ? 'pointer'
                        : 'default',
                    opacity: finished ? 0.65 : 1
                  }}
                  onClick={() => toggleSelect(exec)}
                >
                  {isSelectionEnabled && (
                    <Form.Check
                      type="checkbox"
                      disabled={finished}
                      checked={isSelected}
                      onChange={() => toggleSelect(exec)}
                      className="mb-2"
                      labelClassName="w-100" // ⭐ FOARTE IMPORTANT
                      label={
                        <div className="d-flex justify-content-between align-items-center w-100">
                          {/* STÂNGA */}
                          <div>
                            <strong style={{ marginRight: '20px' }}>
                              {exec.isPhantom ? 'Phantom' : `Drum #${idx + 1}`}
                              {finished &&
                                (fullyProgrammed
                                  ? ' (Fully programmed)'
                                  : ' (Finished)')}
                            </strong>
                            {exec.isPhantom && (
                              <Badge bg="info" className="me-1">
                                PHANTOM
                              </Badge>
                            )}
                          </div>

                          {exec.wireDiameter && (
                            <Badge bg="info" className="me-1">
                              Ø {exec.wireDiameter} mm
                            </Badge>
                          )}

                          {/* DREAPTA */}
                          <div className="text-end">
                            {exec.wireType && (
                              <Badge bg="secondary">{exec.wireType}</Badge>
                            )}
                          </div>
                        </div>
                      }
                    />
                  )}

                  {(() => {
                    const ps = getInputProductionStatus(exec, product);
                    return ps ? (
                      <Badge
                        bg={ps.bg}
                        text={ps.text}
                        className="mb-2 me-1"
                        title="Production state of this drum"
                      >
                        ● {ps.label}
                      </Badge>
                    ) : null;
                  })()}

                  {finished &&
                    (fullyProgrammed ? (
                      <Badge
                        bg="primary"
                        className="mb-2"
                        title="Downstream fully planned for this token — can't re-select"
                      >
                        ● Fully programmed
                      </Badge>
                    ) : (
                      <Badge bg="dark" className="mb-2">
                        Fully allocated
                      </Badge>
                    ))}

                  {exec.color && (
                    <h6 className="mb-2">
                      <Badge
                        bg=""
                        className="me-1 fw-bold"
                        style={cableColorBadgeStyle(exec.color)}
                      >
                        {exec.color}
                      </Badge>
                    </h6>
                  )}

                  {!isSelectionEnabled && (
                    <h6 className="fw-bold mb-2">Drum #{idx + 1}</h6>
                  )}

                  <Row className="text-muted small">
                    <Col xs={6}>
                      <strong>Machine:</strong>
                      <div className="text-dark">{exec.machineId || '-'}</div>
                    </Col>

                    <Col xs={6}>
                      <strong>Cap:</strong>
                      <div className="text-dark">{cap} m</div>
                    </Col>

                    <Col xs={6} className="mt-2">
                      <strong>Planned:</strong>
                      <div className="text-dark">
                        {plannedCap > 0 ? `${plannedCap} m` : '-'}
                        {capDelta != null && Math.abs(capDelta) >= 0.05 && (
                          <span
                            className={`ms-1 ${capDelta > 0 ? 'text-success' : 'text-warning'}`}
                            title="What the drum actually holds, against what was planned"
                          >
                            {capDelta > 0 ? '+' : '−'}
                            {Math.abs(capDelta).toFixed(1)}
                          </span>
                        )}
                      </div>
                    </Col>

                    <Col xs={6} className="mt-2">
                      <strong>Used:</strong>
                      <div className="text-dark">{used.toFixed(1)} m</div>
                    </Col>

                    <Col xs={6} className="mt-2">
                      <strong>Remaining:</strong>
                      <div className="text-dark">{remaining.toFixed(1)} m</div>
                    </Col>

                    {/* ================= CONSTRUCTION INFO (ONLY FOR CONDUCTOR ISOLATION) ================= */}
                    {phaseName === 'conductor_isolation' &&
                      previousPhase?.name === 'pre_stranding' &&
                      typeof exec.ropeIndex === 'number' &&
                      previousPhase.rope?.[exec.ropeIndex] && (
                        <>
                          {/* CONSTRUCTION */}
                          <Col xs={6} className="mt-2">
                            <strong>Construction:</strong>
                            <div className="text-dark fw-semibold">
                              {previousPhase.rope[
                                exec.ropeIndex
                              ].wireDiameter.map((d, i) => (
                                <span key={i}>
                                  {
                                    previousPhase.rope[exec.ropeIndex]
                                      .numberOfWires[i]
                                  }{' '}
                                  × Ø{d}
                                  {i <
                                  previousPhase.rope[exec.ropeIndex]
                                    .wireDiameter.length -
                                    1
                                    ? ' + '
                                    : ''}
                                </span>
                              ))}
                            </div>
                          </Col>

                          {/* TOTAL DRUMS */}
                          <Col xs={6} className="mt-2">
                            <strong>Total drums:</strong>
                            <div className="text-dark fw-semibold">
                              {previousPhase.rope[
                                exec.ropeIndex
                              ].numberOfWires.reduce(
                                (a, b) => a + Number(b || 0),
                                0
                              )}
                            </div>
                          </Col>
                        </>
                      )}
                  </Row>

                  {/* mini progress */}
                  <div className="mt-2">
                    <ProgressTracker
                      compact
                      targetLength={cap}
                      executionSource={[{ plannedLength: used }]}
                    />
                  </div>
                </div>
              </Col>
            );
          })}
        </Row>
      </Card.Body>
    </Card>
  );
};

// Phoenix only ships the five semantic colours. `dark` and `light` are not
// among them — `btn-outline-dark` is not a class, so the diameter buttons were
// rendering with no outline at all, and METAL's grey was the one drum type you
// could not pick out of the row.
const DRUM_TYPE_VARIANTS = {
  PLASTIC: 'info',
  WOOD: 'warning',
  METAL: 'primary'
};
const DRUM_TYPE_FALLBACK = 'success';

const DrumQuickPresetSelector = ({
  title = 'Quick drum preset',
  preset,
  onSelectMaterial,
  onSelectDiameter,
  drumLibrary = [],
  reconcile = false
}) => {
  const selectedMaterial = String(preset?.material || '')
    .trim()
    .toUpperCase();

  const selectedDiameter = Number(preset?.diameter) || null;

  // Derive unique types and diameters from DB
  const drumTypes = useMemo(() => {
    const types = [...new Set(drumLibrary.map(d => d.drumType.toUpperCase()))];
    return types.map(key => ({
      key,
      variant: DRUM_TYPE_VARIANTS[key] || DRUM_TYPE_FALLBACK
    }));
  }, [drumLibrary]);

  const drumDiameters = useMemo(() => {
    const filtered = selectedMaterial
      ? drumLibrary.filter(d => d.drumType.toUpperCase() === selectedMaterial)
      : drumLibrary;
    const diameters = [...new Set(filtered.map(d => d.drumDiameter))].sort((a, b) => a - b);
    return diameters;
  }, [drumLibrary, selectedMaterial]);

  const activeDiameterVariant =
    DRUM_TYPE_VARIANTS[selectedMaterial] || DRUM_TYPE_FALLBACK;

  // Reconcile only re-attributes produced drums — hide the drum preset picker.
  if (reconcile) return null;

  return (
    <div className="mb-3 p-2 p-sm-3 border rounded bg-100 shadow-sm">
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-2">
        <span className="fw-semibold">{title}</span>

        {selectedMaterial || selectedDiameter ? (
          <small className="text-muted">
            Selected: <b>{selectedMaterial || '-'}</b>
            {' / '}
            <b>{selectedDiameter || '-'}</b>
            {selectedDiameter ? ' mm' : ''}
          </small>
        ) : null}
      </div>

      <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
        {drumTypes.map(type => {
          const isActive = selectedMaterial === type.key;

          return (
            <Button
              key={type.key}
              size="sm"
              variant={isActive ? type.variant : `outline-${type.variant}`}
              onClick={() => onSelectMaterial(isActive ? null : type.key)}
            >
              {type.key}
            </Button>
          );
        })}
      </div>

      {selectedMaterial ? (
        <div>
          {/* On a phone the label was the first item of a wrapping row, so the
              diameters started mid-line and the grid of buttons lost its left
              edge. Its own line keeps them in a column you can thumb down. */}
          <div className="fw-semibold mb-2">Diameter (mm)</div>

          <div className="d-flex flex-wrap align-items-center gap-2">
            {drumDiameters.map(diameter => {
              const isActive = Number(selectedDiameter) === Number(diameter);

              return (
                <Button
                  key={diameter}
                  size="sm"
                  variant={
                    isActive
                      ? activeDiameterVariant
                      : `outline-${activeDiameterVariant}`
                  }
                  onClick={() => onSelectDiameter(isActive ? null : diameter)}
                >
                  {diameter}
                </Button>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="small text-muted fst-italic">
          Select drum material first.
        </div>
      )}
    </div>
  );
};
